import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Building2, Clock, CheckCircle, LogOut, User, FileText, Shield, FileSignature, Heart, Users, CalendarIcon, Upload, HelpCircle, X, CheckCircle2, ArrowLeft, XCircle, Eye, Download, RotateCcw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import PropostaStatusDisplay from '@/components/PropostaStatusDisplay';
import ContratoStatusDisplay from '@/components/ContratoStatusDisplay';
import { DocumentosValidacao } from '@/components/DocumentosValidacao';
import ValeTransporteDisplay from '@/components/ValeTransporteDisplay';

// Fallback loading component for error boundaries
const FallbackComponent = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Card className="max-w-md w-full mx-4">
      <CardHeader className="text-center">
        <Building2 className="mx-auto h-12 w-12 text-primary mb-4" />
        <CardTitle>Carregando Sistema</CardTitle>
        <CardDescription>
          Aguarde enquanto carregamos suas informações...
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const etapas = [
  { key: 'proposta', label: 'Proposta', icon: FileText },
  { key: 'dados_pessoais', label: 'Dados Pessoais', icon: User },
  { key: 'envio_documentos', label: 'Envio de Documentos', icon: FileText },
  { key: 'validacao_documentos', label: 'Validação de Dados', icon: Shield },
  { key: 'transporte', label: 'Transporte', icon: FileText },
  { key: 'exame_admissional', label: 'Exame Admissional', icon: Heart },
  { key: 'assinatura_contrato', label: 'Assinatura do Contrato', icon: FileSignature },
  { key: 'integracao', label: 'Integração', icon: Users }
];

// Funções de validação
function validarCPF(cpf: string) {
  if (!cpf) return false;

  cpf = cpf.replace(/[^\d]+/g, '');

  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpf.charAt(i)) * (10 - i);
  }

  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf.charAt(9))) return false;

  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpf.charAt(i)) * (11 - i);
  }

  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;

  return resto === parseInt(cpf.charAt(10));
}

function validarRG(rg: string) {
  if (!rg) return false;

  // Remove caracteres especiais mantendo letras e números
  const rgLimpo = rg.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

  // RG deve ter entre 5 e 14 caracteres alfanuméricos
  // Isso cobre todos os estados brasileiros (MG, SP, RJ, etc.)
  if (rgLimpo.length < 5 || rgLimpo.length > 14) {
    return false;
  }

  // Deve conter pelo menos 4 dígitos numéricos
  const apenasDigitos = rgLimpo.replace(/\D/g, '');
  if (apenasDigitos.length < 4) {
    return false;
  }

  return true;
}

function validarPIS(pis: string) {
  // Remove caracteres não numéricos
  pis = pis.replace(/[^\d]+/g, '');

  // Deve ter exatamente 11 dígitos
  if (pis.length !== 11) return false;

  const multiplicadores = [3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let soma = 0;

  for (let i = 0; i < 10; i++) {
    soma += parseInt(pis.charAt(i), 10) * multiplicadores[i];
  }

  let resto = soma % 11;
  let digito = 11 - resto;
  if (digito === 10 || digito === 11) digito = 0;

  return digito === parseInt(pis.charAt(10), 10);
}

function calcularIdade(dataNascimento: string) {
  const data = new Date(dataNascimento);
  const hoje = new Date();
  let idade = hoje.getFullYear() - data.getFullYear();
  const mes = hoje.getMonth() - data.getMonth();
  if (mes < 0 || (mes === 0 && hoje.getDate() < data.getDate())) {
    idade--;
  }
  return idade;
}

function formatarData(dataString: string) {
  if (!dataString) return '';
  const parts = dataString.split('/');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
  }
  return dataString;
}

function validarDataNascimento(data: string) {
  const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = data.match(regex);
  
  if (!match) return false;
  
  const dia = parseInt(match[1]);
  const mes = parseInt(match[2]);
  const ano = parseInt(match[3]);
  
  if (mes < 1 || mes > 12) return false;
  if (dia < 1 || dia > 31) return false;
  if (ano < 1900 || ano > new Date().getFullYear()) return false;
  
  const dataFormatada = new Date(ano, mes - 1, dia);
  return dataFormatada.getDate() === dia && dataFormatada.getMonth() === mes - 1 && dataFormatada.getFullYear() === ano;
}

// Função para validar Título de Eleitor
function validarTituloEleitor(titulo: string): boolean {
  // Remove caracteres não numéricos
  const numbers = titulo.replace(/\D/g, '');
  
  // Deve ter exatamente 12 dígitos
  if (numbers.length !== 12) return false;
  
  // Verifica se os dígitos 9 e 10 (posições 8 e 9) estão entre 01 e 28
  const uf = parseInt(numbers.slice(8, 10));
  if (uf < 1 || uf > 28) return false;
  
  return true;
}

// Função para validar Carteira de Trabalho
function validarCarteiraTrabalho(carteira: string): boolean {
  // Remove caracteres não numéricos
  const numbers = carteira.replace(/\D/g, '');
  
  // Deve ter exatamente 7 dígitos (modelo antigo) ou 11 dígitos (CPF - modelo digital)
  if (numbers.length !== 7 && numbers.length !== 11) return false;
  
  return true;
}

async function buscarCEP(cep: string) {
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    const data = await response.json();
    if (data.erro) {
      throw new Error('CEP não encontrado');
    }
    return data;
  } catch (error) {
    console.error('Erro ao buscar CEP:', error);
    return null;
  }
}

// Lista de nacionalidades com bandeiras
const nacionalidades = [
  { value: "Afegão", label: "Afegão", flag: "🇦🇫" },
  { value: "Africano do Sul", label: "Africano do Sul", flag: "🇿🇦" },
  { value: "Albanês", label: "Albanês", flag: "🇦🇱" },
  { value: "Alemão", label: "Alemão", flag: "🇩🇪" },
  { value: "Americano", label: "Americano", flag: "🇺🇸" },
  { value: "Andorrano", label: "Andorrano", flag: "🇦🇩" },
  { value: "Angolano", label: "Angolano", flag: "🇦🇴" },
  { value: "Argentino", label: "Argentino", flag: "🇦🇷" },
  { value: "Argelino", label: "Argelino", flag: "🇩🇿" },
  { value: "Armênio", label: "Armênio", flag: "🇦🇲" },
  { value: "Australiano", label: "Australiano", flag: "🇦🇺" },
  { value: "Austríaco", label: "Austríaco", flag: "🇦🇹" },
  { value: "Azerbaijano", label: "Azerbaijano", flag: "🇦🇿" },
  { value: "Bahamense", label: "Bahamense", flag: "🇧🇸" },
  { value: "Bangladês", label: "Bangladês", flag: "🇧🇩" },
  { value: "Barbadense", label: "Barbadense", flag: "🇧🇧" },
  { value: "Bareinita", label: "Bareinita", flag: "🇧🇭" },
  { value: "Belga", label: "Belga", flag: "🇧🇪" },
  { value: "Belizenho", label: "Belizenho", flag: "🇧🇿" },
  { value: "Beninense", label: "Beninense", flag: "🇧🇯" },
  { value: "Bielorrusso", label: "Bielorrusso", flag: "🇧🇾" },
  { value: "Boliviano", label: "Boliviano", flag: "🇧🇴" },
  { value: "Bósnio", label: "Bósnio", flag: "🇧🇦" },
  { value: "Botsuanês", label: "Botsuanês", flag: "🇧🇼" },
  { value: "Brasileiro", label: "Brasileiro", flag: "🇧🇷" },
  { value: "Britânico", label: "Britânico", flag: "🇬🇧" },
  { value: "Bruneano", label: "Bruneano", flag: "🇧🇳" },
  { value: "Búlgaro", label: "Búlgaro", flag: "🇧🇬" },
  { value: "Burquinense", label: "Burquinense", flag: "🇧🇫" },
  { value: "Burundinês", label: "Burundinês", flag: "🇧🇮" },
  { value: "Butanês", label: "Butanês", flag: "🇧🇹" },
  { value: "Cabo-verdiano", label: "Cabo-verdiano", flag: "🇨🇻" },
  { value: "Camaronês", label: "Camaronês", flag: "🇨🇲" },
  { value: "Cambojano", label: "Cambojano", flag: "🇰🇭" },
  { value: "Canadense", label: "Canadense", flag: "🇨🇦" },
  { value: "Catarense", label: "Catarense", flag: "🇶🇦" },
  { value: "Cazaque", label: "Cazaque", flag: "🇰🇿" },
  { value: "Chadiano", label: "Chadiano", flag: "🇹🇩" },
  { value: "Chileno", label: "Chileno", flag: "🇨🇱" },
  { value: "Chinês", label: "Chinês", flag: "🇨🇳" },
  { value: "Cipriota", label: "Cipriota", flag: "🇨🇾" },
  { value: "Colombiano", label: "Colombiano", flag: "🇨🇴" },
  { value: "Comorense", label: "Comorense", flag: "🇰🇲" },
  { value: "Congolês", label: "Congolês", flag: "🇨🇩" },
  { value: "Norte-coreano", label: "Norte-coreano", flag: "🇰🇵" },
  { value: "Sul-coreano", label: "Sul-coreano", flag: "🇰🇷" },
  { value: "Costa-riquenho", label: "Costa-riquenho", flag: "🇨🇷" },
  { value: "Marfinense", label: "Marfinense", flag: "🇨🇮" },
  { value: "Croata", label: "Croata", flag: "🇭🇷" },
  { value: "Cubano", label: "Cubano", flag: "🇨🇺" },
  { value: "Dinamarquês", label: "Dinamarquês", flag: "🇩🇰" },
  { value: "Dominicano", label: "Dominicano", flag: "🇩🇴" },
  { value: "Egípcio", label: "Egípcio", flag: "🇪🇬" },
  { value: "Salvadorenho", label: "Salvadorenho", flag: "🇸🇻" },
  { value: "Emiradense", label: "Emiradense", flag: "🇦🇪" },
  { value: "Equatoriano", label: "Equatoriano", flag: "🇪🇨" },
  { value: "Eritreu", label: "Eritreu", flag: "🇪🇷" },
  { value: "Eslovaco", label: "Eslovaco", flag: "🇸🇰" },
  { value: "Esloveno", label: "Esloveno", flag: "🇸🇮" },
  { value: "Espanhol", label: "Espanhol", flag: "🇪🇸" },
  { value: "Estoniano", label: "Estoniano", flag: "🇪🇪" },
  { value: "Etíope", label: "Etíope", flag: "🇪🇹" },
  { value: "Fijiano", label: "Fijiano", flag: "🇫🇯" },
  { value: "Filipino", label: "Filipino", flag: "🇵🇭" },
  { value: "Finlandês", label: "Finlandês", flag: "🇫🇮" },
  { value: "Francês", label: "Francês", flag: "🇫🇷" },
  { value: "Gabonês", label: "Gabonês", flag: "🇬🇦" },
  { value: "Gambiano", label: "Gambiano", flag: "🇬🇲" },
  { value: "Ganense", label: "Ganense", flag: "🇬🇭" },
  { value: "Georgiano", label: "Georgiano", flag: "🇬🇪" },
  { value: "Greco", label: "Greco", flag: "🇬🇷" },
  { value: "Granadino", label: "Granadino", flag: "🇬🇩" },
  { value: "Guatemalteco", label: "Guatemalteco", flag: "🇬🇹" },
  { value: "Guianense", label: "Guianense", flag: "🇬🇾" },
  { value: "Guineense", label: "Guineense", flag: "🇬🇳" },
  { value: "Haitiano", label: "Haitiano", flag: "🇭🇹" },
  { value: "Hondurenho", label: "Hondurenho", flag: "🇭🇳" },
  { value: "Húngaro", label: "Húngaro", flag: "🇭🇺" },
  { value: "Iemenita", label: "Iemenita", flag: "🇾🇪" },
  { value: "Indiano", label: "Indiano", flag: "🇮🇳" },
  { value: "Indonésio", label: "Indonésio", flag: "🇮🇩" },
  { value: "Iraniano", label: "Iraniano", flag: "🇮🇷" },
  { value: "Iraquiano", label: "Iraquiano", flag: "🇮🇶" },
  { value: "Irlandês", label: "Irlandês", flag: "🇮🇪" },
  { value: "Islandês", label: "Islandês", flag: "🇮🇸" },
  { value: "Israelense", label: "Israelense", flag: "🇮🇱" },
  { value: "Italiano", label: "Italiano", flag: "🇮🇹" },
  { value: "Jamaicano", label: "Jamaicano", flag: "🇯🇲" },
  { value: "Japonês", label: "Japonês", flag: "🇯🇵" },
  { value: "Jordaniano", label: "Jordaniano", flag: "🇯🇴" },
  { value: "Queniano", label: "Queniano", flag: "🇰🇪" },
  { value: "Quirguiz", label: "Quirguiz", flag: "🇰🇬" },
  { value: "Kuwaitiano", label: "Kuwaitiano", flag: "🇰🇼" },
  { value: "Laosiano", label: "Laosiano", flag: "🇱🇦" },
  { value: "Lesotense", label: "Lesotense", flag: "🇱🇸" },
  { value: "Letão", label: "Letão", flag: "🇱🇻" },
  { value: "Libanês", label: "Libanês", flag: "🇱🇧" },
  { value: "Liberiano", label: "Liberiano", flag: "🇱🇷" },
  { value: "Líbio", label: "Líbio", flag: "🇱🇾" },
  { value: "Liechtensteinense", label: "Liechtensteinense", flag: "🇱🇮" },
  { value: "Lituano", label: "Lituano", flag: "🇱🇹" },
  { value: "Luxemburguês", label: "Luxemburguês", flag: "🇱🇺" },
  { value: "Macedônio", label: "Macedônio", flag: "🇲🇰" },
  { value: "Malgaxe", label: "Malgaxe", flag: "🇲🇬" },
  { value: "Malauiano", label: "Malauiano", flag: "🇲🇼" },
  { value: "Malaio", label: "Malaio", flag: "🇲🇾" },
  { value: "Maldivo", label: "Maldivo", flag: "🇲🇻" },
  { value: "Malinês", label: "Malinês", flag: "🇲🇱" },
  { value: "Maltês", label: "Maltês", flag: "🇲🇹" },
  { value: "Marroquino", label: "Marroquino", flag: "🇲🇦" },
  { value: "Mauriciano", label: "Mauriciano", flag: "🇲🇺" },
  { value: "Mauritano", label: "Mauritano", flag: "🇲🇷" },
  { value: "Mexicano", label: "Mexicano", flag: "🇲🇽" },
  { value: "Birmanês", label: "Birmanês", flag: "🇲🇲" },
  { value: "Moldavo", label: "Moldavo", flag: "🇲🇩" },
  { value: "Monegasco", label: "Monegasco", flag: "🇲🇨" },
  { value: "Mongol", label: "Mongol", flag: "🇲🇳" },
  { value: "Montenegrino", label: "Montenegrino", flag: "🇲🇪" },
  { value: "Moçambicano", label: "Moçambicano", flag: "🇲🇿" },
  { value: "Namibiano", label: "Namibiano", flag: "🇳🇦" },
  { value: "Nauruano", label: "Nauruano", flag: "🇳🇷" },
  { value: "Nepalês", label: "Nepalês", flag: "🇳🇵" },
  { value: "Nicaraguense", label: "Nicaraguense", flag: "🇳🇮" },
  { value: "Nigeriano", label: "Nigeriano", flag: "🇳🇬" },
  { value: "Nigerino", label: "Nigerino", flag: "🇳🇪" },
  { value: "Norueguês", label: "Norueguês", flag: "🇳🇴" },
  { value: "Neozelandês", label: "Neozelandês", flag: "🇳🇿" },
  { value: "Omanense", label: "Omanense", flag: "🇴🇲" },
  { value: "Holandês", label: "Holandês", flag: "🇳🇱" },
  { value: "Paquistanês", label: "Paquistanês", flag: "🇵🇰" },
  { value: "Palauense", label: "Palauense", flag: "🇵🇼" },
  { value: "Palestino", label: "Palestino", flag: "🇵🇸" },
  { value: "Panamenho", label: "Panamenho", flag: "🇵🇦" },
  { value: "Papua", label: "Papua", flag: "🇵🇬" },
  { value: "Paraguaio", label: "Paraguaio", flag: "🇵🇾" },
  { value: "Peruano", label: "Peruano", flag: "🇵🇪" },
  { value: "Polonês", label: "Polonês", flag: "🇵🇱" },
  { value: "Português", label: "Português", flag: "🇵🇹" },
  { value: "Ruandês", label: "Ruandês", flag: "🇷🇼" },
  { value: "Romeno", label: "Romeno", flag: "🇷🇴" },
  { value: "Russo", label: "Russo", flag: "🇷🇺" },
  { value: "Salomonense", label: "Salomonense", flag: "🇸🇧" },
  { value: "Samoano", label: "Samoano", flag: "🇼🇸" },
  { value: "Sanmarinense", label: "Sanmarinense", flag: "🇸🇲" },
  { value: "Saudita", label: "Saudita", flag: "🇸🇦" },
  { value: "Senegalês", label: "Senegalês", flag: "🇸🇳" },
  { value: "Sérvio", label: "Sérvio", flag: "🇷🇸" },
  { value: "Seichelense", label: "Seichelense", flag: "🇸🇨" },
  { value: "Serra-leonês", label: "Serra-leonês", flag: "🇸🇱" },
  { value: "Singapurense", label: "Singapurense", flag: "🇸🇬" },
  { value: "Sírio", label: "Sírio", flag: "🇸🇾" },
  { value: "Somali", label: "Somali", flag: "🇸🇴" },
  { value: "Sri-lankês", label: "Sri-lankês", flag: "🇱🇰" },
  { value: "Suazi", label: "Suazi", flag: "🇸🇿" },
  { value: "Sudanês", label: "Sudanês", flag: "🇸🇩" },
  { value: "Sueco", label: "Sueco", flag: "🇸🇪" },
  { value: "Suíço", label: "Suíço", flag: "🇨🇭" },
  { value: "Surinamês", label: "Surinamês", flag: "🇸🇷" },
  { value: "Tailandês", label: "Tailandês", flag: "🇹🇭" },
  { value: "Taiwanês", label: "Taiwanês", flag: "🇹🇼" },
  { value: "Tajique", label: "Tajique", flag: "🇹🇯" },
  { value: "Tanzaniano", label: "Tanzaniano", flag: "🇹🇿" },
  { value: "Tcheco", label: "Tcheco", flag: "🇨🇿" },
  { value: "Timorense", label: "Timorense", flag: "🇹🇱" },
  { value: "Togolês", label: "Togolês", flag: "🇹🇬" },
  { value: "Tonganês", label: "Tonganês", flag: "🇹🇴" },
  { value: "Trinitário", label: "Trinitário", flag: "🇹🇹" },
  { value: "Tunisiano", label: "Tunisiano", flag: "🇹🇳" },
  { value: "Turcomeno", label: "Turcomeno", flag: "🇹🇲" },
  { value: "Turco", label: "Turco", flag: "🇹🇷" },
  { value: "Tuvaluano", label: "Tuvaluano", flag: "🇹🇻" },
  { value: "Ucraniano", label: "Ucraniano", flag: "🇺🇦" },
  { value: "Ugandense", label: "Ugandense", flag: "🇺🇬" },
  { value: "Uruguaio", label: "Uruguaio", flag: "🇺🇾" },
  { value: "Uzbeque", label: "Uzbeque", flag: "🇺🇿" },
  { value: "Vanuatuense", label: "Vanuatuense", flag: "🇻🇺" },
  { value: "Venezuelano", label: "Venezuelano", flag: "🇻🇪" },
  { value: "Vietnamita", label: "Vietnamita", flag: "🇻🇳" },
  { value: "Zambiano", label: "Zambiano", flag: "🇿🇲" },
  { value: "Zimbabuense", label: "Zimbabuense", flag: "🇿🇼" }
];

export default function Contratacao() {
  const { signOut, user } = useAuth();
  const { toast } = useToast();
  
  console.log('🎯 Contratacao component loaded - user:', user?.email);
  
  // Debug: Log component mount
  useEffect(() => {
    console.log('🚀 Contratacao component mounted');
    console.log('📋 User data:', user);
  }, [user]);

  // Error boundary effect para capturar problemas
  useEffect(() => {
    const handleError = (error: any) => {
      console.error('❌ Erro capturado na página de contratação:', error);
      toast({
        title: "Erro detectado",
        description: "Ocorreu um problema na página. Verifique o console para mais detalhes.",
        variant: "destructive"
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, [toast]);
  
  // Estados para popup LGPD
  const [showLgpdModal, setShowLgpdModal] = useState(false);
  const [processingLgpd, setProcessingLgpd] = useState(false);
  const [lgpdChecked, setLgpdChecked] = useState(false);
  
  // Estados para exame admissional
  const [exameData, setExameData] = useState<any>(null);
  const [atestadoFile, setAtestadoFile] = useState<File | null>(null);
  const [uploadingAtestado, setUploadingAtestado] = useState(false);
  const [showGuiaViewer, setShowGuiaViewer] = useState(false);
  const [guiaUrl, setGuiaUrl] = useState<string>('');
  const [currentEtapa, setCurrentEtapa] = useState('proposta');
  const [etapaMaisAvancada, setEtapaMaisAvancada] = useState('proposta');
  const [isPrimeiroEnvio, setIsPrimeiroEnvio] = useState(true);
  const [contratacaoData, setContratacaoData] = useState<any>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [transporteConfirmado, setTransporteConfirmado] = useState(false);
  const [valeTransporte, setValeTransporte] = useState({ valor_ida: 0, valor_volta: 0 });
  const [dadosPessoais, setDadosPessoais] = useState({
    nomeCompleto: '',
    nomeSocial: '',
    dataNascimento: '',
    cpf: '',
    rg: '',
    genero: '',
    generoPersonalizado: '',
    celular: '',
    estadoCivil: '',
    nacionalidade: 'Brasileiro',
    transporteQuantidadeIda: 0,
    transporteValorIda: 0,
    transporteQuantidadeVolta: 0,
    transporteValorVolta: 0,
    numeroPIS: '',
    tituloEleitor: '',
    carteiraTrabalho: '',
    corPele: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    chavePix: '',
    tipoChavePix: '',
    banco: '',
    agencia: '',
    conta: '',
    tipoConta: '',
    nivelEscolaridade: '',
    // Campos de responsável legal
    tipoResponsavel: '',
    nomeResponsavel: '',
    cpfResponsavel: '',
    emailResponsavel: '',
    descricaoOutroResponsavel: ''
  });
  
  const [dadosDeficiencia, setDadosDeficiencia] = useState({
    possui_deficiencia: false,
    tipo_deficiencia: '',
    categoria_deficiencia: '',
    detalhes_deficiencia: ''
  });
  
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [evolucaoData, setEvolucaoData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  // Estados para documentos
  const [documentos, setDocumentos] = useState<{[key: string]: File | null}>({});
  const [documentosAprovados, setDocumentosAprovados] = useState<{[key: string]: boolean}>({});
  const [documentosReprovados, setDocumentosReprovados] = useState<{[key: string]: boolean}>({});
  const [errosDocumentos, setErrosDocumentos] = useState<{[key: string]: string}>({});
  const [validacaoDocumentos, setValidacaoDocumentos] = useState<{[key: string]: any}>({});
  const [enviandoDocumentos, setEnviandoDocumentos] = useState(false);
  const [validandoDocumentos, setValidandoDocumentos] = useState(false);
  const [documentosReenviados, setDocumentosReenviados] = useState<{[key: string]: boolean}>({});
  const [dependentes, setDependentes] = useState<{quantidade: number, idades: {[key: number]: number}}>({quantidade: 0, idades: {}});
  const [dependentesDocumentos, setDependentesDocumentos] = useState<{[key: string]: File | null}>({});
  const [dependentesDocTipo, setDependentesDocTipo] = useState<{[key: string]: string}>({});
  const [dependentesNomes, setDependentesNomes] = useState<{[key: string]: string}>({});
  const [dependentesCpf, setDependentesCpf] = useState<{[key: string]: string}>({});
  const [dependentesTipo, setDependentesTipo] = useState<{[key: string]: string}>({});
  const [dependentesDocumentosAprovados, setDependentesDocumentosAprovados] = useState<{[key: string]: boolean}>({});
  const [dependentesDocumentosReprovados, setDependentesDocumentosReprovados] = useState<{[key: string]: boolean}>({});
  const [showInstructions, setShowInstructions] = useState<string | null>(null);
  const [showWebhookResult, setShowWebhookResult] = useState(false);
  const [webhookJsonResult, setWebhookJsonResult] = useState<any>(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [documentoSelecionado, setDocumentoSelecionado] = useState<{tipo: string, nome: string, url: string} | null>(null);
  const [loadingDocumento, setLoadingDocumento] = useState(false);
  const [tentativasDocumentos, setTentativasDocumentos] = useState(0);
  const [mostrarAvisoTentativas, setMostrarAvisoTentativas] = useState(false);
  const [contratacaoId, setContratacaoId] = useState<string | null>(null);

  // Estado para dados adicionais
  const [isEtapaHistoricoCompletada, setIsEtapaHistoricoCompletada] = useState(false);
  
  // Estado para modais de transporte
  const [modaisTransporte, setModaisTransporte] = useState({
    onibus: false,
    trilhos: false
  });
  
  const [transporteOnibus, setTransporteOnibus] = useState({
    quantidadeIda: 0,
    valorIda: 0,
    quantidadeVolta: 0,
    valorVolta: 0
  });
  
  const [transporteTrilhos, setTransporteTrilhos] = useState({
    quantidadeIda: 0,
    valorIda: 0,
    quantidadeVolta: 0,
    valorVolta: 0
  });

  const [valeTransporteDetalhado, setValeTransporteDetalhado] = useState<any>(null);

  // Verificar LGPD apenas uma vez por sessão
  useEffect(() => {
    if (!user?.email || lgpdChecked) return;
    
    const verificarLgpd = async () => {
      console.log('🔍 Verificando LGPD para usuário:', user.email);
      
      try {
        // Verificar se já foi aceito nesta sessão
        const sessionKey = `lgpd_aceito_${user.email}`;
        const sessionAccepted = sessionStorage.getItem(sessionKey);
        
        if (sessionAccepted === 'true') {
          console.log('✅ LGPD já aceita nesta sessão');
          setLgpdChecked(true);
          return;
        }
        
        const { data } = await supabase
          .from('contratacao')
          .select('lgpd_aceite')
          .eq('email', user.email)
          .maybeSingle();
        
        console.log('📝 Dados LGPD encontrados:', data);
        
        // Se não aceitou LGPD ainda, mostrar modal
        if (data && data.lgpd_aceite !== true) {
          console.log('❌ LGPD não aceita, mostrando modal');
          setShowLgpdModal(true);
        } else {
          console.log('✅ LGPD já aceita');
          // Salvar na sessão que já foi aceito
          sessionStorage.setItem(sessionKey, 'true');
        }
        
        setLgpdChecked(true);
      } catch (error) {
        console.error('Erro ao verificar LGPD:', error);
        setLgpdChecked(true);
      }
    };
    
    verificarLgpd();
  }, [user?.email, lgpdChecked]);

  // Função para verificar e limpar documentos duplicados antes do envio
  const verificarELimparDuplicatas = async (documentoTipo: string) => {
    if (!user?.id || !dadosPessoais.cpf) return true;

    try {
      console.log(`🔍 Verificando duplicatas para ${documentoTipo} - usuário: ${user.id}`);
      
      // 1. Verificar se existe registro na tabela validacao_documentos
      const { data: validacoes, error: validacaoError } = await supabase
        .from('validacao_documentos')
        .select('*')
        .eq('id_usuario', user.id)
        .eq('nome_documento', documentoTipo);

      if (validacaoError) {
        console.error('❌ Erro ao verificar validações:', validacaoError);
        return false;
      }

      if (validacoes && validacoes.length > 0) {
        console.log(`🗑️ Encontradas ${validacoes.length} validações antigas para ${documentoTipo}, removendo...`);
        
        // Remover registros antigos da tabela
        const { error: deleteError } = await supabase
          .from('validacao_documentos')
          .delete()
          .eq('id_usuario', user.id)
          .eq('nome_documento', documentoTipo);

        if (deleteError) {
          console.error('❌ Erro ao remover validações antigas:', deleteError);
          return false;
        }
        
        console.log(`✅ Registros antigos de ${documentoTipo} removidos da tabela`);
      }

      // 2. Remover arquivo antigo do storage
      const cpfFolder = dadosPessoais.cpf;
      const { data: files, error: listError } = await supabase.storage
        .from('contratacao-documentos')
        .list(cpfFolder);

      if (listError) {
        console.error('❌ Erro ao listar arquivos:', listError);
        return false;
      }

      if (files && files.length > 0) {
        // Procurar arquivo com o nome do documento (pode ter extensões diferentes)
        const arquivosDocumento = files.filter(file => 
          file.name.toLowerCase().startsWith(documentoTipo.toLowerCase())
        );

        if (arquivosDocumento.length > 0) {
          console.log(`🗑️ Encontrados ${arquivosDocumento.length} arquivos antigos para ${documentoTipo}, removendo...`);
          
          const filesToRemove = arquivosDocumento.map(file => `${cpfFolder}/${file.name}`);
          
          const { error: removeError } = await supabase.storage
            .from('contratacao-documentos')
            .remove(filesToRemove);

          if (removeError) {
            console.error('❌ Erro ao remover arquivos antigos:', removeError);
            return false;
          }
          
          console.log(`✅ Arquivos antigos de ${documentoTipo} removidos do storage`);
        }
      }

      return true;
    } catch (error) {
      console.error('❌ Erro ao verificar e limpar duplicatas:', error);
      return false;
    }
  };

  // Função para carregar status dos documentos da tabela validacao_documentos
  const carregarStatusDocumentos = async () => {
    if (!user?.id) return;

    try {
      console.log('📋 Carregando status dos documentos validados...');
      
      const { data: validacoes, error } = await supabase
        .from('validacao_documentos')
        .select('*')
        .eq('id_usuario', user.id);

      if (error) {
        console.error('❌ Erro ao carregar validações:', error);
        return;
      }

      if (validacoes && validacoes.length > 0) {
        const aprovados: {[key: string]: boolean} = {};
        const reprovados: {[key: string]: boolean} = {};
        const erros: {[key: string]: string} = {};

        // Mapeamento dos nomes dos documentos da validação para os campos do formulário
        const mapeamentoDocumentos: {[key: string]: string} = {
          'RG': 'rg',
          'Carteira de trabalho': 'carteira_trabalho',
          'Certificado de Reservista': 'certificado_reservista',
          'Reservista': 'certificado_reservista',
          'Foto 3x4': 'foto_3x4',
          'Comprovante Bancário': 'comprovante_bancario',
          'Dados bancarios': 'comprovante_bancario',
          'Comprovante de Residência': 'comprovante_residencia',
          'Comprovante de residencia': 'comprovante_residencia',
          'Comprovante de Escolaridade': 'comprovante_escolaridade',
          'Comprovante de escolaridade': 'comprovante_escolaridade',
          'Título de Eleitor': 'titulo_eleitor',
          'Titulo de eleitor': 'titulo_eleitor',
          'Certidão de Nascimento': 'certidao_nascimento',
          'Certidão de nascimento': 'certidao_nascimento',
          'Certidão de Casamento': 'certidao_casamento',
          'Atestado de Saúde': 'atestado_saude'
        };

        validacoes.forEach(validacao => {
          const nomeDocumento = validacao.nome_documento;
          const campoFormulario = mapeamentoDocumentos[nomeDocumento] || nomeDocumento.toLowerCase().replace(/ /g, '_');
          
          console.log(`📄 Processando documento: ${nomeDocumento} -> ${campoFormulario}`);
          
          if (validacao.retorno_documento === 1) {
            aprovados[campoFormulario] = true;
            console.log(`✅ ${nomeDocumento} (${campoFormulario}) está aprovado`);
          } else if (validacao.retorno_documento === 0) {
            reprovados[campoFormulario] = true;
            if (validacao.descricao_retorno) {
              erros[campoFormulario] = validacao.descricao_retorno;
            }
            console.log(`❌ ${nomeDocumento} (${campoFormulario}) está reprovado: ${validacao.descricao_retorno || 'Sem motivo especificado'}`);
          }
        });

        setDocumentosAprovados(aprovados);
        setDocumentosReprovados(reprovados);
        setErrosDocumentos(erros);
        
        console.log('📊 Status final carregado:', { aprovados, reprovados, erros });
      }
    } catch (error) {
      console.error('❌ Erro ao carregar status dos documentos:', error);
    }
  };


  // Carregar dados da evolução - apenas uma vez
  useEffect(() => {
    if (user && !evolucaoData && !showLgpdModal) {
      console.log('▶️ Executando loadEvolucaoData');
      loadEvolucaoData();
      loadTentativasDocumentos();
    }
  }, [user?.id, showLgpdModal]); // Executar quando user.id muda ou modal LGPD fecha

  // Carregar documentos existentes quando chegar na etapa de documentos
  useEffect(() => {
    if (currentEtapa === 'envio_documentos' && user) {
      loadDocumentosExistentes();
      carregarStatusDocumentos(); // Carregar status dos documentos validados
    }
  }, [currentEtapa, user]);

  // Listener para atualizar status automaticamente
  useEffect(() => {
    if (user?.id && currentEtapa === 'envio_documentos') {
      const interval = setInterval(() => {
        carregarStatusDocumentos();
      }, 5000); // Atualizar a cada 5 segundos

      // Limpar interval quando componente for desmontado ou sair da etapa
      return () => clearInterval(interval);
    }
  }, [user?.id, currentEtapa]);

  useEffect(() => {
    if (currentEtapa === 'exame_admissional' && user) {
      loadExameData();
      
      // Verificar se exame foi aprovado para avançar automaticamente
      const checkExameAprovacao = async () => {
        const { data: evolucaoData } = await supabase
          .from('evolucao_contratacao')
          .select('exame_aprovado, exame_status')
          .eq('user_id', user.id)
          .single();
          
        if (evolucaoData?.exame_aprovado === true && evolucaoData?.exame_status === 'concluido') {
          toast({
            title: "Exame Aprovado!",
            description: "Seu atestado foi aprovado. Avançando para a próxima etapa...",
          });
          
          setTimeout(async () => {
            await registrarTransicaoEtapa('assinatura_contrato', currentEtapa);
            setCurrentEtapa('assinatura_contrato');
            setEtapaMaisAvancada('assinatura_contrato');
          }, 2000);
        }
      };
      
      checkExameAprovacao();
    }
  }, [currentEtapa, user]);

  // Listener para mudanças em tempo real no status do exame
  useEffect(() => {
    if (!user?.id || currentEtapa !== 'exame_admissional') return;

    const channel = supabase
      .channel(`exame-approval-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'evolucao_contratacao',
          filter: `user_id=eq.${user.id}`
        },
        (payload: any) => {
          const newData = payload.new;
          if (newData.exame_aprovado === true && newData.exame_status === 'concluido') {
            toast({
              title: "Exame Aprovado!",
              description: "Seu atestado foi aprovado. Avançando para a próxima etapa...",
            });
            
            setTimeout(async () => {
              await registrarTransicaoEtapa('assinatura_contrato', currentEtapa);
              setCurrentEtapa('assinatura_contrato');
              setEtapaMaisAvancada('assinatura_contrato');
            }, 2000);
          } else if (newData.exame_aprovado === false && newData.exame_status === 'reprovado') {
            toast({
              title: "Atestado Reprovado",
              description: "Seu atestado foi reprovado. Por favor, envie um novo atestado.",
              variant: "destructive"
            });
            
            // Recarregar dados para mostrar o motivo da reprovação
            loadEvolucaoData();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, currentEtapa]);

  // Listener para redirecionamento automático quando todos os documentos forem aprovados
  useEffect(() => {
    const handleDocumentosAprovados = async (event: CustomEvent) => {
      console.log('Evento documentosAprovados recebido:', event.detail);
      console.log('Etapa atual:', currentEtapa);
      
      if (event.detail.todosAprovados && currentEtapa === 'validacao_documentos') {
        console.log('Todos os documentos aprovados, iniciando progressão para exame...');
        
        // Atualizar o banco de dados primeiro
        try {
          const { error } = await supabase
            .from('evolucao_contratacao')
            .update({ 
              etapa_atual: 'exame_admissional',
              etapa_mais_avancada: 'exame_admissional',
              validacao_status: 'concluido'
            })
            .eq('user_id', user?.id);

          if (error) {
            console.error('Erro ao atualizar evolução da contratação:', error);
            throw error;
          }

          console.log('Banco de dados atualizado com sucesso');

          // Aguardar 2 segundos para mostrar o status de sucesso, depois avançar
          setTimeout(() => {
            console.log('Avançando para próxima etapa...');
            avancarEtapa();
          }, 2000);
        } catch (error) {
          console.error('Erro ao processar documentos aprovados:', error);
          toast({
            title: "Erro",
            description: "Erro ao processar aprovação dos documentos.",
            variant: "destructive"
          });
        }
      }
    };

    window.addEventListener('documentosAprovados', handleDocumentosAprovados as EventListener);
    
    return () => {
      window.removeEventListener('documentosAprovados', handleDocumentosAprovados as EventListener);
    };
  }, [currentEtapa, user?.id, toast]);

  // 🔧 FALLBACK MELHORADO: Verificar automaticamente se validacao_status === 'concluido' em qualquer etapa
  useEffect(() => {
    // Expandir verificação para múltiplas etapas antes de integração
    if (user?.id && ['validacao_documentos', 'exame_admissional', 'assinatura_contrato'].includes(currentEtapa)) {
      const verificarValidacaoConcluida = async () => {
        try {
          // Buscar status da validação
          const { data: evolucaoData, error: evolucaoError } = await supabase
            .from('evolucao_contratacao')
            .select('validacao_status, quantidade_documentos_enviados, etapa_atual')
            .eq('user_id', user.id)
            .maybeSingle();

          if (evolucaoError) {
            console.error('Erro ao buscar evolução:', evolucaoError);
            return;
          }

          // Buscar validações dos documentos
          const { data: validacoesData, error: validacoesError } = await supabase
            .from('validacao_documentos')
            .select('retorno_documento')
            .eq('id_usuario', user.id);

          if (validacoesError) {
            console.error('Erro ao buscar validações:', validacoesError);
            return;
          }

          if (!validacoesData || validacoesData.length === 0) {
            console.log('Nenhuma validação encontrada ainda');
            return;
          }

          const documentosAprovados = validacoesData.filter(v => v.retorno_documento === 1).length;
          const documentosReprovados = validacoesData.filter(v => v.retorno_documento === 0).length;
          const totalProcessados = documentosAprovados + documentosReprovados;
          
          // Usar fallback se quantidade_documentos_enviados estiver zerado
          let quantidadeEnviados = evolucaoData?.quantidade_documentos_enviados || 0;
          if (quantidadeEnviados === 0 && totalProcessados > 0) {
            console.warn('⚠️ FALLBACK AUTO-CHECK: quantidade_documentos_enviados zerado, usando total processado:', totalProcessados);
            quantidadeEnviados = totalProcessados;
          }

          // Se validacao_status já está concluído OU todos documentos foram aprovados, avançar
          const todosAprovados = quantidadeEnviados > 0 && documentosAprovados === quantidadeEnviados && documentosReprovados === 0;
          const statusConcluido = evolucaoData?.validacao_status === 'concluido';

          console.log('🔍 AUTO-CHECK Validação:', {
            currentEtapa,
            etapaAtualDB: evolucaoData?.etapa_atual,
            statusConcluido,
            todosAprovados,
            documentosAprovados,
            quantidadeEnviados,
            documentosReprovados
          });

          // VERIFICAÇÃO EXTRA: Se validação está concluída OU todos documentos aprovados, avançar
          if (statusConcluido || todosAprovados) {
            if (currentEtapa === 'validacao_documentos') {
              console.log('✅ Validação já concluída com sucesso, avançando automaticamente...');
              
              // Atualizar para próxima etapa
              const { error: updateError } = await supabase
                .from('evolucao_contratacao')
                .update({
                  etapa_atual: 'exame_admissional',
                  etapa_mais_avancada: 'exame_admissional'
                })
                .eq('user_id', user.id);

              if (updateError) {
                console.error('Erro ao atualizar para exame:', updateError);
                return;
              }

              toast({
                title: "Documentos Aprovados!",
                description: "Seus documentos foram validados com sucesso. Avançando para Exame Admissional...",
              });

              setTimeout(() => {
                setCurrentEtapa('exame_admissional');
                setEtapaMaisAvancada('exame_admissional');
              }, 2000);
            } else if (currentEtapa === 'exame_admissional' || currentEtapa === 'assinatura_contrato') {
              // Usuário já avançou corretamente, não fazer nada
              console.log('✅ Usuário já está na etapa correta:', currentEtapa);
            }
          }
        } catch (error) {
          console.error('Erro no auto-check de validação:', error);
        }
      };

      // Executar verificação após 1 segundo (dar tempo para carregar dados)
      const timeout = setTimeout(verificarValidacaoConcluida, 1000);
      return () => clearTimeout(timeout);
    }
  }, [currentEtapa, user?.id, toast]);

  // 🔄 VERIFICAÇÃO PERIÓDICA: Desbloquear automaticamente se validação for concluída
  useEffect(() => {
    if (!user?.id) return;
    
    const interval = setInterval(async () => {
      try {
        const { data: evolucao } = await supabase
          .from('evolucao_contratacao')
          .select('etapa_atual, validacao_status')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (evolucao?.validacao_status === 'concluido' && 
            currentEtapa === 'validacao_documentos') {
          console.log('🔄 Verificação periódica: desbloqueando usuário...');
          
          // Atualizar estado local imediatamente
          setCurrentEtapa('exame_admissional');
          setEtapaMaisAvancada('exame_admissional');
          
          // Garantir que DB também está sincronizado
          await supabase
            .from('evolucao_contratacao')
            .update({
              etapa_atual: 'exame_admissional',
              etapa_mais_avancada: 'exame_admissional'
            })
            .eq('user_id', user.id);
        }
      } catch (error) {
        console.error('Erro na verificação periódica:', error);
      }
    }, 30000); // 30 segundos
    
    return () => clearInterval(interval);
  }, [user?.id, currentEtapa]);

  // Função para carregar documentos existentes do banco de dados
  const loadDocumentosExistentes = async () => {
    try {
      // Carregar status dos documentos da tabela documentos_contratacao
      const { data: documentosData, error } = await supabase
        .from('documentos_contratacao')
        .select('tipo_documento, status_validacao, nome_arquivo, caminho_storage')
        .eq('user_id', user?.id);

      if (error) {
        console.error('Erro ao carregar documentos:', error);
        return;
      }

      // Carregar validações da tabela validacao_documentos
      const { data: validacoesData, error: validacoesError } = await supabase
        .from('validacao_documentos')
        .select('nome_documento, retorno_documento, descricao_retorno')
        .eq('id_usuario', user?.id);

      if (validacoesError) {
        console.error('Erro ao carregar validações:', validacoesError);
      }

      if (documentosData && documentosData.length > 0) {
        console.log('Documentos encontrados na base:', documentosData);
        
        // Criar objetos com status dos documentos
        const documentosAprovadosTemp: {[key: string]: boolean} = {};
        const documentosReprovadosTemp: {[key: string]: boolean} = {};
        const dependentesDocumentosAprovadosTemp: {[key: string]: boolean} = {};
        const dependentesDocumentosReprovadosTemp: {[key: string]: boolean} = {};
        const validacaoTemp: {[key: string]: any} = {};
        const errosTemp: {[key: string]: string} = {};
        
        // Processar validações da tabela validacao_documentos
        if (validacoesData && validacoesData.length > 0) {
          // Mapeamento de nomes da tabela validacao_documentos para tipos de documento do código
          const mapeamentoNomes: {[key: string]: string[]} = {
            'RG': ['rg'],
            'Carteira de trabalho': ['carteira_trabalho'],
            'Certidão de nascimento': ['certidao_nascimento'],
            'Comprovante de residencia': ['comprovante_residencia'],
            'Comprovante de escolaridade': ['comprovante_escolaridade'],
            'Titulo de eleitor': ['titulo_eleitor'],
            'Foto 3x4': ['foto_3x4'],
            'Dados bancarios': ['comprovante_bancario'],
            'Atestado de saude': ['atestado_saude'],
            'Certificado de reservista': ['certificado_reservista'],
            'Reservista': ['certificado_reservista']
          };

          validacoesData.forEach(validacao => {
            validacaoTemp[validacao.nome_documento] = validacao;
            
            // Verificar se é comprovante de vacinação de dependente no formato "CV dependente X"
            if (validacao.nome_documento.startsWith('CV dependente')) {
              const match = validacao.nome_documento.match(/CV dependente (\d+)/);
              if (match) {
                const numeroDependente = match[1];
                const tipoDoc = `dependente_${numeroDependente}_comprovante_vacinacao`;
                
                if (validacao.retorno_documento === 1) {
                  dependentesDocumentosAprovadosTemp[tipoDoc] = true;
                  dependentesDocumentosAprovadosTemp[validacao.nome_documento] = true;
                } else if (validacao.retorno_documento === 0) {
                  dependentesDocumentosReprovadosTemp[tipoDoc] = true;
                  dependentesDocumentosReprovadosTemp[validacao.nome_documento] = true;
                  if (validacao.descricao_retorno) {
                    errosTemp[tipoDoc] = validacao.descricao_retorno;
                    errosTemp[validacao.nome_documento] = validacao.descricao_retorno;
                  }
                }
              }
            }
            // Verificar se é documento de dependente no formato "dependente_X_documento"
            else if (validacao.nome_documento.startsWith('dependente_')) {
              if (validacao.retorno_documento === 1) {
                dependentesDocumentosAprovadosTemp[validacao.nome_documento] = true;
              } else if (validacao.retorno_documento === 0) {
                dependentesDocumentosReprovadosTemp[validacao.nome_documento] = true;
                if (validacao.descricao_retorno) {
                  errosTemp[validacao.nome_documento] = validacao.descricao_retorno;
                }
              }
            }
            // Documentos principais - aplicar validação para todos os tipos de documento mapeados
            else {
              const mapeamentoNomes: {[key: string]: string[]} = {
                'RG': ['rg'],
                'Carteira de trabalho': ['carteira_trabalho'],
                'Certidão de nascimento': ['certidao_nascimento'],
                'Comprovante de residencia': ['comprovante_residencia'],
                'Comprovante de escolaridade': ['comprovante_escolaridade'],
                'Titulo de eleitor': ['titulo_eleitor'],
                'Foto 3x4': ['foto_3x4'],
                'Dados bancarios': ['comprovante_bancario'],
                'Atestado de saude': ['atestado_saude'],
                'Certificado de reservista': ['certificado_reservista'],
                'Reservista': ['certificado_reservista']
              };

              const tiposDocumento = mapeamentoNomes[validacao.nome_documento] || [validacao.nome_documento];
              
            tiposDocumento.forEach(tipoDoc => {
              if (validacao.retorno_documento === 1) {
                documentosAprovadosTemp[tipoDoc] = true;
              } else if (validacao.retorno_documento === 0) {
                documentosReprovadosTemp[tipoDoc] = true;
                if (validacao.descricao_retorno) {
                  errosTemp[tipoDoc] = validacao.descricao_retorno;
                }
              }
              // Documentos em validação (null/undefined) não alteram o status visual
            });
            }
          });
        }

        // Fallback para documentos que não estão na tabela validacao_documentos
        // (usar status da tabela documentos_contratacao)
        documentosData.forEach(doc => {
          // Só processar se não existe validação específica para este tipo de documento
          const jaProcessado = Object.values(validacaoTemp).some(val => {
            const mapeamentoNomes: {[key: string]: string[]} = {
              'RG': ['rg'],
              'Carteira de trabalho': ['carteira_trabalho'],
              'Certidão de nascimento': ['certidao_nascimento'],
              'Comprovante de residencia': ['comprovante_residencia'],
              'Comprovante de escolaridade': ['comprovante_escolaridade'],
              'Titulo de eleitor': ['titulo_eleitor'],
              'Foto 3x4': ['foto_3x4'],
              'Dados bancarios': ['comprovante_bancario'],
              'Atestado de saude': ['atestado_saude'],
              'Certificado de reservista': ['certificado_reservista'],
              'Reservista': ['certificado_reservista']
            };
            const tipos = mapeamentoNomes[val.nome_documento] || [val.nome_documento];
            return tipos.includes(doc.tipo_documento);
          });

          if (!jaProcessado) {
            if (doc.tipo_documento.startsWith('dependente_')) {
              if (doc.status_validacao === 'aprovado') {
                dependentesDocumentosAprovadosTemp[doc.tipo_documento] = true;
              } else if (doc.status_validacao === 'reprovado') {
                dependentesDocumentosReprovadosTemp[doc.tipo_documento] = true;
              }
            } else {
              if (doc.status_validacao === 'aprovado') {
                documentosAprovadosTemp[doc.tipo_documento] = true;
              } else if (doc.status_validacao === 'reprovado') {
                documentosReprovadosTemp[doc.tipo_documento] = true;
              }
            }
          }
        });

        setDocumentosAprovados(documentosAprovadosTemp);
        setDocumentosReprovados(documentosReprovadosTemp);
        setDependentesDocumentosAprovados(dependentesDocumentosAprovadosTemp);
        setDependentesDocumentosReprovados(dependentesDocumentosReprovadosTemp);
        setValidacaoDocumentos(validacaoTemp);
        setErrosDocumentos(errosTemp);
        
        // Verificar se há documentos inválidos que precisam ser reenviados
        await verificarReenvioDocumentosInvalidos(validacoesData);
        
        toast({
          title: "Documentos carregados",
          description: `Status de ${documentosData.length} documentos carregado.`,
        });
      }
    } catch (error) {
      console.error('Erro ao carregar documentos existentes:', error);
    }
  };

  // Função para verificar e reenviar documentos inválidos automaticamente (apenas uma vez)
  const verificarReenvioDocumentosInvalidos = async (validacoesData: any[]) => {
    if (!validacoesData || validacoesData.length === 0) return;

    const documentosParaReenviar: any[] = [];
    
    for (const validacao of validacoesData) {
      // CONDIÇÃO CRÍTICA: Só processar documentos inválidos que ainda não foram reenviados
      // E que não estão aprovados (para evitar reenvio de documentos já válidos)
      if (validacao.retorno_documento === 0 && !documentosReenviados[validacao.nome_documento]) {
        
        // Mapear nome do documento da validação para o tipo usado no storage
        const mapeamentoValidacaoParaTipo: {[key: string]: string[]} = {
          'RG': ['rg'],
          'Carteira de trabalho': ['carteira_trabalho'],
          'Certidão de nascimento': ['certidao_nascimento'],
          'Comprovante de residencia': ['comprovante_residencia'],
          'Comprovante de escolaridade': ['comprovante_escolaridade'],
          'Titulo de eleitor': ['titulo_eleitor'],
          'Foto 3x4': ['foto_3x4'],
          'Dados bancarios': ['comprovante_bancario'],
          'Atestado de saude': ['atestado_saude'],
          'Certificado de reservista': ['certificado_reservista']
        };

        const tiposDocumento = mapeamentoValidacaoParaTipo[validacao.nome_documento] || [validacao.nome_documento];
        
        // Buscar arquivos correspondentes no storage - apenas um arquivo por tipo para evitar duplicatas
        for (const tipoDoc of tiposDocumento) {
          const { data: documentosData } = await supabase
            .from('documentos_contratacao')
            .select('tipo_documento, caminho_storage, nome_arquivo')
            .eq('user_id', user?.id)
            .eq('tipo_documento', tipoDoc)
            .order('created_at', { ascending: false })
            .limit(1);

          if (documentosData && documentosData.length > 0) {
            const doc = documentosData[0];
            
            // Fazer download do arquivo do storage
            const { data: fileData } = await supabase.storage
              .from('contratacao-documentos')
              .download(doc.caminho_storage);

            if (fileData) {
              const file = new File([fileData], doc.nome_arquivo, { type: fileData.type });
              documentosParaReenviar.push({
                documento: validacao.nome_documento,
                tipo: tipoDoc,
                file: file,
                nomeOriginal: doc.nome_arquivo
              });
            }
            
            // BREAK: Para RG, pegar apenas um arquivo para evitar reenvio desnecessário
            break;
          }
        }
      }
    }

    // Reenviar documentos inválidos se houver algum (apenas uma vez por documento)
    if (documentosParaReenviar.length > 0) {
      console.log(`Reenviando ${documentosParaReenviar.length} documentos inválidos para revalidação (apenas uma vez)...`);
      
      // Criar FormData com os documentos
      const formData = new FormData();
      
      for (const item of documentosParaReenviar) {
        formData.append(item.tipo, item.file, item.nomeOriginal);
      }

      try {
        // Enviar para o webhook
        await enviarParaWebhook(formData);
        
        // CONTROLE CRÍTICO: Marcar como reenviados IMEDIATAMENTE para evitar loop
        const novosReenviados = { ...documentosReenviados };
        for (const item of documentosParaReenviar) {
          novosReenviados[item.documento] = true;
        }
        setDocumentosReenviados(novosReenviados);
        
        console.log('Documentos marcados como reenviados:', Object.keys(novosReenviados));
        
        toast({
          title: "Reenvio automático",
          description: `${documentosParaReenviar.length} documentos inválidos foram reenviados automaticamente para nova validação.`,
        });
        
      } catch (error) {
        console.error('Erro no reenvio automático:', error);
      }
    }
  };

  const loadExameData = async () => {
    try {
      const { data: contratacaoData, error } = await supabase
        .from('contratacao')
        .select('endereco_exame, data_exame, horario_exame, guia_exame_path')
        .eq('email', user?.email)
        .maybeSingle();

      if (error) {
        console.error('Erro ao carregar dados do exame:', error);
        return;
      }

      setExameData(contratacaoData);
    } catch (error) {
      console.error('Erro ao carregar dados do exame:', error);
    }
  };

  // Função para visualizar documento
  const visualizarDocumento = async (tipoDocumento: string) => {
    if (!user?.id) return;

    setLoadingDocumento(true);
    try {
      // Buscar CPF do usuário
      const { data: dadosPessoais, error: cpfError } = await supabase
        .from('dados_pessoais')
        .select('cpf')
        .eq('user_id', user.id)
        .single();

      if (cpfError || !dadosPessoais?.cpf) {
        toast({
          title: "Erro",
          description: "CPF do usuário não encontrado.",
          variant: "destructive"
        });
        return;
      }

      const cpf = dadosPessoais.cpf;
      
      // Mapeamento dos tipos de documento para os nomes dos arquivos no storage
      const mapeamentoNomes: {[key: string]: string} = {
        'rg': 'RG',
        'carteira_trabalho': 'Carteira de Trabalho',
        'certificado_reservista': 'Certificado de Reservista',
        'foto_3x4': 'Foto 3x4',
        'comprovante_bancario': 'Comprovante Bancario',
        'comprovante_residencia': 'Comprovante de Residencia',
        'comprovante_escolaridade': 'Comprovante de Escolaridade',
        'titulo_eleitor': 'Titulo de Eleitor',
        'certidao_nascimento': 'Certidao de Nascimento',
        'certidao_casamento': 'Certidao de Casamento',
        'atestado_saude': 'Atestado de Saude'
      };

      const nomeArquivoEsperado = mapeamentoNomes[tipoDocumento] || tipoDocumento;
      
      // Listar arquivos na pasta do CPF do usuário
      const { data: files, error: listError } = await supabase.storage
        .from('contratacao-documentos')
        .list(`${cpf}/`, {
          limit: 100,
          offset: 0
        });

      if (listError) {
        console.error('Erro ao listar arquivos:', listError);
        toast({
          title: "Erro ao acessar documentos",
          description: "Não foi possível acessar a pasta de documentos.",
          variant: "destructive"
        });
        return;
      }

      if (!files || files.length === 0) {
        toast({
          title: "Nenhum documento encontrado",
          description: "Não há documentos na pasta do usuário.",
          variant: "destructive"
        });
        return;
      }

      console.log('Arquivos encontrados:', files.map(f => f.name));
      console.log('Procurando por:', nomeArquivoEsperado);

      // Procurar arquivo com o nome esperado (ignorando extensão)
      const arquivo = files.find(file => {
        const nomeArquivo = file.name;
        
        // Remover extensão para comparar apenas o nome
        const nomeBase = nomeArquivo.replace(/\.[^/.]+$/, "");
        
        console.log(`Comparando: "${nomeBase}" com "${nomeArquivoEsperado}"`);
        
        return nomeBase === nomeArquivoEsperado;
      });

      if (!arquivo) {
        console.log('Arquivo não encontrado. Nomes disponíveis:', files.map(f => f.name.replace(/\.[^/.]+$/, "")));
        toast({
          title: "Documento não encontrado",
          description: `Documento "${nomeArquivoEsperado}" não foi encontrado na pasta do usuário.`,
          variant: "destructive"
        });
        return;
      }

      // Caminho completo do arquivo
      const caminhoCompleto = `${cpf}/${arquivo.name}`;

      console.log('Documento encontrado:', arquivo.name);
      console.log('Caminho completo:', caminhoCompleto);

      // Gerar URL assinada para visualização
      const { data: urlData, error: urlError } = await supabase.storage
        .from('contratacao-documentos')
        .createSignedUrl(caminhoCompleto, 3600); // URL válida por 1 hora

      if (urlError || !urlData?.signedUrl) {
        console.error('Erro ao criar URL:', urlError);
        toast({
          title: "Erro ao acessar documento",
          description: "Não foi possível gerar link de acesso ao documento.",
          variant: "destructive"
        });
        return;
      }

      setDocumentoSelecionado({
        tipo: tipoDocumento,
        nome: arquivo.name,
        url: urlData.signedUrl
      });
      setShowDocumentViewer(true);

    } catch (error) {
      console.error('Erro ao visualizar documento:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar documento.",
        variant: "destructive"
      });
    } finally {
      setLoadingDocumento(false);
    }
  };

  const uploadAtestadoSaude = async () => {
    if (!atestadoFile || !user?.id) return false;

    setUploadingAtestado(true);
    try {
      // Buscar CPF do usuário
      const { data: dadosPessoais, error: cpfError } = await supabase
        .from('dados_pessoais')
        .select('cpf')
        .eq('user_id', user.id)
        .single();

      if (cpfError || !dadosPessoais?.cpf) {
        throw new Error('CPF do usuário não encontrado');
      }

      const cpf = dadosPessoais.cpf;
      
      // Nome do arquivo seguindo o mesmo padrão dos outros documentos
      const nomeArquivo = `Atestado de Saude.${atestadoFile.name.split('.').pop()}`;
      const fileName = `${cpf}/${nomeArquivo}`;

      console.log('Enviando atestado para:', fileName);

      // Primeiro, tentar remover arquivo anterior se existir
      try {
        await supabase.storage
          .from('contratacao-documentos')
          .remove([fileName]);
      } catch (error) {
        // Ignorar erro se arquivo não existir
        console.log('Arquivo anterior não encontrado, continuando...');
      }

      // Upload do novo arquivo
      const { error: uploadError } = await supabase.storage
        .from('contratacao-documentos')
        .upload(fileName, atestadoFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('Erro no upload:', uploadError);
        throw uploadError;
      }

      console.log('✅ Atestado enviado com sucesso!');

      // Resetar status de exame na evolução para 'pendente' quando reenviar atestado
      console.log('🔄 Atualizando status do exame para user_id:', user.id);
      const { data: updateData, error: updateError } = await supabase
        .from('evolucao_contratacao')
        .update({ 
          exame_status: 'pendente',
          exame_aprovado: null,
          exame_reprovado_motivo: null,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select();

      if (updateError) {
        console.error('❌ Erro ao atualizar status do exame:', updateError);
        // Não falhar por causa disso, apenas logar o erro
      } else {
        console.log(`✅ Atualizado ${updateData?.length || 0} registro(s) de evolução`);
        if ((updateData?.length || 0) !== 1) {
          console.warn(`⚠️ ATENÇÃO: Esperado 1 registro, mas ${updateData?.length || 0} foram atualizados!`);
        }
      }

      toast({
        title: "Atestado enviado!",
        description: "Aguarde, nossa equipe está validando seu atestado de saúde.",
      });

      setAtestadoFile(null);
      
      // Recarregar dados do exame para mostrar status atualizado
      await loadEvolucaoData();
      
      return true;
    } catch (error) {
      console.error('Erro ao enviar atestado:', error);
      toast({
        title: "Erro no envio",
        description: `Erro ao enviar atestado: ${error.message}`,
        variant: "destructive"
      });
      return false;
    } finally {
      setUploadingAtestado(false);
    }
  };

  const visualizarGuiaExame = async () => {
    if (!exameData?.guia_exame_path) return;

    try {
      // Gerar URL assinada para visualização
      const { data: urlData, error: urlError } = await supabase.storage
        .from('contratacao-documentos')
        .createSignedUrl(exameData.guia_exame_path, 3600); // URL válida por 1 hora

      if (urlError || !urlData?.signedUrl) {
        toast({
          title: "Erro ao acessar guia",
          description: "Não foi possível gerar link de acesso à guia.",
          variant: "destructive"
        });
        return;
      }

      setGuiaUrl(urlData.signedUrl);
      setShowGuiaViewer(true);
    } catch (error) {
      console.error('Erro ao visualizar guia:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar guia.",
        variant: "destructive"
      });
    }
  };

  const baixarGuiaExame = async () => {
    if (!guiaUrl) return;

    try {
      const response = await fetch(guiaUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `guia-exame-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Download iniciado",
        description: "A guia está sendo baixada.",
      });
    } catch (error) {
      console.error('Erro ao baixar guia:', error);
      toast({
        title: "Erro",
        description: "Erro ao baixar guia.",
        variant: "destructive"
      });
    }
  };

  // Função para baixar documento
  const baixarDocumento = async () => {
    if (!documentoSelecionado) return;

    try {
      const response = await fetch(documentoSelecionado.url);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentoSelecionado.nome;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Download iniciado",
        description: "O documento está sendo baixado.",
      });
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      toast({
        title: "Erro no download",
        description: "Não foi possível baixar o documento.",
        variant: "destructive"
      });
    }
  };

  // Função para permitir substituição de documento
  const permitirSubstituicao = (tipoDocumento: string) => {
    // Remover status de aprovado/reprovado para permitir nova seleção
    setDocumentosAprovados(prev => ({ ...prev, [tipoDocumento]: false }));
    setDocumentosReprovados(prev => ({ ...prev, [tipoDocumento]: false }));
    setErrosDocumentos(prev => ({ ...prev, [tipoDocumento]: '' }));
    
    // Limpar arquivo atual se houver
    setDocumentos(prev => ({ ...prev, [tipoDocumento]: null }));
    
    toast({
      title: "Documento liberado para substituição",
      description: "Agora você pode selecionar um novo arquivo.",
    });
  };

  // Funções LGPD
  const handleLgpdAceite = async () => {
    setProcessingLgpd(true);
    try {
      const { error } = await supabase
        .from('contratacao')
        .update({ 
          lgpd_aceite: true, 
          lgpd_data: new Date().toISOString() 
        })
        .eq('email', user?.email);

      if (error) throw error;

      setShowLgpdModal(false);
      setLgpdChecked(true);
      
      // Salvar na sessão que foi aceito
      const sessionKey = `lgpd_aceito_${user?.email}`;
      sessionStorage.setItem(sessionKey, 'true');
      
      // Após aceitar LGPD, carregar dados se ainda não foram carregados
      if (!evolucaoData) {
        loadEvolucaoData();
        loadTentativasDocumentos();
      }
      
      toast({
        title: "Consentimento registrado",
        description: "Agradecemos por aceitar os termos de uso de dados.",
      });
    } catch (error) {
      console.error('Erro ao salvar aceite LGPD:', error);
      toast({
        title: "Erro",
        description: "Erro ao registrar consentimento. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setProcessingLgpd(false);
    }
  };

  const handleLgpdRejeicao = () => {
    signOut();
  };

  const loadEvolucaoData = async () => {
    try {
      // Carregar evolução da contratação - sempre buscar o mais recente
      console.log('🔍 Carregando evolução para user_id:', user?.id);
      const { data: evolucaoData, error: evolucaoError } = await supabase
        .from('evolucao_contratacao')
        .select('*')
        .eq('user_id', user?.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (evolucaoError && evolucaoError.code !== 'PGRST116') {
        throw evolucaoError;
      }

       if (evolucaoData) {
         setEvolucaoData(evolucaoData);
         // Normaliza chaves de etapas herdadas de versões antigas
         const normalizeEtapaKey = (key?: string | null) => {
           const map: Record<string, string> = {
             contrato: 'assinatura_contrato',
             validacao: 'validacao_documentos',
             documentos: 'envio_documentos',
           };
           if (!key) return '';
           return map[key] || key;
         };
         const etapaAtualNorm = normalizeEtapaKey(evolucaoData.etapa_atual);
         const etapaMaisAvancadaNorm = normalizeEtapaKey(evolucaoData.etapa_mais_avancada);
         setEtapaMaisAvancada(etapaMaisAvancadaNorm || etapaAtualNorm);
         
         // Verificar o estágio e aceite na tabela contratacao para definir a etapa inicial correta
         const { data: contratacaoDataResult } = await supabase
          .from('contratacao')
          .select('id, stage, bt_aceite, aceite_proposta, data_inicio, horario_entrada, horario_saida, quem_recebera_contratado, observacoes, vale_transporte_valor_ida, vale_transporte_valor_volta')
          .eq('email', user?.email)
          .maybeSingle() as { data: any; error: any };
        
        setContratacaoData(contratacaoDataResult);
        
        // Carregar valores de vale transporte
        if (contratacaoDataResult) {
          setValeTransporte({
            valor_ida: contratacaoDataResult.vale_transporte_valor_ida || 0,
            valor_volta: contratacaoDataResult.vale_transporte_valor_volta || 0
          });
          
          // Buscar dados detalhados do vale transporte
          try {
            const { data: vtDetalhado } = await supabase
              .from('vale_transporte_detalhado')
              .select('*')
              .eq('user_id', user?.id)
              .maybeSingle();
            
            if (vtDetalhado) {
              setValeTransporteDetalhado(vtDetalhado);
            }
          } catch (error) {
            console.error('Erro ao buscar vale transporte detalhado:', error);
          }
        }
        
        // Carregar status de confirmação de transporte
        if (evolucaoData) {
          setTransporteConfirmado(evolucaoData.transporte_confirmado || false);
        }
        
        // Definir o contratacao_id para usar nos componentes
        if (contratacaoDataResult?.id) {
          setContratacaoId(contratacaoDataResult.id);
        }
        
        // Determinar a etapa inicial baseada na evolução (normalizada)
        let etapaInicial = 'proposta';
        
        if (etapaMaisAvancadaNorm) {
          etapaInicial = etapaMaisAvancadaNorm;
        } 
        // Se não tem etapa mais avançada mas tem etapa atual, usar ela
        else if (etapaAtualNorm) {
          etapaInicial = etapaAtualNorm;
        }
        // Se proposta foi aceita mas não tem evolução, ir para dados pessoais
        else if (contratacaoDataResult?.bt_aceite === 1) {
          etapaInicial = 'dados_pessoais';
        }
        
        console.log('🚀 Carregando etapa inicial:', { 
          bt_aceite: contratacaoDataResult?.bt_aceite, 
          etapa_atual: evolucaoData.etapa_atual,
          etapa_mais_avancada: evolucaoData.etapa_mais_avancada,
          etapaAtualNorm,
          etapaMaisAvancadaNorm,
          etapaInicial,
          currentEtapa 
        });
        
        // Só definir a etapa se ela realmente precisar mudar E se não estivermos num loop
        const novaEtapa = etapaInicial;
        if (currentEtapa !== novaEtapa) {
          console.log('🎯 Mudando etapa de', currentEtapa, 'para', novaEtapa);
          setCurrentEtapa(novaEtapa);
        } else {
          console.log('⏭️ Etapa já está correta:', currentEtapa);
        }
        
        // ============================================
        // VERIFICAÇÃO DE CONSISTÊNCIA DE ETAPAS
        // ============================================
        // Verificar se a etapa atual faz sentido com os dados reais
        if (evolucaoData && contratacaoDataResult) {
          console.log('🔍 Verificando consistência da etapa:', etapaAtualNorm);
          
          // Buscar dados reais para validação
          const { data: dadosPessoaisCheck } = await supabase
            .from('dados_pessoais')
            .select('id, nome_completo, cpf')
            .eq('user_id', user?.id)
            .maybeSingle();
          
          const { data: documentosCheck, count: documentosCount } = await supabase
            .from('documentos_candidatos')
            .select('*', { count: 'exact' })
            .eq('user_id', user?.id);
          
          const { data: contratosCheck } = await supabase
            .from('contratacao_contratos')
            .select('bt_aceite, clicksign_assinado')
            .eq('contratacao_id', contratacaoDataResult.id);
          
          // Determinar a etapa correta baseada nos dados reais
          let etapaCorreta = 'proposta';
          
          // IMPORTANTE: Índice da etapa mais avançada já alcançada (para evitar regressão)
          const etapaMaisAvancadaIndex = etapas.findIndex(e => e.key === etapaMaisAvancadaNorm);
          const propostaIndex = etapas.findIndex(e => e.key === 'proposta');
          const jáPassouDeProposta = etapaMaisAvancadaIndex > propostaIndex;
          
          console.log('🔍 Verificação anti-regressão:', {
            etapaMaisAvancadaNorm,
            etapaMaisAvancadaIndex,
            jáPassouDeProposta
          });
          
          // Se proposta não foi aceita E o usuário ainda NÃO passou de proposta, deve estar em proposta
          if (!jáPassouDeProposta && !contratacaoDataResult.aceite_proposta && !contratacaoDataResult.bt_aceite) {
            etapaCorreta = 'proposta';
          }
          // Se proposta foi aceita mas não tem dados pessoais
          else if ((contratacaoDataResult.aceite_proposta || contratacaoDataResult.bt_aceite || jáPassouDeProposta) && 
                   (!dadosPessoaisCheck || !dadosPessoaisCheck.nome_completo || !dadosPessoaisCheck.cpf)) {
            etapaCorreta = 'dados_pessoais';
          }
          // Se tem dados pessoais mas não confirmou transporte
          else if (dadosPessoaisCheck && !evolucaoData.transporte_confirmado) {
            etapaCorreta = 'transporte';
          }
          // Se confirmou transporte mas não tem documentos
          else if (dadosPessoaisCheck && evolucaoData.transporte_confirmado && (!documentosCount || documentosCount === 0)) {
            etapaCorreta = 'envio_documentos';
          }
          // Se tem documentos mas não foram aprovados
          else if (documentosCount && documentosCount > 0) {
            const { data: tentativasCheck } = await supabase
              .from('contratacao_tentativas_documentos')
              .select('resultado')
              .eq('contratacao_id', contratacaoDataResult.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            
            if (!tentativasCheck || tentativasCheck.resultado !== 'aprovado') {
              etapaCorreta = 'validacao_documentos';
            } else {
              // Documentos aprovados, verificar exame
              if (!evolucaoData.exame_aprovado) {
                etapaCorreta = 'exame_admissional';
              } else {
                // Exame aprovado, verificar contratos
                const todosContratosAssinados = contratosCheck?.every(c => 
                  c.bt_aceite && (c.clicksign_assinado === 'signed' || c.clicksign_assinado === 'S')
                );
                
                if (!todosContratosAssinados || !contratosCheck || contratosCheck.length === 0) {
                  etapaCorreta = 'assinatura_contrato';
                } else {
                  // Tudo completo
                  etapaCorreta = 'integracao';
                }
              }
            }
          }
          
          // IMPORTANTE: Nunca regredir além da etapa mais avançada já alcançada
          const etapaCorretaIndex = etapas.findIndex(e => e.key === etapaCorreta);
          
          if (etapaCorretaIndex < etapaMaisAvancadaIndex) {
            console.log('⚠️ REGRESSÃO BLOQUEADA!');
            console.log(`   Etapa calculada: ${etapaCorreta} (índice ${etapaCorretaIndex})`);
            console.log(`   Etapa mais avançada: ${etapaMaisAvancadaNorm} (índice ${etapaMaisAvancadaIndex})`);
            console.log('   Mantendo etapa mais avançada sem regredir.');
            // Manter a etapa mais avançada
            etapaCorreta = etapaMaisAvancadaNorm;
          }
          
          // Se a etapa atual está inconsistente, corrigir
          if (etapaCorreta !== etapaAtualNorm) {
            console.log('⚠️ INCONSISTÊNCIA DETECTADA!');
            console.log(`   Etapa registrada: ${etapaAtualNorm}`);
            console.log(`   Etapa correta: ${etapaCorreta}`);
            console.log('   Corrigindo automaticamente...');
            
            // Atualizar evolucao_contratacao - mas nunca diminuir etapa_mais_avancada
            const novaEtapaMaisAvancada = etapaMaisAvancadaIndex >= etapaCorretaIndex 
              ? etapaMaisAvancadaNorm 
              : etapaCorreta;
            
            const { error: updateError } = await supabase
              .from('evolucao_contratacao')
              .update({
                etapa_atual: etapaCorreta,
                etapa_mais_avancada: novaEtapaMaisAvancada,
                updated_at: new Date().toISOString()
              })
              .eq('user_id', user?.id);
            
            if (updateError) {
              console.error('❌ Erro ao corrigir inconsistência:', updateError);
            } else {
              console.log('✅ Inconsistência corrigida com sucesso!');
              setCurrentEtapa(etapaCorreta);
              setEtapaMaisAvancada(novaEtapaMaisAvancada);
              
              toast({
                title: "Progresso sincronizado",
                description: "Suas informações foram atualizadas. Continue de onde parou.",
              });
            }
          } else {
            console.log('✅ Etapa está consistente com os dados');
          }
        }
        // ============================================
        
      } else {
        // Sem registro de evolução - verificar se proposta foi aceita
        console.log('⚠️ Nenhum registro de evolução encontrado');
        if (currentEtapa !== 'proposta') {
          console.log('🎯 Definindo etapa inicial como proposta (sem evolução)');
          setCurrentEtapa('proposta');
        }
      }

      // Carregar dados pessoais da nova tabela
      const { data: dadosData, error: dadosError } = await supabase
        .from('dados_pessoais')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (dadosError && dadosError.code !== 'PGRST116') {
        console.error('Erro ao carregar dados pessoais:', dadosError);
      }

      if (dadosData) {
        // Só bloquear campos se os dados essenciais já foram preenchidos
        const temDadosEssenciais = dadosData.nome_completo && dadosData.cpf;
        setIsPrimeiroEnvio(!temDadosEssenciais);
        
        // Converter campos para o formato esperado pelo formulário
        const dadosFormatados = {
          nomeCompleto: dadosData.nome_completo || '',
          nomeSocial: dadosData.nome_social || '',
          dataNascimento: dadosData.data_nascimento ? new Date(dadosData.data_nascimento).toLocaleDateString('pt-BR') : '',
          cpf: dadosData.cpf || '',
          rg: dadosData.rg || '',
          genero: dadosData.genero || '',
          generoPersonalizado: '',
          celular: dadosData.celular || '',
          estadoCivil: dadosData.estado_civil || '',
          nacionalidade: dadosData.nacionalidade || 'Brasileiro',
          transporteQuantidadeIda: dadosData.transporte_quantidade_ida || 0,
          transporteValorIda: dadosData.transporte_valor_ida || 0,
          transporteQuantidadeVolta: dadosData.transporte_quantidade_volta || 0,
          transporteValorVolta: dadosData.transporte_valor_volta || 0,
          numeroPIS: dadosData.numero_pis || '',
          tituloEleitor: dadosData.titulo_eleitor || '',
          carteiraTrabalho: dadosData.carteira_trabalho || '',
          corPele: dadosData.cor_pele || '',
          cep: dadosData.cep || '',
          logradouro: dadosData.logradouro || '',
          numero: dadosData.numero || '',
          complemento: dadosData.complemento || '',
          bairro: dadosData.bairro || '',
          cidade: dadosData.cidade || '',
          estado: dadosData.estado || '',
          chavePix: dadosData.chave_pix || '',
          tipoChavePix: dadosData.tipo_chave_pix || '',
          banco: dadosData.banco || '',
          agencia: dadosData.agencia || '',
          conta: dadosData.conta || '',
          tipoConta: dadosData.tipo_conta || '',
          nivelEscolaridade: '',
          // Campos de responsável legal
          tipoResponsavel: dadosData.tipo_responsavel || '',
          nomeResponsavel: dadosData.nome_responsavel || '',
          cpfResponsavel: dadosData.cpf_responsavel || '',
          emailResponsavel: dadosData.email_responsavel || '',
          descricaoOutroResponsavel: dadosData.descricao_outro_responsavel || ''
        };
        setDadosPessoais(dadosFormatados);
        
        // Configurar modais de transporte baseado nos dados existentes
        if (dadosData.transporte_quantidade_ida > 0 || dadosData.transporte_valor_ida > 0 || 
            dadosData.transporte_quantidade_volta > 0 || dadosData.transporte_valor_volta > 0) {
          // Por padrão, assumir que são dados de ônibus se existem dados de transporte
          setModaisTransporte({ onibus: true, trilhos: false });
          setTransporteOnibus({
            quantidadeIda: dadosData.transporte_quantidade_ida || 0,
            valorIda: dadosData.transporte_valor_ida || 0,
            quantidadeVolta: dadosData.transporte_quantidade_volta || 0,
            valorVolta: dadosData.transporte_valor_volta || 0
          });
        }
      }

      // Carregar dados de deficiência
      const { data: deficienciaData, error: deficienciaError } = await supabase
        .from('user_disabilities')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (deficienciaError && deficienciaError.code !== 'PGRST116') {
        console.error('Erro ao carregar dados de deficiência:', deficienciaError);
      }

      if (deficienciaData) {
        setDadosDeficiencia({
          possui_deficiencia: deficienciaData.possui_deficiencia || false,
          tipo_deficiencia: deficienciaData.tipo_deficiencia || '',
          categoria_deficiencia: deficienciaData.categoria_deficiencia || '',
          detalhes_deficiencia: deficienciaData.detalhes_deficiencia || ''
        });
      }
    } catch (error) {
      console.error('Erro ao carregar evolução:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do processo.",
        variant: "destructive"
      });
      // Carregar tentativas de documentos
      await loadTentativasDocumentos();
    } finally {
      setLoading(false);
    }
  };

  const loadTentativasDocumentos = async () => {
    if (!user?.email) return;

    try {
      // Buscar dados de contratação para obter tentativas
      const { data: contratacaoData, error } = await supabase
        .from('contratacao')
        .select('tentativas_documentos')
        .eq('email', user.email)
        .single();

      if (!error && contratacaoData) {
        const tentativas = contratacaoData.tentativas_documentos || 0;
        setTentativasDocumentos(tentativas);
        setMostrarAvisoTentativas(tentativas >= 3);
      }
    } catch (error) {
      console.error('Erro ao carregar tentativas:', error);
    }
  };

  const createInitialRecord = async () => {
    try {
      console.log('📝 Criando/atualizando registro inicial para user_id:', user?.id);
      const { data, error } = await supabase
        .from('evolucao_contratacao')
        .upsert({
          user_id: user?.id,
          etapa_atual: 'proposta',
          etapa_mais_avancada: 'proposta',
          proposta_status: 'pendente',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id',  // Com a constraint UNIQUE, evita duplicatas
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Erro ao criar/atualizar registro inicial:', error);
        throw error;
      }
      
      console.log('✅ Registro inicial criado/atualizado:', data);
      setEvolucaoData(data);
    } catch (error) {
      console.error('❌ Erro ao criar registro inicial:', error);
    }
  };

  const validarFormulario = () => {
    const newErrors: {[key: string]: string} = {};

    // Nome completo - apenas letras
    if (!dadosPessoais.nomeCompleto.trim()) {
      newErrors.nomeCompleto = 'Nome completo é obrigatório';
    } else if (!/^[A-Za-zÀ-ú\s]+$/.test(dadosPessoais.nomeCompleto)) {
      newErrors.nomeCompleto = 'Nome deve conter apenas letras e espaços';
    }

    // Data de nascimento - validação de idade mínima
    if (!dadosPessoais.dataNascimento) {
      newErrors.dataNascimento = 'Data de nascimento é obrigatória';
    } else if (!validarDataNascimento(dadosPessoais.dataNascimento)) {
      newErrors.dataNascimento = 'Data de nascimento inválida';
    } else {
      const idade = calcularIdade(formatarData(dadosPessoais.dataNascimento));
      if (idade < 16) {
        newErrors.dataNascimento = 'Idade mínima para contratação é de 16 anos. Por favor, entre em contato com o RH.';
      }
    }

    // CPF
    if (!validarCPF(dadosPessoais.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }

      // RG
      if (!validarRG(dadosPessoais.rg)) {
        newErrors.rg = 'RG inválido. Informe entre 5 e 14 caracteres alfanuméricos.';
      }

    // Gênero
    if (!dadosPessoais.genero) {
      newErrors.genero = 'Gênero é obrigatório';
    }

    // Celular
    if (!dadosPessoais.celular.trim()) {
      newErrors.celular = 'Celular é obrigatório';
    }

    // Estado civil
    if (!dadosPessoais.estadoCivil.trim()) {
      newErrors.estadoCivil = 'Estado civil é obrigatório';
    }

    // PIS - apenas números e exatamente 11 dígitos
    const pisNumeros = dadosPessoais.numeroPIS.replace(/\D/g, '');
    if (!pisNumeros) {
      newErrors.numeroPIS = 'Número do PIS é obrigatório';
    } else if (pisNumeros.length !== 11) {
      newErrors.numeroPIS = 'PIS deve ter exatamente 11 dígitos';
    } else if (!validarPIS(pisNumeros)) {
      newErrors.numeroPIS = 'Número do PIS inválido';
    }

    // Título de Eleitor - apenas números e exatamente 12 dígitos
    const tituloNumeros = dadosPessoais.tituloEleitor.replace(/\D/g, '');
    if (!tituloNumeros) {
      newErrors.tituloEleitor = 'Número de inscrição do título de eleitor é obrigatório';
    } else if (!validarTituloEleitor(tituloNumeros)) {
      newErrors.tituloEleitor = 'Número de inscrição inválido. Verifique se o título possui 12 dígitos numéricos válidos.';
    }

    // Carteira de Trabalho - apenas números e 7 ou 11 dígitos
    const carteiraNumeros = dadosPessoais.carteiraTrabalho.replace(/\D/g, '');
    if (!carteiraNumeros) {
      newErrors.carteiraTrabalho = 'Número da Carteira de Trabalho é obrigatório';
    } else if (!validarCarteiraTrabalho(carteiraNumeros)) {
      newErrors.carteiraTrabalho = 'Número da Carteira de Trabalho inválido. Informe um número de 7 dígitos (modelo antigo) ou o número do CPF com 11 dígitos.';
    }

    // Cor da pele
    if (!dadosPessoais.corPele) {
      newErrors.corPele = 'Cor da pele é obrigatória';
    }

    // Endereço
    if (!dadosPessoais.cep.trim()) {
      newErrors.cep = 'CEP é obrigatório';
    }
    if (!dadosPessoais.logradouro.trim()) {
      newErrors.logradouro = 'Logradouro é obrigatório';
    }
    if (!dadosPessoais.numero.trim()) {
      newErrors.numero = 'Número é obrigatório';
    }
    if (!dadosPessoais.bairro.trim()) {
      newErrors.bairro = 'Bairro é obrigatório';
    }
    if (!dadosPessoais.cidade.trim()) {
      newErrors.cidade = 'Cidade é obrigatória';
    }
    if (!dadosPessoais.estado.trim()) {
      newErrors.estado = 'Estado é obrigatório';
    }

    // Dados bancários
    if (!dadosPessoais.chavePix.trim()) {
      newErrors.chavePix = 'Chave PIX é obrigatória';
    }
    if (!dadosPessoais.banco.trim()) {
      newErrors.banco = 'Banco é obrigatório';
    }
    if (!dadosPessoais.agencia.trim()) {
      newErrors.agencia = 'Agência é obrigatória';
    }
    if (!dadosPessoais.conta.trim()) {
      newErrors.conta = 'Conta é obrigatória';
    }
    if (!dadosPessoais.tipoConta) {
      newErrors.tipoConta = 'Tipo de conta é obrigatório';
    }

    // Validação de deficiência
    if (dadosDeficiencia.possui_deficiencia && !dadosDeficiencia.categoria_deficiencia) {
      newErrors.categoria_deficiencia = 'Tipo de deficiência é obrigatório';
    }

    // Validação de responsável legal para menores de 18 anos
    if (dadosPessoais.dataNascimento && validarDataNascimento(dadosPessoais.dataNascimento)) {
      const idade = calcularIdade(formatarData(dadosPessoais.dataNascimento));
      if (idade < 18) {
        if (!dadosPessoais.tipoResponsavel) {
          newErrors.tipoResponsavel = 'Tipo de responsável é obrigatório para menores de 18 anos';
        }
        if (!dadosPessoais.nomeResponsavel.trim()) {
          newErrors.nomeResponsavel = 'Nome do responsável é obrigatório para menores de 18 anos';
        }
        if (!validarCPF(dadosPessoais.cpfResponsavel)) {
          newErrors.cpfResponsavel = 'CPF do responsável é obrigatório e deve ser válido para menores de 18 anos';
        }
        if (!dadosPessoais.emailResponsavel.trim()) {
          newErrors.emailResponsavel = 'E-mail do responsável é obrigatório para menores de 18 anos';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dadosPessoais.emailResponsavel)) {
          newErrors.emailResponsavel = 'E-mail do responsável deve ter um formato válido';
        }
        if (dadosPessoais.tipoResponsavel === 'Outro' && !dadosPessoais.descricaoOutroResponsavel.trim()) {
          newErrors.descricaoOutroResponsavel = 'Descrição é obrigatória quando "Outro" é selecionado';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const enviarDados = async () => {
    if (!validarFormulario()) {
      toast({
        title: "Erro de validação",
        description: "Por favor, corrija os campos destacados.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Preparar dados para salvar na tabela dados_pessoais
      const dadosParaSalvar = {
        user_id: user?.id,
        nome_completo: dadosPessoais.nomeCompleto,
        nome_social: dadosPessoais.nomeSocial || null,
        data_nascimento: dadosPessoais.dataNascimento ? formatarData(dadosPessoais.dataNascimento) : null,
        cpf: dadosPessoais.cpf,
        rg: dadosPessoais.rg,
        genero: dadosPessoais.genero,
        genero_personalizado: dadosPessoais.genero === 'outro' ? dadosPessoais.generoPersonalizado : null,
        celular: dadosPessoais.celular,
        estado_civil: dadosPessoais.estadoCivil,
        nacionalidade: dadosPessoais.nacionalidade,
        transporte_quantidade_ida: modaisTransporte?.onibus ? transporteOnibus?.quantidadeIda || 0 : (modaisTransporte?.trilhos ? transporteTrilhos?.quantidadeIda || 0 : 0),
        transporte_valor_ida: modaisTransporte?.onibus ? transporteOnibus?.valorIda || 0 : (modaisTransporte?.trilhos ? transporteTrilhos?.valorIda || 0 : 0),
        transporte_quantidade_volta: modaisTransporte?.onibus ? transporteOnibus?.quantidadeVolta || 0 : (modaisTransporte?.trilhos ? transporteTrilhos?.quantidadeVolta || 0 : 0),
        transporte_valor_volta: modaisTransporte?.onibus ? transporteOnibus?.valorVolta || 0 : (modaisTransporte?.trilhos ? transporteTrilhos?.valorVolta || 0 : 0),
        numero_pis: dadosPessoais.numeroPIS,
        titulo_eleitor: dadosPessoais.tituloEleitor,
        carteira_trabalho: dadosPessoais.carteiraTrabalho,
        cor_pele: dadosPessoais.corPele,
        cep: dadosPessoais.cep,
        logradouro: dadosPessoais.logradouro,
        numero: dadosPessoais.numero,
        complemento: dadosPessoais.complemento,
        bairro: dadosPessoais.bairro,
        cidade: dadosPessoais.cidade,
        estado: dadosPessoais.estado,
        chave_pix: dadosPessoais.chavePix,
        tipo_chave_pix: dadosPessoais.tipoChavePix,
        banco: dadosPessoais.banco,
        agencia: dadosPessoais.agencia,
        conta: dadosPessoais.conta,
        tipo_conta: dadosPessoais.tipoConta,
        nivel_escolaridade: dadosPessoais.nivelEscolaridade || null
      };

      // Salvar ou atualizar dados pessoais
      const { error: dadosError } = await supabase
        .from('dados_pessoais')
        .upsert(dadosParaSalvar, { onConflict: 'user_id' });

      if (dadosError) throw dadosError;

      // Salvar ou atualizar dados de deficiência
      if (dadosDeficiencia.possui_deficiencia) {
        const { error: deficienciaError } = await supabase
          .from('user_disabilities')
          .upsert({
            user_id: user?.id,
            possui_deficiencia: dadosDeficiencia.possui_deficiencia,
            tipo_deficiencia: dadosDeficiencia.tipo_deficiencia || null,
            categoria_deficiencia: dadosDeficiencia.categoria_deficiencia || null,
            detalhes_deficiencia: dadosDeficiencia.detalhes_deficiencia || null
          }, { onConflict: 'user_id' });

        if (deficienciaError) throw deficienciaError;
      } else {
        // Se não possui deficiência, remover registro se existir
        const { error: deleteError } = await supabase
          .from('user_disabilities')
          .delete()
          .eq('user_id', user?.id);

        if (deleteError && deleteError.code !== 'PGRST116') {
          console.error('Erro ao remover dados de deficiência:', deleteError);
        }
      }

      // Após o primeiro envio bem-sucedido, marcar como não sendo mais o primeiro envio
      if (isPrimeiroEnvio) {
        setIsPrimeiroEnvio(false);
      }

      // Marcar dados pessoais como concluídos na evolução sempre que são salvos
      const { error: evolucaoError } = await supabase
        .from('evolucao_contratacao')
        .upsert({
          user_id: user?.id,
          dados_pessoais: { status: 'concluido', timestamp: new Date().toISOString() }
        }, { onConflict: 'user_id' });

      if (evolucaoError) {
        console.error('Erro ao atualizar evolução dos dados pessoais:', evolucaoError);
      }

      // Atualizar etapa na tabela de evolução apenas se for a primeira vez
      // ou se o usuário estiver na primeira etapa
      const etapaAtualIndex = etapas.findIndex(e => e.key === currentEtapa);
      const proximaEtapa = 'envio_documentos';
      const proximaEtapaIndex = etapas.findIndex(e => e.key === proximaEtapa);
      
      // Só avançar se estivermos na primeira etapa ou se for progresso natural
      if (currentEtapa === 'dados_pessoais' || etapaAtualIndex < proximaEtapaIndex) {
        // Registrar a transição usando o novo sistema
        await registrarTransicaoEtapa(proximaEtapa, currentEtapa);

        setCurrentEtapa(proximaEtapa);
        if (etapaMaisAvancada === 'dados_pessoais') {
          setEtapaMaisAvancada(proximaEtapa);
        }
      }
      
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Erro ao enviar dados:', error);
      toast({
        title: "Erro",
        description: "Erro ao enviar dados.",
        variant: "destructive"
      });
    }
  };

  const handleCEPChange = async (cep: string) => {
    setDadosPessoais({...dadosPessoais, cep});
    
    if (cep.replace(/\D/g, '').length === 8) {
      const data = await buscarCEP(cep.replace(/\D/g, ''));
      if (data) {
        setDadosPessoais(prev => ({
          ...prev,
          logradouro: data.logradouro,
          bairro: data.bairro,
          cidade: data.localidade,
          estado: data.uf
        }));
      }
    }
  };

  // Função para registrar transição de etapa
  const registrarTransicaoEtapa = async (novaEtapa: string, etapaAnterior: string) => {
    try {
      // Verificar se é uma nova progressão (evitar duplicatas)
      const etapaAtualIndex = etapas.findIndex(e => e.key === etapaAnterior);
      const novaEtapaIndex = etapas.findIndex(e => e.key === novaEtapa);
      
      // Só registrar se for uma progressão para frente
      if (novaEtapaIndex > etapaAtualIndex) {
        const updateData: any = {
          etapa_atual: novaEtapa,
          updated_at: new Date().toISOString()
        };

        // Atualizar status da nova etapa como 'concluido' e etapa mais avançada
        if (novaEtapaIndex > etapas.findIndex(e => e.key === etapaMaisAvancada)) {
          updateData.etapa_mais_avancada = novaEtapa;
        }

        // Atualizar status específico da etapa
        switch (novaEtapa) {
          case 'proposta':
            updateData.proposta_status = 'concluido';
            break;
          case 'dados_pessoais':
            updateData.dados_pessoais = { status: 'concluido', timestamp: new Date().toISOString() };
            break;
          case 'envio_documentos':
            updateData.documentos = { status: 'concluido', timestamp: new Date().toISOString() };
            break;
          case 'validacao_documentos':
            updateData.validacao_status = 'concluido';
            break;
          case 'exame_admissional':
            updateData.exame_status = 'concluido';
            break;
          case 'assinatura_contrato':
            updateData.contrato_assinado = true;
            break;
          case 'integracao':
            updateData.integracao_concluida = true;
            break;
        }

        const { error } = await supabase
          .from('evolucao_contratacao')
          .update(updateData)
          .eq('user_id', user?.id);

        if (error) throw error;

        console.log(`Transição registrada: ${etapaAnterior} → ${novaEtapa}`);
      }
    } catch (error) {
      console.error('Erro ao registrar transição de etapa:', error);
    }
  };

  // Função para atualizar apenas o nome social
  const atualizarNomeSocial = async (nomeSocial: string) => {
    if (!user?.id) return;
    
    try {
      const { error } = await supabase
        .from('dados_pessoais')
        .upsert({
          user_id: user.id,
          nome_social: nomeSocial || null
        }, { onConflict: 'user_id' });

      if (error) {
        console.error('Erro ao atualizar nome social:', error);
      } else {
        console.log('Nome social atualizado com sucesso');
      }
    } catch (error) {
      console.error('Erro ao salvar nome social:', error);
    }
  };

  const avancarEtapa = async () => {
    const currentIndex = etapas.findIndex(e => e.key === currentEtapa);
    if (currentIndex < etapas.length - 1) {
      const nextEtapa = etapas[currentIndex + 1].key;
      
      try {
        // VALIDAÇÕES OBRIGATÓRIAS antes de avançar
        // Validar etapa PROPOSTA
        if (currentEtapa === 'proposta') {
          const { data: contratacaoData } = await supabase
            .from('contratacao')
            .select('aceite_proposta')
            .eq('email', user?.email)
            .maybeSingle();
          
          if (!contratacaoData?.aceite_proposta) {
            toast({
              title: "Ação necessária",
              description: "Você precisa aceitar a proposta antes de continuar.",
              variant: "destructive"
            });
            return;
          }
        }
        
        // Validar etapa DADOS PESSOAIS
        if (currentEtapa === 'dados_pessoais') {
          const { data: dadosPessoaisData } = await supabase
            .from('dados_pessoais')
            .select('id, nome_completo, cpf, data_nascimento')
            .eq('user_id', user?.id)
            .maybeSingle();
          
          if (!dadosPessoaisData || !dadosPessoaisData.nome_completo || !dadosPessoaisData.cpf) {
            toast({
              title: "Dados incompletos",
              description: "Você precisa preencher seus dados pessoais antes de continuar.",
              variant: "destructive"
            });
            return;
          }
        }
        
        // Validar etapa ENVIO DOCUMENTOS
        if (currentEtapa === 'envio_documentos') {
          const { data: documentosData, count } = await supabase
            .from('documentos_candidatos')
            .select('*', { count: 'exact' })
            .eq('user_id', user?.id);
          
          if (!count || count === 0) {
            toast({
              title: "Documentos necessários",
              description: "Você precisa enviar pelo menos um documento antes de continuar.",
              variant: "destructive"
            });
            return;
          }
        }
        
        // Validar etapa VALIDAÇÃO DOCUMENTOS
        if (currentEtapa === 'validacao_documentos') {
          const { data: tentativasData } = await supabase
            .from('contratacao_tentativas_documentos')
            .select('resultado')
            .eq('contratacao_id', contratacaoId)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          
          if (!tentativasData || tentativasData.resultado !== 'aprovado') {
            toast({
              title: "Validação pendente",
              description: "Aguarde a aprovação dos seus documentos pelo RH.",
              variant: "destructive"
            });
            return;
          }
        }
        
        // Validar etapa EXAME ADMISSIONAL
        if (currentEtapa === 'exame_admissional') {
          const { data: evolucaoData } = await supabase
            .from('evolucao_contratacao')
            .select('exame_aprovado')
            .eq('user_id', user?.id)
            .maybeSingle();
          
          if (!evolucaoData?.exame_aprovado) {
            toast({
              title: "Exame pendente",
              description: "Aguarde a aprovação do seu exame admissional.",
              variant: "destructive"
            });
            return;
          }
        }
        
        // Validar etapa ASSINATURA CONTRATO
        if (currentEtapa === 'assinatura_contrato') {
          const { data: contratosData, count } = await supabase
            .from('contratacao_contratos')
            .select('bt_aceite, clicksign_assinado', { count: 'exact' })
            .eq('contratacao_id', contratacaoId);
          
          if (!count || count === 0) {
            toast({
              title: "Contratos não encontrados",
              description: "Nenhum contrato disponível. Entre em contato com o RH.",
              variant: "destructive"
            });
            return;
          }
          
          const todosAssinados = contratosData?.every(c => 
            c.bt_aceite && (c.clicksign_assinado === 'signed' || c.clicksign_assinado === 'S')
          );
          
          if (!todosAssinados) {
            toast({
              title: "Assinatura pendente",
              description: "Você precisa assinar todos os contratos antes de continuar.",
              variant: "destructive"
            });
            return;
          }
        }
        
        // Se passou por todas as validações, pode avançar
        await registrarTransicaoEtapa(nextEtapa, currentEtapa);

        setCurrentEtapa(nextEtapa);
        setEtapaMaisAvancada(nextEtapa);
        toast({
          title: "Etapa avançada",
          description: `Avançou para: ${etapas[currentIndex + 1].label}`
        });
      } catch (error) {
        console.error('Erro ao avançar etapa:', error);
        toast({
          title: "Erro",
          description: "Erro ao avançar etapa.",
          variant: "destructive"
        });
      }
    }
  };

  const navegarParaEtapa = async (targetEtapa: string) => {
    const targetIndex = etapas.findIndex(e => e.key === targetEtapa);
    const etapaMaisAvancadaIndex = etapas.findIndex(e => e.key === etapaMaisAvancada);
    
    // Sempre permitir navegação para a proposta
    if (targetEtapa === 'proposta') {
      setCurrentEtapa(targetEtapa);
      return;
    }
    
    // Para outras etapas, permitir navegação livre para etapas já alcançadas
    if (targetIndex <= etapaMaisAvancadaIndex) {
      setCurrentEtapa(targetEtapa);
      return;
    }
    
    // Para etapas futuras, verificar se a proposta foi aceita
    try {
      const { data: contratacaoData } = await supabase
        .from('contratacao')
        .select('aceite_proposta')
        .eq('email', user?.email)
        .maybeSingle() as { data: any; error: any };

      if (!contratacaoData?.aceite_proposta) {
        toast({
          title: "Aceite necessário",
          description: "Você precisa aceitar a proposta antes de acessar outras etapas.",
          variant: "destructive"
        });
        setCurrentEtapa('proposta');
        return;
      }
    } catch (error) {
      console.error('Erro ao verificar aceite:', error);
      return;
    }
    
    // Permitir navegação apenas para etapas até a mais avançada alcançada
    if (targetIndex > etapaMaisAvancadaIndex) {
      return; // Não permitir navegar para etapas futuras
    }
    
    // Para etapas já concluídas, apenas atualizar o estado local
    // Não é necessário atualizar o banco para navegação entre etapas concluídas
    setCurrentEtapa(targetEtapa);
    
    // Só atualizar o banco se for uma progressão para uma nova etapa
    if (targetIndex === etapaMaisAvancadaIndex) {
      try {
        const { error } = await supabase
          .from('evolucao_contratacao')
          .update({ 
            etapa_atual: targetEtapa
          })
          .eq('user_id', user?.id);

        if (error) throw error;
      } catch (error) {
        console.error('Erro ao atualizar etapa atual:', error);
        // Não mostrar erro para o usuário se for apenas navegação
      }
    }
  };

  
  // Função para verificar se uma etapa está em modo de visualização (já concluída)
  const isEtapaVisualizacao = (etapa: string) => {
    const etapaIndex = etapas.findIndex(e => e.key === etapa);
    const etapaMaisAvancadaIndex = etapas.findIndex(e => e.key === etapaMaisAvancada);
    const currentEtapaIndex = etapas.findIndex(e => e.key === currentEtapa);
    
    // Se a etapa atual é menor que a mais avançada, está em modo visualização
    return etapaIndex < etapaMaisAvancadaIndex && currentEtapa !== etapa;
  };

  // Definição dos campos de documentos
  // Função para retornar o label do documento de certidão baseado no estado civil
  const getCertidaoLabel = () => {
    switch (dadosPessoais.estadoCivil) {
      case 'casado':
        return 'Certidão de Casamento';
      case 'divorciado':
        return 'Certidão de Divórcio';
      case 'viuvo':
        return 'Certidão de Óbito do Cônjuge';
      default:
        return 'Certidão de Nascimento';
    }
  };

  const camposDocumentos = [
    { key: 'rg', label: 'RG (Frente e Verso)', required: true, onlyImages: true },
    { key: 'certificado_reservista', label: 'Certificado de Reservista', required: dadosPessoais.genero !== 'feminino' },
    { key: 'foto_3x4', label: 'Foto 3x4', required: true, onlyImages: true },
    { key: 'comprovante_bancario', label: 'Comprovante de Dados Bancários', required: true },
    { key: 'comprovante_escolaridade', label: 'Comprovante de Escolaridade', required: true },
    { key: 'titulo_eleitor', label: 'Título de Eleitor', required: true },
    { key: 'comprovante_residencia', label: 'Comprovante de Residência', required: true },
    { key: 'carteira_trabalho', label: 'Carteira de Trabalho', required: true },
    { key: 'certidao_nascimento', label: getCertidaoLabel(), required: true }
  ];

  // Função para validar formato de arquivo
  const validarFormatoArquivo = (file: File): boolean => {
    const formatosPermitidos = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    return formatosPermitidos.includes(file.type);
  };

  // Função para manipular upload de arquivo
  const handleFileUpload = (key: string, file: File | null) => {
    if (!file) {
      setDocumentos(prev => ({ ...prev, [key]: null }));
      return;
    }

    if (!validarFormatoArquivo(file)) {
      toast({
        title: "Formato de arquivo inválido",
        description: "Por favor, envie apenas arquivos JPG, PNG ou PDF.",
        variant: "destructive"
      });
      return;
    }

    setDocumentos(prev => ({ ...prev, [key]: file }));
    
    // Limpar status de reprovado quando novo arquivo for selecionado
    if (documentosReprovados[key]) {
      setDocumentosReprovados(prev => ({ ...prev, [key]: false }));
    }
    
    // Limpar erro anterior se houver
    if (errosDocumentos[key]) {
      setErrosDocumentos(prev => ({ ...prev, [key]: '' }));
    }
  };

  // Função para manipular upload de dependentes
  const handleDependenteFileUpload = (key: string, file: File | null) => {
    if (!file) {
      setDependentesDocumentos(prev => ({ ...prev, [key]: null }));
      return;
    }

    if (!validarFormatoArquivo(file)) {
      toast({
        title: "Formato de arquivo inválido",
        description: "Por favor, envie apenas arquivos JPG, PNG ou PDF.",
        variant: "destructive"
      });
      return;
    }

    setDependentesDocumentos(prev => ({ ...prev, [key]: file }));
  };

  // Função para enviar documentos
  const enviarDocumentos = async () => {
    console.log('🚀 Iniciando envio de documentos...');
    setEnviandoDocumentos(true);
    
    try {
      // Verificar se todos os documentos obrigatórios foram enviados (apenas os não aprovados)
      const documentosObrigatorios = camposDocumentos.filter(campo => 
        campo.required && !documentosAprovados[campo.key]
      );
      
      const documentosFaltando = documentosObrigatorios.filter(campo => 
        !documentos[campo.key]
      );

      if (documentosFaltando.length > 0) {
        toast({
          title: "Documentos obrigatórios faltando",
          description: `Por favor, envie: ${documentosFaltando.map(d => d.label).join(', ')}`,
          variant: "destructive"
        });
        return;
      }

      // Verificar se os dados dos dependentes foram preenchidos corretamente
      const dependentesSemDados = [];
      for (let i = 1; i <= dependentes.quantidade; i++) {
        const dependenteKey = `dependente_${i}`;
        
        // Verificar campos obrigatórios: nome, CPF e tipo
        if (!dependentesNomes[dependenteKey]?.trim()) {
          dependentesSemDados.push(`Dependente ${i} - Nome obrigatório`);
        }
        if (!dependentesCpf[dependenteKey]?.trim()) {
          dependentesSemDados.push(`Dependente ${i} - CPF obrigatório`);
        }
        if (!dependentesTipo[dependenteKey]?.trim()) {
          dependentesSemDados.push(`Dependente ${i} - Tipo obrigatório`);
        }
      }

      if (dependentesSemDados.length > 0) {
        toast({
          title: "Dados dos dependentes obrigatórios",
          description: `Por favor, preencha: ${dependentesSemDados.join(', ')}`,
          variant: "destructive"
        });
        return;
      }

      // Preparar FormData para envio
      const formData = new FormData();
      
      // Calcular novas tentativas primeiro
      const novasTentativas = tentativasDocumentos + 1;
      
      // Adicionar número de dependentes
      formData.append('dependentes_quantidade', dependentes.quantidade.toString());
      
      // Adicionar dados dos dependentes
      Object.keys(dependentesNomes).forEach(key => {
        if (dependentesNomes[key]) {
          formData.append(`${key}_nome`, dependentesNomes[key]);
        }
      });

      // Adicionar CPF dos dependentes
      Object.keys(dependentesCpf).forEach(key => {
        if (dependentesCpf[key]) {
          formData.append(`${key}_cpf`, dependentesCpf[key]);
        }
      });

      // Adicionar tipo dos dependentes
      Object.keys(dependentesTipo).forEach(key => {
        if (dependentesTipo[key]) {
          formData.append(`${key}_tipo`, dependentesTipo[key]);
        }
      });
      
      // Buscar CPF do usuário para usar como pasta
      const { data: dadosPessoais } = await supabase
        .from('dados_pessoais')
        .select('cpf')
        .eq('user_id', user?.id)
        .single();
      
      const cpfPasta = dadosPessoais?.cpf?.replace(/\D/g, '') || user?.id;
      
      // Mapeamento de tipos de documentos para nomes específicos
      const nomesPadrao = {
        'rg': 'RG',
        'comprovante_bancario': 'Comprovante Bancario',
        'comprovante_residencia': 'Comprovante de Residencia',
        'carteira_trabalho': 'Carteira de Trabalho',
        'certidao_nascimento': 'Certidao de Nascimento',
        'comprovante_escolaridade': 'Comprovante de Escolaridade',
        'certificado_reservista': 'Certificado de Reservista',
        'foto_3x4': 'Foto 3x4',
        'titulo_eleitor': 'Titulo de Eleitor'
      };

      // Upload dos arquivos para o bucket e salvar no banco
      await Promise.all(Object.entries(documentos).map(async ([key, file]) => {
        if (file) {
          console.log(`📄 Processando documento: ${key}`, file.name);
          
          // Verificar e limpar duplicatas antes do upload
          console.log(`🔍 Verificando duplicatas para: ${key}`);
          const sucesso = await verificarELimparDuplicatas(key);
          if (!sucesso) {
            console.warn(`⚠️ Falha ao limpar duplicatas para ${key}, continuando...`);
          }
          
          const nomeDocumento = nomesPadrao[key] || key;
          const fileName = `${cpfPasta}/${nomeDocumento}.${file.name.split('.').pop()}`;
          
          console.log(`📤 Fazendo upload do arquivo: ${fileName}`);
          const { error: uploadError } = await supabase.storage
            .from('contratacao-documentos')
            .upload(fileName, file, { upsert: true });
          
          if (uploadError) {
            console.error('❌ Erro no upload:', uploadError);
            throw new Error(`Erro no upload do arquivo ${file.name}`);
          }
          
          console.log('✅ Upload realizado com sucesso');
          formData.append(key, file);
        }
      }));

      // Upload dos dependentes
      console.log('👨‍👩‍👧‍👦 Processando documentos de dependentes...');
      await Promise.all(Object.entries(dependentesDocumentos).map(async ([key, file]) => {
        if (file) {
          console.log(`📄 Processando documento de dependente: ${key}`, file.name);
          const nomeDocumento = nomesPadrao[key] || key;
          // Salvar na pasta dependentes dentro do CPF do usuário
          const fileName = `${cpfPasta}/dependentes/${nomeDocumento}.${file.name.split('.').pop()}`;
          
          // Apagar documento anterior do mesmo tipo se existir
          console.log(`🔍 Verificando documentos de dependentes existentes para tipo: ${key}`);
          const { data: existingDocs, error: searchError } = await supabase
            .from('documentos_contratacao')
            .select('caminho_storage')
            .eq('user_id', user?.id)
            .eq('tipo_documento', key);
          
          if (searchError) {
            console.error('❌ Erro ao buscar documentos de dependentes existentes:', searchError);
          } else {
            console.log(`📋 Documentos de dependentes existentes:`, existingDocs);
          }
          
          if (existingDocs && existingDocs.length > 0) {
            console.log(`🗑️ Removendo ${existingDocs.length} documentos de dependentes antigos do tipo ${key}`);
            // Apagar arquivos antigos do storage
            for (const doc of existingDocs) {
              console.log(`🗑️ Removendo arquivo de dependente do storage: ${doc.caminho_storage}`);
              const { error: removeError } = await supabase.storage
                .from('contratacao-documentos')
                .remove([doc.caminho_storage]);
              
              if (removeError) {
                console.error('❌ Erro ao remover arquivo de dependente do storage:', removeError);
              } else {
                console.log('✅ Arquivo de dependente removido do storage com sucesso');
              }
            }
            // Apagar registros antigos do banco
            console.log(`🗑️ Removendo registros de dependentes do banco para tipo: ${key}`);
            const { error: deleteError } = await supabase
              .from('documentos_contratacao')
              .delete()
              .eq('user_id', user?.id)
              .eq('tipo_documento', key);
              
            if (deleteError) {
              console.error('❌ Erro ao remover registros de dependentes do banco:', deleteError);
            } else {
              console.log('✅ Registros de dependentes removidos do banco com sucesso');
            }
          }
          
          console.log(`📤 Fazendo upload do arquivo de dependente: ${fileName}`);
          const { error: uploadError } = await supabase.storage
            .from('contratacao-documentos')
            .upload(fileName, file);
          
          if (uploadError) {
            console.error('❌ Erro no upload de dependente:', uploadError);
            throw new Error(`Erro no upload do arquivo ${file.name}`);
          } else {
            console.log('✅ Upload de dependente realizado com sucesso');
          }
          
          // Salvar no banco de dados com upsert para garantir apenas um por tipo
          console.log(`💾 Salvando registro de dependente no banco para tipo: ${key}`);
          const { error: upsertError } = await supabase
            .from('documentos_contratacao')
            .upsert({
              user_id: user?.id,
              cpf: cpfPasta,
              tipo_documento: key,
              nome_arquivo: file.name,
              caminho_storage: fileName,
              status_validacao: 'reprovado'
            }, {
              onConflict: 'user_id,tipo_documento'
            });
            
          if (upsertError) {
            console.error('❌ Erro ao salvar dependente no banco:', upsertError);
          } else {
            console.log('✅ Registro de dependente salvo no banco com sucesso');
          }
          
          formData.append(key, file);
        }
      }));

      // Incrementar contador de tentativas no banco
      await supabase
        .from('contratacao')
        .update({ tentativas_documentos: novasTentativas })
        .eq('email', user?.email);

      // Registrar tentativa na tabela de controle
      const documentosEnviados: any = {};
      Object.entries(documentos).forEach(([key, file]) => {
        if (file && !documentosAprovados[key]) {
          documentosEnviados[key] = file.name;
        }
      });
      Object.entries(dependentesDocumentos).forEach(([key, file]) => {
        if (file) {
          documentosEnviados[key] = file.name;
        }
      });

      const { data: contratacaoData } = await supabase
        .from('contratacao')
        .select('id')
        .eq('email', user?.email)
        .single();

      if (contratacaoData) {
        setContratacaoId(contratacaoData.id);
        await supabase
          .from('contratacao_tentativas_documentos')
          .insert({
            contratacao_id: contratacaoData.id,
            tentativa_numero: novasTentativas,
            documentos_enviados: documentosEnviados
          });
      }

      setTentativasDocumentos(novasTentativas);
      setMostrarAvisoTentativas(novasTentativas >= 3);

      // Ir para a tela de validação primeiro
      await registrarTransicaoEtapa('validacao_documentos', currentEtapa);
      setCurrentEtapa('validacao_documentos');
      setEtapaMaisAvancada('validacao_documentos');
      

      // Atualizar banco de dados
      await supabase
        .from('evolucao_contratacao')
        .update({ 
          etapa_atual: 'validacao_documentos',
          etapa_mais_avancada: 'validacao_documentos'
        })
        .eq('user_id', user?.id);

      // Enviar dados para o webhook sem aguardar resposta
      await enviarParaWebhook(formData);
      
      // Mostrar mensagem de validação em progresso
      toast({
        title: "Documentos enviados",
        description: "Seus documentos estão sendo validados, é um processo que pode demorar até 10 minutos. Após esse tempo, atualize a página. Se preferir, pode sair do site e voltar após algum tempo.",
        variant: "default"
      });

    } catch (error) {
      console.error('Erro ao enviar documentos:', error);
      toast({
        title: "Erro no envio",
        description: "Erro ao enviar documentos. Tente novamente.",
        variant: "destructive"
      });
      
      // Se houve erro, voltar para a etapa anterior
      setCurrentEtapa('envio_documentos');
    } finally {
      setEnviandoDocumentos(false);
    }
  };

  // Função para enviar para o webhook sem aguardar resposta
  const enviarParaWebhook = async (formData: FormData) => {
    console.log('Enviando documentos para o webhook...');
    
    try {
      // Get JWT token for authentication
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Usuário não autenticado');
      }

      // Call edge function directly with fetch and proper authentication
      const response = await fetch(
        `https://azyixwxndwhaiimqybmd.supabase.co/functions/v1/enviar-documentos-webhook`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'x-client-info': 'supabase-js-web'
          },
          body: formData
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro na resposta do webhook:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('Envio realizado com sucesso:', data);
    } catch (error) {
      console.error('Erro ao enviar para o webhook:', error);
      throw error;
    }
  };

  // Função para salvar documentos aprovados no Storage e DB
  const salvarDocumentosAprovados = async (novosAprovados: {[key: string]: boolean}) => {
    if (!user?.id) return;
    
    // Buscar CPF do usuário nos dados pessoais
    const { data: dadosPessoais } = await supabase
      .from('dados_pessoais')
      .select('cpf')
      .eq('user_id', user.id)
      .single();
    
    const cpf = dadosPessoais?.cpf;
    if (!cpf) {
      console.error('CPF não encontrado para o usuário');
      toast({
        title: "Erro",
        description: "CPF não encontrado. Complete os dados pessoais primeiro.",
        variant: "destructive"
      });
      return;
    }

    // Função para limpar TODOS os documentos existentes na pasta do CPF
    const limparTodosDocumentosExistentes = async () => {
      try {
        console.log(`🧹 Iniciando limpeza completa da pasta: ${cpf}`);
        
        // Listar todos os arquivos na pasta do CPF
        const { data: arquivos, error: listError } = await supabase.storage
          .from('contratacao-documentos')
          .list(cpf);

        if (listError) {
          console.error('❌ Erro ao listar arquivos para limpeza:', listError);
          return false;
        }

        console.log(`📁 Encontrados ${arquivos?.length || 0} arquivos na pasta ${cpf}:`, arquivos?.map(a => a.name));

        // Remover TODOS os arquivos existentes na pasta um por um
        if (arquivos && arquivos.length > 0) {
          // Criar lista de caminhos para remover
          const caminhosParaRemover = arquivos.map(arquivo => `${cpf}/${arquivo.name}`);
          
          console.log(`🗑️ Removendo ${caminhosParaRemover.length} arquivos:`, caminhosParaRemover);
          
          // Tentar remover todos de uma vez primeiro
          const { error: removeAllError } = await supabase.storage
            .from('contratacao-documentos')
            .remove(caminhosParaRemover);

          if (removeAllError) {
            console.error('❌ Erro ao remover arquivos em lote:', removeAllError);
            
            // Se falhou em lote, tentar um por um
            console.log('🔄 Tentando remoção individual...');
            for (const arquivo of arquivos) {
              const caminhoArquivo = `${cpf}/${arquivo.name}`;
              console.log(`🗑️ Removendo arquivo individualmente: ${caminhoArquivo}`);
              
              const { error: removeError } = await supabase.storage
                .from('contratacao-documentos')
                .remove([caminhoArquivo]);

              if (removeError) {
                console.error(`❌ Erro ao remover arquivo ${caminhoArquivo}:`, removeError);
              } else {
                console.log(`✅ Arquivo removido: ${caminhoArquivo}`);
              }
              
              // Pequena pausa entre remoções
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          } else {
            console.log(`✅ Todos os ${caminhosParaRemover.length} arquivos removidos com sucesso`);
          }
          
          console.log(`✅ Limpeza da pasta ${cpf} concluída`);
        } else {
          console.log(`✅ Pasta ${cpf} já está vazia`);
        }
        
        // Verificar se ainda existem arquivos após a limpeza
        const { data: arquivosRestantes } = await supabase.storage
          .from('contratacao-documentos')
          .list(cpf);
          
        if (arquivosRestantes && arquivosRestantes.length > 0) {
          console.warn(`⚠️ Ainda restam ${arquivosRestantes.length} arquivos após limpeza:`, arquivosRestantes.map(a => a.name));
        } else {
          console.log(`✅ Verificação confirmada: pasta ${cpf} está completamente limpa`);
        }
        
        return true;
      } catch (error) {
        console.error('❌ Erro crítico ao limpar documentos existentes:', error);
        return false;
      }
    };
    
    try {
      // PRIMEIRO: Limpar TODOS os documentos existentes da pasta do CPF antes de fazer qualquer upload
      console.log('Iniciando processo de envio de documentos...');
      const limpezaOk = await limparTodosDocumentosExistentes();
      
      if (!limpezaOk) {
        throw new Error('Falha na limpeza dos documentos existentes');
      }
      
      console.log('Limpeza concluída, iniciando uploads...');
      
      for (const [tipoDocumento, arquivo] of Object.entries(documentos)) {
        if (arquivo) {
          
          // Verificar se o documento foi aprovado para decidir o status
          const aprovado = novosAprovados[tipoDocumento] || false;
          
          // Usar nome simples baseado no tipo de documento
          const nomeArquivo = arquivo.name;
          const caminhoArquivo = `${cpf}/${nomeArquivo}`;
          
          // Upload para o storage (SEMPRE, independente do status)
          const { error: uploadError } = await supabase.storage
            .from('contratacao-documentos')
            .upload(caminhoArquivo, arquivo, {
              cacheControl: '3600',
              upsert: true
            });
          
          if (uploadError) {
            console.error(`Erro ao salvar documento ${tipoDocumento}:`, uploadError);
            toast({
              title: "Aviso",
              description: `Erro ao salvar documento ${tipoDocumento} no storage.`,
              variant: "destructive"
            });
          } else {
            console.log(`Documento ${tipoDocumento} salvo com sucesso no storage:`, caminhoArquivo);
            
            // Salvar status na tabela documentos_contratacao
            const { error: dbError } = await supabase
              .from('documentos_contratacao')
              .upsert({
                user_id: user.id,
                cpf: cpf,
                tipo_documento: tipoDocumento,
                nome_arquivo: nomeArquivo,
                caminho_storage: caminhoArquivo,
                status_validacao: aprovado ? 'aprovado' : 'reprovado'
              }, { onConflict: 'user_id,tipo_documento' });
            
            if (dbError) {
              console.error(`Erro ao salvar status do documento ${tipoDocumento}:`, dbError);
            } else {
              console.log(`Status do documento ${tipoDocumento} salvo na base de dados como ${aprovado ? 'aprovado' : 'reprovado'}`);
            }
          }
        }
      }
      
      // Salvar também documentos de dependentes aprovados
      if (dependentes.quantidade > 0) {
        for (let i = 1; i <= dependentes.quantidade; i++) {
          const rgKey = `dependente_${i}_rg`;
          const escolaridadeKey = `dependente_${i}_comprovante_escolaridade`;
          const certidaoKey = `dependente_${i}_certidao_nascimento`;
          
          // RG do dependente (sempre obrigatório)
          const rgFile = dependentesDocumentos[rgKey];
          if (rgFile) {
            const timestamp = Date.now();
            const extensao = rgFile.name.split('.').pop();
            const nomeArquivo = `${cpf}_${rgKey}_${timestamp}.${extensao}`;
            const caminhoArquivo = `${cpf}/dependentes/${nomeArquivo}`;
            
            const { error: uploadError } = await supabase.storage
              .from('contratacao-documentos')
              .upload(caminhoArquivo, rgFile, {
                cacheControl: '3600',
                upsert: true
              });
            
            if (uploadError) {
              console.error(`Erro ao salvar documento ${rgKey}:`, uploadError);
            } else {
              console.log(`Documento ${rgKey} salvo com sucesso no storage:`, caminhoArquivo);
            }
          }
          
          // Documento adicional baseado na idade
          const idade = dependentes.idades[i];
          let docAdicionalKey = '';
          let docAdicionalFile = null;
          
          if (idade >= 7 && idade <= 14) {
            docAdicionalKey = escolaridadeKey;
            docAdicionalFile = dependentesDocumentos[docAdicionalKey];
          } else if (idade <= 6) {
            docAdicionalKey = `dependente_${i}_comprovante_vacinacao`;
            docAdicionalFile = dependentesDocumentos[docAdicionalKey];
          }
          if (docAdicionalFile) {
            const timestamp = Date.now();
            const extensao = docAdicionalFile.name.split('.').pop();
            const nomeArquivo = `${cpf}_${docAdicionalKey}_${timestamp}.${extensao}`;
            const caminhoArquivo = `${cpf}/dependentes/${nomeArquivo}`;
            
            const { error: uploadError } = await supabase.storage
              .from('contratacao-documentos')
              .upload(caminhoArquivo, docAdicionalFile, {
                cacheControl: '3600',
                upsert: true
              });
            
            if (uploadError) {
              console.error(`Erro ao salvar documento ${docAdicionalKey}:`, uploadError);
            } else {
              console.log(`Documento ${docAdicionalKey} salvo com sucesso no storage:`, caminhoArquivo);
            }
          }
        }
      }
      
      // Salvar dados dos dependentes na nova tabela
      if (dependentes.quantidade > 0) {
        for (let i = 1; i <= dependentes.quantidade; i++) {
          const dependenteKey = `dependente_${i}`;
          const nome = dependentesNomes[dependenteKey];
          const cpfDependente = dependentesCpf[dependenteKey];
          const tipo = dependentesTipo[dependenteKey];
          
          if (nome && cpfDependente && tipo) {
            try {
              const { error: dbError } = await supabase
                .from('dependentes_dados')
                .upsert({
                  user_id: user.id,
                  numero_dependente: i,
                  nome_completo: nome,
                  cpf_dependente: cpfDependente,
                  tipo_dependente: tipo
                }, { onConflict: 'user_id,numero_dependente' });
              
              if (dbError) {
                console.error(`Erro ao salvar dados do dependente ${i}:`, dbError);
              } else {
                console.log(`Dados do dependente ${i} salvos com sucesso`);
              }
            } catch (error) {
              console.error(`Erro ao salvar dados do dependente ${i}:`, error);
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Erro ao salvar documentos no storage:', error);
      toast({
        title: "Aviso",
        description: "Erro ao salvar documentos no storage, mas a validação foi concluída.",
        variant: "destructive"
      });
    }
  };

  // Função para reenvio automático e transparente
  const processarValidacaoComReenvio = async (dadosRetorno: any) => {
    // Identificar documentos reprovados
    const documentosReprovadosArray = [];
    const mapeamentoCampos = {
      'ValidoRG': ['rg'],
      'ValidoCT': ['carteira_trabalho'],
      'ValidoRE': ['certificado_reservista'],
      'Valido34': ['foto_3x4'],
      'ValidoDK': ['comprovante_bancario'],
      'ValidoCE': ['comprovante_escolaridade'],
      'ValidoTE': ['titulo_eleitor'],
      'ValidoCR': ['comprovante_residencia'],
      'ValidoCN': ['certidao_nascimento']
    };

    // Verificar se resultado é um array
    const dadosValidacao = Array.isArray(dadosRetorno) ? dadosRetorno[0] : dadosRetorno;

    // Identificar documentos reprovados
    Object.entries(mapeamentoCampos).forEach(([campoJson, campos]) => {
      if (dadosValidacao[campoJson] === 'NAO') {
        documentosReprovadosArray.push(...campos);
      }
    });

    if (documentosReprovadosArray.length > 0) {
      console.log('Documentos reprovados detectados, tentando reenvio automático...', documentosReprovadosArray);
      
      try {
        // Criar FormData apenas com os documentos reprovados
        const formData = new FormData();
        
        for (const campo of documentosReprovadosArray) {
          const arquivo = documentos[campo];
          if (arquivo) {
            formData.append(campo, arquivo);
          }
        }

        if (!formData.entries().next().done) {
          console.log('Reenviando documentos automaticamente...');
          
          const { data: novoRetorno, error } = await supabase.functions.invoke('enviar-documentos-webhook', {
            body: formData,
          });

          if (error) throw error;

          // Verificar novamente os resultados após reenvio
          const novosDadosValidacao = Array.isArray(novoRetorno) ? novoRetorno[0] : novoRetorno;
          const aindaReprovados = [];

          Object.entries(mapeamentoCampos).forEach(([campoJson, campos]) => {
            if (novosDadosValidacao[campoJson] === 'NAO') {
              aindaReprovados.push(...campos);
            }
          });

          if (aindaReprovados.length > 0) {
            // Ainda há documentos reprovados após segunda tentativa
            processarRespostaWebhook(novoRetorno);
          } else {
            // Todos aprovados após reenvio
            processarRespostaWebhook(novoRetorno);
          }
        } else {
          // Não há arquivos para reenviar
          processarRespostaWebhook(dadosRetorno);
        }
      } catch (error) {
        console.error('Erro no reenvio automático:', error);
        processarRespostaWebhook(dadosRetorno);
      }
    } else {
      // Todos documentos aprovados na primeira tentativa
      processarRespostaWebhook(dadosRetorno);
    }
  };

  // Função para processar resposta do webhook
  const processarRespostaWebhook = async (resultado: any) => {
    setValidandoDocumentos(false);
    
    // Salvar o JSON completo para exibir no popup
    setWebhookJsonResult(resultado);
    setShowWebhookResult(true);
    
    const novosAprovados = { ...documentosAprovados };
    const novosReprovados = { ...documentosReprovados };
    const novosErros = { ...errosDocumentos };

    // Verificar se resultado é um array (como no exemplo fornecido)
    const dadosValidacao = Array.isArray(resultado) ? resultado[0] : resultado;

    // Processar ValidoRG - RG
    if (dadosValidacao.ValidoRG === 'SIM') {
      novosAprovados['rg'] = true;
      delete novosReprovados['rg'];
      delete novosErros['rg'];
    } else if (dadosValidacao.ValidoRG === 'NAO') {
      novosAprovados['rg'] = false;
      novosReprovados['rg'] = true;
      const erroRG = dadosValidacao.ErroRG && dadosValidacao.ErroRG !== 'NULL' ? dadosValidacao.ErroRG : 'Documento rejeitado';
      novosErros['rg'] = erroRG;
    }

    // Processar ValidoCT - Carteira de Trabalho
    if (dadosValidacao.ValidoCT === 'SIM') {
      novosAprovados['carteira_trabalho'] = true;
      delete novosReprovados['carteira_trabalho'];
      delete novosErros['carteira_trabalho'];
    } else if (dadosValidacao.ValidoCT === 'NAO') {
      novosAprovados['carteira_trabalho'] = false;
      novosReprovados['carteira_trabalho'] = true;
      const erroCT = dadosValidacao.ErroCT && dadosValidacao.ErroCT !== 'NULL' ? dadosValidacao.ErroCT : 'Documento rejeitado';
      novosErros['carteira_trabalho'] = erroCT;
    }

    // Mapeamento dos outros campos
    const mapeamentoCampos = {
      'ValidoRE': { campo: 'certificado_reservista', erro: 'ErroRE' },
      'Valido34': { campo: 'foto_3x4', erro: 'Erro34' },
      'ValidoDK': { campo: 'comprovante_bancario', erro: 'ErroDK' },
      'ValidoCE': { campo: 'comprovante_escolaridade', erro: 'ErroCE' },
      'ValidoTE': { campo: 'titulo_eleitor', erro: 'ErroTE' },
      'ValidoCR': { campo: 'comprovante_residencia', erro: 'ErroCR' },
      'ValidoCN': { campo: 'certidao_nascimento', erro: 'ErroCN' }
    };

    // Processar outros campos
    Object.entries(mapeamentoCampos).forEach(([campoJson, config]) => {
      if (dadosValidacao[campoJson] === 'SIM') {
        novosAprovados[config.campo] = true;
        delete novosReprovados[config.campo];
        delete novosErros[config.campo];
      } else if (dadosValidacao[campoJson] === 'NAO') {
        novosAprovados[config.campo] = false;
        novosReprovados[config.campo] = true;
        const erro = dadosValidacao[config.erro] && dadosValidacao[config.erro] !== 'NULL' ? dadosValidacao[config.erro] : 'Documento rejeitado';
        novosErros[config.campo] = erro;
      }
    });

    // Salvar documentos aprovados no Storage do Supabase
    await salvarDocumentosAprovados(novosAprovados);

    setDocumentosAprovados(novosAprovados);
    setDocumentosReprovados(novosReprovados);
    setErrosDocumentos(novosErros);

    // Verificar se todos os documentos foram aprovados
    const documentosRequeridos = camposDocumentos.filter(campo => campo.required);
    const todosAprovados = documentosRequeridos.every(campo => novosAprovados[campo.key]);
    
    
    if (todosAprovados) {
      toast({
        title: "✅ Todos os documentos foram aprovados!",
        description: "Redirecionando para exame admissional...",
        variant: "default"
      });
      
      // Atualizar status no Supabase antes de avançar
      try {
        const { error } = await supabase
          .from('evolucao_contratacao')
          .update({ 
            etapa_atual: 'exame_admissional',
            etapa_mais_avancada: 'exame_admissional',
            validacao_status: 'concluido'
          })
          .eq('user_id', user?.id);

        if (error) throw error;

        // Avançar para próxima etapa automaticamente após 2 segundos
        setTimeout(async () => {
          await registrarTransicaoEtapa('exame_admissional', currentEtapa);
          setCurrentEtapa('exame_admissional');
          setEtapaMaisAvancada('exame_admissional');
        }, 2000);
      } catch (error) {
        console.error('Erro ao atualizar status de validação:', error);
        toast({
          title: "Erro",
          description: "Erro ao atualizar status. Tente novamente.",
          variant: "destructive"
        });
      }
    } else {
      // Voltar para a etapa de envio de documentos para correções
      setCurrentEtapa('envio_documentos');
      
      // Mostrar quais documentos ainda precisam ser corrigidos
      const documentosPendentes = documentosRequeridos.filter(campo => 
        !novosAprovados[campo.key]
      );
      
      if (documentosPendentes.length > 0) {
        toast({
          title: "Alguns documentos precisam ser corrigidos",
          description: `Corrija os seguintes documentos: ${documentosPendentes.map(d => d.label).join(', ')}`,
          variant: "destructive"
        });
      }
    }
  };

  const getProgressPercentage = () => {
    const currentIndex = etapas.findIndex(e => e.key === currentEtapa);
    return ((currentIndex + 1) / etapas.length) * 100;
  };

  if (loading) {
    return (
      <ProtectedRoute>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </ProtectedRoute>
    );
  }

  try {
    return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gradient-to-br from-primary/20 via-background to-secondary/20 p-4">
        <div className="max-w-4xl mx-auto">
          {/* Header com botão de logout */}
          <div className="relative">
            {/* Botão Logout - canto superior direito */}
            {user && (
              <div className="absolute top-0 right-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={signOut}
                  className="flex items-center gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  Sair
                </Button>
              </div>
            )}
            
            <div className="text-center mb-6 sm:mb-8">
              <Building2 className="h-12 w-12 sm:h-16 sm:w-16 text-primary mx-auto mb-3 sm:mb-4" />
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2 break-words">
                Processo de Contratação
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground">Grupo Ares</p>
            </div>
          </div>

          {/* Barra de progresso */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Progresso do processo</span>
                  <span>{Math.round(getProgressPercentage())}%</span>
                </div>
                <Progress value={getProgressPercentage()} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <Tabs value={currentEtapa} className="w-full">
                <TabsList className="grid w-full grid-cols-8 p-2 gap-1 text-xs">
                  {etapas.map((etapa) => {
                    const Icon = etapa.icon;
                    const etapaIndex = etapas.findIndex(e => e.key === etapa.key);
                    const etapaMaisAvancadaIndex = etapas.findIndex(e => e.key === etapaMaisAvancada);
                    const isCompleted = etapaMaisAvancadaIndex > etapaIndex;
                    const isCurrent = currentEtapa === etapa.key;
                    const isAccessible = etapaIndex <= etapaMaisAvancadaIndex; // Permite acesso até a etapa mais avançada
                    
                    return (
                      <TabsTrigger 
                        key={etapa.key} 
                        value={etapa.key}
                        disabled={!isAccessible}
                        onClick={() => isAccessible ? navegarParaEtapa(etapa.key) : undefined}
                        className={cn(
                          "flex flex-col items-center gap-1 py-2 px-0.5 cursor-pointer text-xs min-w-0",
                          !isAccessible && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="text-xs text-center leading-tight truncate w-full">{etapa.label}</span>
                  <div className="h-3 w-3">
                    {isCompleted && <CheckCircle className="h-3 w-3 text-green-500" />}
                  </div>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                <div className="p-6">
                  <TabsContent value="proposta" className="space-y-6">
                    <PropostaStatusDisplay 
                      userEmail={user?.email || ''}
                      onAvancar={() => {
                        // Aguardar um pouco para garantir que o banco foi atualizado, então avançar
                        setTimeout(async () => {
                          await registrarTransicaoEtapa('dados_pessoais', currentEtapa);
                          setCurrentEtapa('dados_pessoais');
                          setEtapaMaisAvancada('dados_pessoais');
                        }, 100);
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="dados_pessoais" className="space-y-6">
                    <div className="space-y-6">
                      {/* Indicador de modo visualização */}
                      {isEtapaVisualizacao('dados_pessoais') && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center space-x-2">
                            <div className="text-blue-600">👁️</div>
                            <span className="text-blue-700 font-medium">
                              Visualizando dados já preenchidos (modo somente leitura)
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <h3 className="text-lg font-semibold">Dados Pessoais</h3>
                      
                      {/* Dados básicos */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                           <Label htmlFor="nomeCompleto">Nome Completo *</Label>
                            <Input
                             id="nomeCompleto"
                             value={dadosPessoais.nomeCompleto}
                             onChange={(e) => {
                               // Apenas letras e espaços
                               const value = e.target.value.replace(/[^A-Za-zÀ-ú\s]/g, '');
                               setDadosPessoais({...dadosPessoais, nomeCompleto: value});
                             }}
                             placeholder="Seu nome completo"
                             readOnly={isEtapaVisualizacao('dados_pessoais') || !isPrimeiroEnvio}
                             disabled={!isPrimeiroEnvio}
                             className={cn(
                               errors.nomeCompleto ? 'border-red-500 bg-red-50' : '',
                               dadosPessoais.nomeCompleto && /^[A-Za-zÀ-ú\s]+$/.test(dadosPessoais.nomeCompleto) && dadosPessoais.nomeCompleto.trim().length > 2 ? 'border-green-500 bg-green-50' : '',
                               isEtapaVisualizacao('dados_pessoais') || !isPrimeiroEnvio ? 'bg-gray-100 cursor-not-allowed' : ''
                             )}
                           />
                           {errors.nomeCompleto && <p className="text-red-500 text-xs mt-1">{errors.nomeCompleto}</p>}
                           {dadosPessoais.nomeCompleto && /^[A-Za-zÀ-ú\s]+$/.test(dadosPessoais.nomeCompleto) && dadosPessoais.nomeCompleto.trim().length > 2 && <p className="text-green-600 text-xs mt-1">✓ Nome válido</p>}
                         </div>
                         
                         <div>
                           <Label htmlFor="nomeSocial">Nome Social</Label>
                            <Input
                             id="nomeSocial"
                             value={dadosPessoais.nomeSocial}
                              onChange={(e) => {
                                // Apenas letras e espaços
                                const value = e.target.value.replace(/[^A-Za-zÀ-ú\s]/g, '');
                                setDadosPessoais({...dadosPessoais, nomeSocial: value});
                                // Salvar automaticamente o nome social
                                atualizarNomeSocial(value);
                              }}
                              placeholder="Informe seu nome social (opcional)"
                              readOnly={isEtapaVisualizacao('dados_pessoais')}
                              disabled={isEtapaVisualizacao('dados_pessoais')}
                              className={cn(
                                isEtapaVisualizacao('dados_pessoais') ? 'bg-gray-100 cursor-not-allowed' : ''
                               )}
                            />
                          </div>

                          <div>
                           <Label htmlFor="dataNascimento">Data de Nascimento *</Label>
                          <Input
                            id="dataNascimento"
                            value={dadosPessoais.dataNascimento}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              let formatted = value;
                              if (value.length >= 2) {
                                formatted = value.slice(0, 2) + '/' + value.slice(2);
                              }
                              if (value.length >= 4) {
                                formatted = value.slice(0, 2) + '/' + value.slice(2, 4) + '/' + value.slice(4, 8);
                              }
                              setDadosPessoais({...dadosPessoais, dataNascimento: formatted});
                            }}
                            placeholder="DD/MM/AAAA"
                            maxLength={10}
                            className={cn(
                              (() => {
                                if (!dadosPessoais.dataNascimento) return '';
                                if (!validarDataNascimento(dadosPessoais.dataNascimento)) return 'border-red-500 bg-red-50';
                                const idade = calcularIdade(formatarData(dadosPessoais.dataNascimento));
                                if (idade < 16) return 'border-red-500 bg-red-50';
                                return 'border-green-500 bg-green-50';
                              })()
                            )}
                          />
                          {errors.dataNascimento && <p className="text-red-500 text-xs mt-1">{errors.dataNascimento}</p>}
                           {dadosPessoais.dataNascimento && validarDataNascimento(dadosPessoais.dataNascimento) && (() => {
                             const idade = calcularIdade(formatarData(dadosPessoais.dataNascimento));
                             if (idade >= 16) {
                               return <p className="text-green-600 text-xs mt-1">✓ Idade: {idade} anos</p>;
                             }
                             return null;
                           })()}
                         </div>

                         <div>
                           <Label htmlFor="cpf">CPF *</Label>
                           <Input
                             id="cpf"
                             value={dadosPessoais.cpf}
                             onChange={(e) => {
                               const value = e.target.value.replace(/\D/g, '');
                               if (value.length <= 11) {
                                 setDadosPessoais({...dadosPessoais, cpf: value});
                               }
                             }}
                             placeholder="00000000000"
                             maxLength={11}
                             readOnly={!isPrimeiroEnvio}
                             disabled={!isPrimeiroEnvio}
                             className={cn(
                               errors.cpf ? 'border-red-500' : '',
                               dadosPessoais.cpf && validarCPF(dadosPessoais.cpf) ? 'border-green-500 bg-green-50' : '',
                               dadosPessoais.cpf && dadosPessoais.cpf.length === 11 && !validarCPF(dadosPessoais.cpf) ? 'border-red-500 bg-red-50' : '',
                               !isPrimeiroEnvio ? 'bg-gray-100 cursor-not-allowed' : ''
                             )}
                           />
                          {errors.cpf && <p className="text-red-500 text-xs mt-1">{errors.cpf}</p>}
                          {dadosPessoais.cpf && validarCPF(dadosPessoais.cpf) && <p className="text-green-600 text-xs mt-1">✓ CPF válido</p>}
                        </div>

                        <div>
                          <Label htmlFor="rg">RG *</Label>
                          <Input
                            id="rg"
                            value={dadosPessoais.rg}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^a-zA-Z0-9.-]/g, '').toUpperCase();
                              if (value.length <= 20) {
                                setDadosPessoais({...dadosPessoais, rg: value});
                              }
                            }}
                            placeholder="Ex: 12.345.678-9 ou MG-12345678"
                            maxLength={20}
                            className={cn(
                              errors.rg ? 'border-red-500' : '',
                              dadosPessoais.rg && validarRG(dadosPessoais.rg) ? 'border-green-500 bg-green-50' : '',
                              dadosPessoais.rg && dadosPessoais.rg.length >= 5 && !validarRG(dadosPessoais.rg) ? 'border-red-500 bg-red-50' : ''
                            )}
                          />
                          {errors.rg && <p className="text-red-500 text-xs mt-1">{errors.rg}</p>}
                          {dadosPessoais.rg && validarRG(dadosPessoais.rg) && <p className="text-green-600 text-xs mt-1">✓ RG válido</p>}
                        </div>

                        <div>
                          <Label htmlFor="genero">Identidade de Gênero *</Label>
                          <Select 
                            onValueChange={(value) => {
                              setDadosPessoais({
                                ...dadosPessoais, 
                                genero: value,
                                // Limpar campo personalizado se não for "outro"
                                generoPersonalizado: value === 'outro' ? dadosPessoais.generoPersonalizado : ''
                              });
                            }} 
                            value={dadosPessoais.genero}
                            disabled={isEtapaVisualizacao('dados_pessoais')}
                          >
                            <SelectTrigger className={cn(
                              errors.genero ? 'border-red-500 bg-red-50' : '',
                              dadosPessoais.genero ? 'border-green-500 bg-green-50' : '',
                              isEtapaVisualizacao('dados_pessoais') ? 'bg-gray-100 cursor-not-allowed' : ''
                            )}>
                              <SelectValue placeholder="Selecione sua identidade de gênero" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="masculino">Masculino</SelectItem>
                              <SelectItem value="feminino">Feminino</SelectItem>
                              <SelectItem value="neutro">Neutro</SelectItem>  
                              <SelectItem value="outro">Outro</SelectItem>
                              <SelectItem value="prefiro_nao_responder">Prefiro não responder</SelectItem>
                            </SelectContent>
                          </Select>
                          {errors.genero && <p className="text-red-500 text-xs mt-1">{errors.genero}</p>}
                          {dadosPessoais.genero && <p className="text-green-600 text-xs mt-1">✓ Selecionado</p>}
                        </div>

                        {/* Campo adicional quando "Outro" for selecionado */}
                        {dadosPessoais.genero === 'outro' && (
                          <div>
                            <Label htmlFor="generoPersonalizado">Descreva sua identidade de gênero</Label>
                            <Input
                              id="generoPersonalizado"
                              value={dadosPessoais.generoPersonalizado}
                              onChange={(e) => setDadosPessoais({...dadosPessoais, generoPersonalizado: e.target.value})}
                              placeholder="Descreva como você se identifica"
                              disabled={isEtapaVisualizacao('dados_pessoais')}
                              className={cn(
                                isEtapaVisualizacao('dados_pessoais') ? 'bg-gray-100 cursor-not-allowed' : ''
                              )}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Este campo é opcional e as informações serão tratadas com total confidencialidade.
                            </p>
                          </div>
                        )}

                        <div>
                          <Label htmlFor="corPele">Autodeclaração Étnico Racial *</Label>
                          <Select onValueChange={(value) => setDadosPessoais({...dadosPessoais, corPele: value})} value={dadosPessoais.corPele}>
                            <SelectTrigger className={errors.corPele ? 'border-red-500' : ''}>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="branca">Branca</SelectItem>
                              <SelectItem value="preta">Preta</SelectItem>
                              <SelectItem value="parda">Parda</SelectItem>
                              <SelectItem value="amarela">Amarela</SelectItem>
                              <SelectItem value="indigena">Indígena</SelectItem>
                              <SelectItem value="nao_responder">Prefiro não responder</SelectItem>
                            </SelectContent>
                          </Select>
                          {errors.corPele && <p className="text-red-500 text-xs mt-1">{errors.corPele}</p>}
                        </div>

                        <div>
                          <Label htmlFor="celular">Celular *</Label>
                          <Input
                            id="celular"
                            value={dadosPessoais.celular}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              setDadosPessoais({...dadosPessoais, celular: value});
                            }}
                            placeholder="11999999999"
                            className={cn(
                              errors.celular ? 'border-red-500 bg-red-50' : '',
                              dadosPessoais.celular && dadosPessoais.celular.length >= 10 ? 'border-green-500 bg-green-50' : ''
                            )}
                          />
                          {errors.celular && <p className="text-red-500 text-xs mt-1">{errors.celular}</p>}
                          {dadosPessoais.celular && dadosPessoais.celular.length >= 10 && <p className="text-green-600 text-xs mt-1">✓ Número válido</p>}
                        </div>

                        <div>
                          <Label htmlFor="estadoCivil">Estado Civil *</Label>
                          <Select onValueChange={(value) => setDadosPessoais({...dadosPessoais, estadoCivil: value})} value={dadosPessoais.estadoCivil}>
                            <SelectTrigger className={cn(
                              errors.estadoCivil ? 'border-red-500 bg-red-50' : '',
                              dadosPessoais.estadoCivil ? 'border-green-500 bg-green-50' : ''
                            )}>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="solteiro">Solteiro(a)</SelectItem>
                              <SelectItem value="casado">Casado(a)</SelectItem>
                              <SelectItem value="divorciado">Divorciado(a)</SelectItem>
                              <SelectItem value="viuvo">Viúvo(a)</SelectItem>
                              <SelectItem value="uniao_estavel">União Estável</SelectItem>
                            </SelectContent>
                          </Select>
                          {errors.estadoCivil && <p className="text-red-500 text-xs mt-1">{errors.estadoCivil}</p>}
                          {dadosPessoais.estadoCivil && <p className="text-green-600 text-xs mt-1">✓ Selecionado</p>}
                        </div>

                        <div>
                          <Label htmlFor="nacionalidade">Nacionalidade *</Label>
                          <Select value={dadosPessoais.nacionalidade} onValueChange={(value) => setDadosPessoais({...dadosPessoais, nacionalidade: value})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione sua nacionalidade">
                                {dadosPessoais.nacionalidade && (
                                  <div className="flex items-center gap-2">
                                    <span>{nacionalidades.find(n => n.value === dadosPessoais.nacionalidade)?.flag}</span>
                                    <span>{dadosPessoais.nacionalidade}</span>
                                  </div>
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {nacionalidades.map((nacionalidade) => (
                                <SelectItem key={nacionalidade.value} value={nacionalidade.value}>
                                  <div className="flex items-center gap-2">
                                    <span>{nacionalidade.flag}</span>
                                    <span>{nacionalidade.label}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors.nacionalidade && <p className="text-red-500 text-xs mt-1">{errors.nacionalidade}</p>}
                          {dadosPessoais.nacionalidade && <p className="text-green-600 text-xs mt-1">✓ Selecionado</p>}
                        </div>
                      </div>

                      {/* Seção de Acessibilidade */}
                      <div className="mt-8 pt-6 border-t border-muted">
                        <div className="mb-4">
                          <h3 className="text-lg font-semibold text-foreground mb-2">Informações de Acessibilidade</h3>
                          <p className="text-sm text-muted-foreground">
                            Campos opcionais para garantir um ambiente de trabalho inclusivo e acessível.
                          </p>
                        </div>
                        
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="possuiDeficiencia">Possui alguma deficiência? *</Label>
                            <Select
                              value={dadosDeficiencia.possui_deficiencia ? dadosDeficiencia.tipo_deficiencia : 'nao'}
                              onValueChange={(value) => {
                                if (value === 'nao') {
                                  setDadosDeficiencia({
                                    possui_deficiencia: false,
                                    tipo_deficiencia: '',
                                    categoria_deficiencia: '',
                                    detalhes_deficiencia: ''
                                  });
                                } else {
                                  setDadosDeficiencia({
                                    ...dadosDeficiencia,
                                    possui_deficiencia: true,
                                    tipo_deficiencia: value,
                                    categoria_deficiencia: '',
                                    detalhes_deficiencia: ''
                                  });
                                }
                              }}
                              disabled={isEtapaVisualizacao('dados_pessoais')}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione uma opção" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="nao">Não</SelectItem>
                                <SelectItem value="fisica">Física</SelectItem>
                                <SelectItem value="mental">Mental</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Campo condicional para tipo de deficiência física */}
                          {dadosDeficiencia.possui_deficiencia && dadosDeficiencia.tipo_deficiencia === 'fisica' && (
                            <div>
                              <Label htmlFor="categoriaDeficienciaFisica">Tipo de deficiência física *</Label>
                              <Select
                                value={dadosDeficiencia.categoria_deficiencia}
                                onValueChange={(value) => {
                                  setDadosDeficiencia({
                                    ...dadosDeficiencia,
                                    categoria_deficiencia: value
                                  });
                                }}
                                disabled={isEtapaVisualizacao('dados_pessoais')}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione o tipo" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="mobilidade_reduzida">Mobilidade reduzida</SelectItem>
                                  <SelectItem value="amputacao">Amputação</SelectItem>
                                  <SelectItem value="paraplegia_tetraplegia">Paraplegia/Tetraplegia</SelectItem>
                                  <SelectItem value="sequelas_neurologicas">Sequelas neurológicas</SelectItem>
                                  <SelectItem value="osteomuscular">Osteomuscular</SelectItem>
                                  <SelectItem value="nanismo">Nanismo</SelectItem>
                                  <SelectItem value="outra">Outra</SelectItem>
                                </SelectContent>
                              </Select>
                              {errors.categoria_deficiencia && <p className="text-red-500 text-xs mt-1">{errors.categoria_deficiencia}</p>}
                            </div>
                          )}

                          {/* Campo condicional para tipo de deficiência mental */}
                          {dadosDeficiencia.possui_deficiencia && dadosDeficiencia.tipo_deficiencia === 'mental' && (
                            <div>
                              <Label htmlFor="categoriaDeficienciaMental">Tipo de deficiência mental/intelectual *</Label>
                              <Select
                                value={dadosDeficiencia.categoria_deficiencia}
                                onValueChange={(value) => {
                                  setDadosDeficiencia({
                                    ...dadosDeficiencia,
                                    categoria_deficiencia: value
                                  });
                                }}
                                disabled={isEtapaVisualizacao('dados_pessoais')}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione o tipo" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="intelectual">Intelectual</SelectItem>
                                  <SelectItem value="tea">Transtorno do espectro autista (TEA)</SelectItem>
                                  <SelectItem value="transtorno_psicossocial">Transtorno psicossocial</SelectItem>
                                  <SelectItem value="tdah">TDAH</SelectItem>
                                  <SelectItem value="outra">Outra</SelectItem>
                                </SelectContent>
                              </Select>
                              {errors.categoria_deficiencia && <p className="text-red-500 text-xs mt-1">{errors.categoria_deficiencia}</p>}
                            </div>
                          )}

                          {/* Campo de detalhes (opcional) quando há deficiência */}
                          {dadosDeficiencia.possui_deficiencia && (dadosDeficiencia.tipo_deficiencia === 'fisica' || dadosDeficiencia.tipo_deficiencia === 'mental') && (
                            <div>
                              <Label htmlFor="detalhesDeficiencia">Detalhes da deficiência (opcional)</Label>
                              <Textarea
                                id="detalhesDeficiencia"
                                value={dadosDeficiencia.detalhes_deficiencia}
                                onChange={(e) => {
                                  setDadosDeficiencia({
                                    ...dadosDeficiencia,
                                    detalhes_deficiencia: e.target.value
                                  });
                                }}
                                placeholder="Descreva detalhes adicionais sobre a deficiência, se necessário..."
                                className="min-h-[80px]"
                                disabled={isEtapaVisualizacao('dados_pessoais')}
                              />
                            </div>
                          )}

                          {/* Campo de Nível de Escolaridade */}
                          <div>
                            <Label htmlFor="nivelEscolaridade">Nível de Escolaridade</Label>
                            <Select
                              value={dadosPessoais.nivelEscolaridade || ''}
                              onValueChange={(value) => setDadosPessoais({...dadosPessoais, nivelEscolaridade: value})}
                              disabled={isEtapaVisualizacao('dados_pessoais')}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione o nível de escolaridade" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="ensino_superior_completo">Ensino Superior Completo</SelectItem>
                                <SelectItem value="ensino_superior_cursando">Ensino Superior Cursando</SelectItem>
                                <SelectItem value="ensino_medio_completo">Ensino Médio Completo</SelectItem>
                                <SelectItem value="ensino_medio_incompleto">Ensino Médio Incompleto</SelectItem>
                                <SelectItem value="ensino_fundamental_completo">Ensino Fundamental Completo</SelectItem>
                                <SelectItem value="ensino_fundamental_incompleto">Ensino Fundamental Incompleto</SelectItem>
                                <SelectItem value="analfabeto">Analfabeto</SelectItem>
                              </SelectContent>
                            </Select>
                            {dadosPessoais.nivelEscolaridade && <p className="text-green-600 text-xs mt-1">✓ Selecionado</p>}
                          </div>
                        </div>
                      </div>

                      {/* PIS, Título de Eleitor e Cor da Pele */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="numeroPIS">Número do PIS *</Label>
                          <Input
                            id="numeroPIS"
                            value={dadosPessoais.numeroPIS}
                            onChange={(e) => {
                              // Apenas números, máximo 11 dígitos
                              const value = e.target.value.replace(/\D/g, '');
                              if (value.length <= 11) {
                                setDadosPessoais({...dadosPessoais, numeroPIS: value});
                              }
                            }}
                            placeholder="00000000000"
                            maxLength={11}
                            className={cn(
                              (() => {
                                if (!dadosPessoais.numeroPIS) return '';
                                const pisNumeros = dadosPessoais.numeroPIS.replace(/\D/g, '');
                                if (pisNumeros.length !== 11) return 'border-red-500 bg-red-50';
                                if (!validarPIS(pisNumeros)) return 'border-red-500 bg-red-50';
                                return 'border-green-500 bg-green-50';
                              })()
                            )}
                          />
                          {errors.numeroPIS && <p className="text-red-500 text-xs mt-1">{errors.numeroPIS}</p>}
                          {dadosPessoais.numeroPIS && (() => {
                            const pisNumeros = dadosPessoais.numeroPIS.replace(/\D/g, '');
                            if (pisNumeros.length === 11 && validarPIS(pisNumeros)) {
                              return <p className="text-green-600 text-xs mt-1">✓ PIS válido</p>;
                            }
                            return null;
                          })()}
                         </div>

                         <div>
                          <Label htmlFor="tituloEleitor">Número de Inscrição do Título de Eleitor *</Label>
                          <Input
                            id="tituloEleitor"
                            value={dadosPessoais.tituloEleitor}
                            onChange={(e) => {
                              // Apenas números, máximo 12 dígitos
                              const value = e.target.value.replace(/\D/g, '');
                              if (value.length <= 12) {
                                setDadosPessoais({...dadosPessoais, tituloEleitor: value});
                              }
                            }}
                            placeholder="000000000000"
                            maxLength={12}
                            className={cn(
                              (() => {
                                if (!dadosPessoais.tituloEleitor) return '';
                                if (!validarTituloEleitor(dadosPessoais.tituloEleitor)) return 'border-red-500 bg-red-50';
                                return 'border-green-500 bg-green-50';
                              })()
                            )}
                          />
                          {errors.tituloEleitor && <p className="text-red-500 text-xs mt-1">{errors.tituloEleitor}</p>}
                          {dadosPessoais.tituloEleitor && validarTituloEleitor(dadosPessoais.tituloEleitor) && (
                            <p className="text-green-600 text-xs mt-1">✓ Título de eleitor válido</p>
                          )}
                         </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="carteiraTrabalho">Número da Carteira de Trabalho *</Label>
                          <Input
                            id="carteiraTrabalho"
                            value={dadosPessoais.carteiraTrabalho}
                            onChange={(e) => {
                              // Apenas números, máximo 11 dígitos
                              const value = e.target.value.replace(/\D/g, '');
                              if (value.length <= 11) {
                                setDadosPessoais({...dadosPessoais, carteiraTrabalho: value});
                              }
                            }}
                            placeholder="1234567 ou 00000000000"
                            maxLength={11}
                            className={cn(
                              (() => {
                                if (!dadosPessoais.carteiraTrabalho) return '';
                                if (!validarCarteiraTrabalho(dadosPessoais.carteiraTrabalho)) return 'border-red-500 bg-red-50';
                                return 'border-green-500 bg-green-50';
                              })()
                            )}
                          />
                          {errors.carteiraTrabalho && <p className="text-red-500 text-xs mt-1">{errors.carteiraTrabalho}</p>}
                          {dadosPessoais.carteiraTrabalho && validarCarteiraTrabalho(dadosPessoais.carteiraTrabalho) && (
                            <p className="text-green-600 text-xs mt-1">✓ Carteira de trabalho válida</p>
                          )}
                        </div>

                        <div></div>
                      </div>


                      {/* Endereço */}
                      <div className="space-y-4">
                        <h4 className="font-medium">Endereço Completo</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="cep">CEP *</Label>
                            <Input
                              id="cep"
                              value={dadosPessoais.cep}
                              onChange={(e) => handleCEPChange(e.target.value)}
                              placeholder="00000-000"
                              className={errors.cep ? 'border-red-500' : ''}
                            />
                            {errors.cep && <p className="text-red-500 text-xs mt-1">{errors.cep}</p>}
                          </div>
                          <div className="md:col-span-2">
                            <Label htmlFor="logradouro">Logradouro *</Label>
                            <Input
                              id="logradouro"
                              value={dadosPessoais.logradouro}
                              onChange={(e) => setDadosPessoais({...dadosPessoais, logradouro: e.target.value})}
                              placeholder="Rua, Avenida, etc."
                              className={errors.logradouro ? 'border-red-500' : ''}
                            />
                            {errors.logradouro && <p className="text-red-500 text-xs mt-1">{errors.logradouro}</p>}
                          </div>
                          <div>
                            <Label htmlFor="numero">Número *</Label>
                            <Input
                              id="numero"
                              value={dadosPessoais.numero}
                              onChange={(e) => setDadosPessoais({...dadosPessoais, numero: e.target.value})}
                              placeholder="123"
                              className={errors.numero ? 'border-red-500' : ''}
                            />
                            {errors.numero && <p className="text-red-500 text-xs mt-1">{errors.numero}</p>}
                          </div>
                          <div>
                            <Label htmlFor="complemento">Complemento</Label>
                            <Input
                              id="complemento"
                              value={dadosPessoais.complemento}
                              onChange={(e) => setDadosPessoais({...dadosPessoais, complemento: e.target.value})}
                              placeholder="Apto, Bloco, etc."
                            />
                          </div>
                          <div>
                            <Label htmlFor="bairro">Bairro *</Label>
                            <Input
                              id="bairro"
                              value={dadosPessoais.bairro}
                              onChange={(e) => setDadosPessoais({...dadosPessoais, bairro: e.target.value})}
                              placeholder="Nome do bairro"
                              className={errors.bairro ? 'border-red-500' : ''}
                            />
                            {errors.bairro && <p className="text-red-500 text-xs mt-1">{errors.bairro}</p>}
                          </div>
                          <div>
                            <Label htmlFor="cidade">Cidade *</Label>
                            <Input
                              id="cidade"
                              value={dadosPessoais.cidade}
                              onChange={(e) => setDadosPessoais({...dadosPessoais, cidade: e.target.value})}
                              placeholder="Nome da cidade"
                              className={errors.cidade ? 'border-red-500' : ''}
                            />
                            {errors.cidade && <p className="text-red-500 text-xs mt-1">{errors.cidade}</p>}
                          </div>
                          <div>
                            <Label htmlFor="estado">Estado (UF) *</Label>
                            <Input
                              id="estado"
                              value={dadosPessoais.estado}
                              onChange={(e) => setDadosPessoais({...dadosPessoais, estado: e.target.value.toUpperCase()})}
                              placeholder="SP"
                              maxLength={2}
                              className={errors.estado ? 'border-red-500' : ''}
                            />
                            {errors.estado && <p className="text-red-500 text-xs mt-1">{errors.estado}</p>}
                          </div>
                        </div>
                      </div>

                      {/* Dados Bancários */}
                      <div className="space-y-4">
                        <h4 className="font-medium">Dados Bancários</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="tipoChavePix">Tipo de Chave PIX *</Label>
                            <Select
                              value={dadosPessoais.tipoChavePix}
                              onValueChange={(value) => setDadosPessoais({...dadosPessoais, tipoChavePix: value})}
                              disabled={isEtapaVisualizacao('dados_pessoais')}
                            >
                              <SelectTrigger className={errors.tipoChavePix ? 'border-red-500' : ''}>
                                <SelectValue placeholder="Selecione o tipo de chave PIX" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cpf">CPF</SelectItem>
                                <SelectItem value="celular">Celular</SelectItem>
                                <SelectItem value="email">E-mail</SelectItem>
                                <SelectItem value="chave-aleatoria">Chave Aleatória</SelectItem>
                              </SelectContent>
                            </Select>
                            {errors.tipoChavePix && <p className="text-red-500 text-xs mt-1">{errors.tipoChavePix}</p>}
                          </div>
                          <div>
                            <Label htmlFor="chavePix">Chave PIX *</Label>
                            <Input
                              id="chavePix"
                              value={dadosPessoais.chavePix}
                              onChange={(e) => setDadosPessoais({...dadosPessoais, chavePix: e.target.value})}
                              placeholder={
                                dadosPessoais.tipoChavePix === 'cpf' ? 'Digite seu CPF' :
                                dadosPessoais.tipoChavePix === 'celular' ? 'Digite seu celular' :
                                dadosPessoais.tipoChavePix === 'email' ? 'Digite seu e-mail' :
                                dadosPessoais.tipoChavePix === 'chave-aleatoria' ? 'Cole sua chave aleatória' :
                                'CPF, email, telefone ou chave aleatória'
                              }
                              className={errors.chavePix ? 'border-red-500' : ''}
                              disabled={isEtapaVisualizacao('dados_pessoais')}
                            />
                            {errors.chavePix && <p className="text-red-500 text-xs mt-1">{errors.chavePix}</p>}
                          </div>
                          <div>
                            <Label htmlFor="banco">Banco *</Label>
                            <Input
                              id="banco"
                              value={dadosPessoais.banco}
                              onChange={(e) => setDadosPessoais({...dadosPessoais, banco: e.target.value})}
                              placeholder="Nome do banco"
                              className={errors.banco ? 'border-red-500' : ''}
                            />
                            {errors.banco && <p className="text-red-500 text-xs mt-1">{errors.banco}</p>}
                          </div>
                          <div>
                            <Label htmlFor="agencia">Agência *</Label>
                            <Input
                              id="agencia"
                              value={dadosPessoais.agencia}
                              onChange={(e) => setDadosPessoais({...dadosPessoais, agencia: e.target.value.replace(/\D/g, '')})}
                              placeholder="1234"
                              maxLength={4}
                              className={errors.agencia ? 'border-red-500' : ''}
                            />
                            {errors.agencia && <p className="text-red-500 text-xs mt-1">{errors.agencia}</p>}
                          </div>
                          <div>
                            <Label htmlFor="conta">Conta *</Label>
                            <Input
                              id="conta"
                              value={dadosPessoais.conta}
                              onChange={(e) => setDadosPessoais({...dadosPessoais, conta: e.target.value.replace(/\D/g, '')})}
                              placeholder="12345678"
                              maxLength={10}
                              className={errors.conta ? 'border-red-500' : ''}
                            />
                            {errors.conta && <p className="text-red-500 text-xs mt-1">{errors.conta}</p>}
                          </div>
                          <div className="md:col-span-2">
                            <Label htmlFor="tipoConta">Tipo de Conta *</Label>
                            <Select onValueChange={(value) => setDadosPessoais({...dadosPessoais, tipoConta: value})} value={dadosPessoais.tipoConta}>
                              <SelectTrigger className={errors.tipoConta ? 'border-red-500' : ''}>
                                <SelectValue placeholder="Selecione o tipo de conta" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="conta_corrente">Conta Corrente</SelectItem>
                                <SelectItem value="conta_poupanca">Conta Poupança</SelectItem>
                                <SelectItem value="conta_pagamento">Conta Pagamento</SelectItem>
                                <SelectItem value="conta_salario">Conta Salário</SelectItem>
                              </SelectContent>
                            </Select>
                            {errors.tipoConta && <p className="text-red-500 text-xs mt-1">{errors.tipoConta}</p>}
                          </div>
                        </div>
                      </div>


                      <div className="flex justify-center">
                        <Button onClick={enviarDados} size="lg" className="px-8">
                          Enviar Dados
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="envio_documentos" className="space-y-6">
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold">Envio de Documentos</h3>
                      
                      {/* Aviso para usuários com mais de 3 tentativas */}
                      {mostrarAvisoTentativas && (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center gap-2 text-yellow-800">
                            <div className="flex-shrink-0">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div>
                              <h4 className="font-medium">Atenção</h4>
                              <p className="text-sm mt-1">
                                O RH foi sinalizado sobre problemas no envio de seus documentos. Aguarde ou continue tentando enviar fotos de seus documentos com melhor qualidade e clareza.
                              </p>
                              <p className="text-xs mt-2 text-yellow-600">
                                Tentativas realizadas: {tentativasDocumentos}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Campos de documentos principais em duas colunas */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {camposDocumentos.map((campo, index) => (
                          <div key={campo.key} className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Label htmlFor={campo.key} className="font-medium">
                                {campo.label} {campo.required && '*'}
                              </Label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 rounded-full"
                                onClick={() => setShowInstructions(campo.key)}
                              >
                                <HelpCircle className="h-4 w-4" />
                              </Button>
                            </div>
                            
                            <div className="space-y-2">
                              <input
                                id={campo.key}
                                type="file"
                                accept={campo.onlyImages ? ".jpg,.jpeg,.png" : ".jpg,.jpeg,.png,.pdf"}
                                onChange={(e) => handleFileUpload(campo.key, e.target.files?.[0] || null)}
                                disabled={documentosAprovados[campo.key] || (campo.key === 'certificado_reservista' && dadosPessoais.genero === 'feminino')}
                                className={cn(
                                  "w-full p-2 border rounded-md text-sm",
                                  documentosAprovados[campo.key] ? "bg-green-50 border-green-300" : 
                                  documentosReprovados[campo.key] ? "bg-red-50 border-red-300" : 
                                  (campo.key === 'certificado_reservista' && dadosPessoais.genero === 'feminino') ? "bg-gray-100 border-gray-300 cursor-not-allowed" : "border-gray-300",
                                  errosDocumentos[campo.key] ? "border-red-500" : ""
                                )}
                              />
                              
                              {/* Avisos sobre tipos de arquivo */}
                              {campo.onlyImages && (
                                <p className="text-xs text-blue-600 mt-1">
                                  📷 Apenas imagens (JPG, JPEG, PNG)
                                </p>
                              )}
                              {campo.key === 'certificado_reservista' && dadosPessoais.genero === 'feminino' && (
                                <p className="text-xs text-gray-500 mt-1">
                                  ℹ️ Campo desabilitado para pessoas do gênero feminino
                                </p>
                              )}
                              
                              {/* Status do documento com botões de ação */}
                              {documentosAprovados[campo.key] && (
                                <div className="space-y-2 mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                                  <div className="flex items-center gap-1 text-green-700 text-sm font-medium">
                                    <CheckCircle2 className="h-4 w-4" />
                                    <span>✅ Documento APROVADO</span>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => visualizarDocumento(campo.key)}
                                      disabled={loadingDocumento}
                                      className="flex items-center gap-1"
                                    >
                                      <Eye className="h-3 w-3" />
                                      Ver
                                    </Button>
                                     <Button
                                       type="button"
                                       variant="outline"
                                       size="sm"
                                       onClick={() => permitirSubstituicao(campo.key)}
                                       className="flex items-center gap-1 flex-shrink-0"
                                     >
                                       <RotateCcw className="h-3 w-3" />
                                       <span className="hidden sm:inline">Substituir</span>
                                     </Button>
                                  </div>
                                </div>
                              )}
                              
                              {documentosReprovados[campo.key] && !documentosAprovados[campo.key] && (
                                <div className="space-y-2 mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                  <div className="flex items-center gap-1 text-red-700 text-sm font-medium">
                                    <XCircle className="h-4 w-4" />
                                    <span>❌ Documento REPROVADO - Selecione um novo arquivo</span>
                                  </div>
                                  {errosDocumentos[campo.key] && (
                                    <p className="text-red-600 text-xs bg-white p-2 rounded border">
                                      <strong>Motivo:</strong> {errosDocumentos[campo.key]}
                                    </p>
                                  )}
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => visualizarDocumento(campo.key)}
                                    disabled={loadingDocumento}
                                    className="flex items-center gap-1"
                                  >
                                    <Eye className="h-3 w-3" />
                                    Ver documento anterior
                                  </Button>
                                </div>
                              )}
                              
                              {errosDocumentos[campo.key] && (
                                <p className="text-red-500 text-xs">{errosDocumentos[campo.key]}</p>
                              )}
                              
                              {documentos[campo.key] && !documentosAprovados[campo.key] && (
                                <p className="text-blue-600 text-xs">
                                  Arquivo selecionado: {documentos[campo.key]?.name}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Seção de dependentes */}
                      <div className="space-y-4 border-t pt-6">
                        <h4 className="font-medium">Dependentes</h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="possui_dependentes">Possui dependentes?</Label>
                            <Select 
                              onValueChange={(value) => setDependentes({...dependentes, quantidade: parseInt(value)})} 
                              value={dependentes.quantidade.toString()}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Não" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">Não</SelectItem>
                                <SelectItem value="1">1 dependente</SelectItem>
                                <SelectItem value="2">2 dependentes</SelectItem>
                                <SelectItem value="3">3 dependentes</SelectItem>
                                <SelectItem value="4">4 dependentes</SelectItem>
                                <SelectItem value="5">5 dependentes</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                        </div>

                        {/* Campos de documentos dos dependentes */}
                        {dependentes.quantidade > 0 && (
                          <div className="space-y-4">
                            <h5 className="font-medium text-sm">Documentos dos Dependentes</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {Array.from({ length: dependentes.quantidade }, (_, i) => i + 1).map(num => (
                                 <div key={num} className="space-y-4 p-4 border rounded-lg">
                                   <h6 className="font-medium text-sm">Dependente {num}</h6>
                                   
                                   {/* Pergunta sobre idade individual */}
                                   <div>
                                     <Label htmlFor={`dependente_${num}_idade`}>Idade do dependente</Label>
                                     <Select 
                                        onValueChange={(value) => setDependentes({
                                          ...dependentes, 
                                          idades: {...dependentes.idades, [num]: parseInt(value)}
                                        })}
                                       value={dependentes.idades[num]?.toString() || ''}
                                     >
                                       <SelectTrigger>
                                          <SelectValue placeholder="Selecione a idade" />
                                       </SelectTrigger>
                                        <SelectContent>
                                          {Array.from({ length: 15 }, (_, i) => i).map(idade => (
                                            <SelectItem key={idade} value={idade.toString()}>
                                              {idade} {idade === 1 ? 'ano' : 'anos'}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                     </Select>
                                   </div>
                                   
                                     {/* Opção de documento do dependente */}
                                     <div className="space-y-4">
                                       <div>
                                         <Label className="text-sm font-medium">Documento do Dependente *</Label>
                                         <p className="text-xs text-muted-foreground mb-3">
                                           Escolha uma das opções abaixo para enviar o documento do dependente:
                                         </p>
                                         <div className="space-y-4">
                                            {/* Campos fixos sempre visíveis */}
                                            <div className="space-y-4">
                                              <div>
                                                <Label htmlFor={`dependente_${num}_nome`}>Nome do Dependente *</Label>
                                                <input
                                                  id={`dependente_${num}_nome`}
                                                  type="text"
                                                  placeholder="Digite o nome completo do dependente"
                                                  value={dependentesNomes[`dependente_${num}`] || ''}
                                                  onChange={(e) => setDependentesNomes(prev => ({
                                                    ...prev,
                                                    [`dependente_${num}`]: e.target.value
                                                  }))}
                                                  className="w-full p-2 border rounded-md text-sm"
                                                />
                                              </div>

                                              <div>
                                                <Label htmlFor={`dependente_${num}_cpf`}>CPF do Dependente *</Label>
                                                <input
                                                  id={`dependente_${num}_cpf`}
                                                  type="text"
                                                  placeholder="Digite apenas números"
                                                  value={dependentesCpf[`dependente_${num}`] || ''}
                                                  onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '');
                                                    if (value.length <= 11) {
                                                      setDependentesCpf(prev => ({
                                                        ...prev,
                                                        [`dependente_${num}`]: value
                                                      }));
                                                    }
                                                  }}
                                                  maxLength={11}
                                                  className="w-full p-2 border rounded-md text-sm"
                                                />
                                              </div>

                                              <div>
                                                <Label htmlFor={`dependente_${num}_tipo`}>Tipo de Dependente *</Label>
                                                <Select
                                                  value={dependentesTipo[`dependente_${num}`] || ''}
                                                  onValueChange={(value) => setDependentesTipo(prev => ({
                                                    ...prev,
                                                    [`dependente_${num}`]: value
                                                  }))}
                                                >
                                                  <SelectTrigger>
                                                    <SelectValue placeholder="Selecione o tipo de dependente" />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    <SelectItem value="conjuge">Cônjuge ou companheiro(a)</SelectItem>
                                                    <SelectItem value="filhos">Filhos e enteados (até 21 anos ou até 24 se estudando)</SelectItem>
                                                    <SelectItem value="pais">Pais, avós e bisavós (com renda limitada)</SelectItem>
                                                    <SelectItem value="irmaos">Irmãos, netos, bisnetos (com guarda judicial)</SelectItem>
                                                    <SelectItem value="menor_guarda">Menor pobre sob guarda</SelectItem>
                                                    <SelectItem value="incapaz">Pessoa incapaz sob tutela ou curatela</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </div>
                                            </div>

                                            {/* Radio buttons para escolher o tipo de documento */}
                                            <div className="flex justify-evenly items-center">
                                              <label className="flex items-center space-x-2 cursor-pointer">
                                                <input
                                                  type="radio"
                                                  name={`dependente_${num}_doc_tipo`}
                                                  value="rg"
                                                  checked={dependentesDocTipo[`dependente_${num}`] === 'rg' || !dependentesDocTipo[`dependente_${num}`]}
                                                   onChange={(e) => {
                                                     setDependentesDocTipo(prev => ({
                                                       ...prev,
                                                       [`dependente_${num}`]: e.target.value
                                                     }));
                                                     // Limpar arquivos da opção não selecionada
                                                     setDependentesDocumentos(prev => ({
                                                       ...prev,
                                                       [`dependente_${num}_certidao_nascimento`]: null,
                                                       [`dependente_${num}_comprovante_invalidez`]: null
                                                     }));
                                                   }}
                                                  className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                />
                                                <span className="text-sm">RG</span>
                                              </label>
                                              <label className="flex items-center space-x-2 cursor-pointer">
                                                <input
                                                  type="radio"
                                                  name={`dependente_${num}_doc_tipo`}
                                                  value="certidao"
                                                  checked={dependentesDocTipo[`dependente_${num}`] === 'certidao'}
                                                   onChange={(e) => {
                                                     setDependentesDocTipo(prev => ({
                                                       ...prev,
                                                       [`dependente_${num}`]: e.target.value
                                                     }));
                                                      // Limpar arquivos da opção não selecionada
                                                      setDependentesDocumentos(prev => ({
                                                        ...prev,
                                                        [`dependente_${num}_rg`]: null,
                                                        [`dependente_${num}_comprovante_invalidez`]: null
                                                      }));
                                                   }}
                                                  className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                />
                                                <span className="text-sm">Certidão de Nascimento</span>
                                              </label>
                                              <label className="flex items-center space-x-2 cursor-pointer">
                                                <input
                                                  type="radio"
                                                  name={`dependente_${num}_doc_tipo`}
                                                  value="comprovante_invalidez"
                                                  checked={dependentesDocTipo[`dependente_${num}`] === 'comprovante_invalidez'}
                                                   onChange={(e) => {
                                                     setDependentesDocTipo(prev => ({
                                                       ...prev,
                                                       [`dependente_${num}`]: e.target.value
                                                     }));
                                                      // Limpar arquivos da opção não selecionada
                                                      setDependentesDocumentos(prev => ({
                                                        ...prev,
                                                        [`dependente_${num}_rg`]: null,
                                                        [`dependente_${num}_certidao_nascimento`]: null
                                                      }));
                                                   }}
                                                  className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                                />
                                                <span className="text-sm">Comprovante de Invalidez</span>
                                              </label>
                                            </div>

                                            {/* Campos condicionais baseados na seleção */}
                                            {(!dependentesDocTipo[`dependente_${num}`] || dependentesDocTipo[`dependente_${num}`] === 'rg') && (
                                              <div>
                                                <Label htmlFor={`dependente_${num}_rg`}>RG *</Label>
                                                <input
                                                  id={`dependente_${num}_rg`}
                                                  type="file"
                                                  accept=".jpg,.jpeg,.png,.pdf"
                                                  onChange={(e) => handleDependenteFileUpload(`dependente_${num}_rg`, e.target.files?.[0] || null)}
                                                  className="w-full p-2 border rounded-md text-sm"
                                                />
                                              </div>
                                            )}

                                            {dependentesDocTipo[`dependente_${num}`] === 'certidao' && (
                                              <div>
                                                <Label htmlFor={`dependente_${num}_certidao_nascimento`}>Certidão de Nascimento *</Label>
                                                <input
                                                  id={`dependente_${num}_certidao_nascimento`}
                                                  type="file"
                                                  accept=".jpg,.jpeg,.png,.pdf"
                                                  onChange={(e) => handleDependenteFileUpload(`dependente_${num}_certidao_nascimento`, e.target.files?.[0] || null)}
                                                  className="w-full p-2 border rounded-md text-sm"
                                                />
                                              </div>
                                            )}

                                            {dependentesDocTipo[`dependente_${num}`] === 'comprovante_invalidez' && (
                                              <div>
                                                <Label htmlFor={`dependente_${num}_comprovante_invalidez`}>Comprovante de Invalidez *</Label>
                                                <input
                                                  id={`dependente_${num}_comprovante_invalidez`}
                                                  type="file"
                                                  accept=".jpg,.jpeg,.png,.pdf"
                                                  onChange={(e) => handleDependenteFileUpload(`dependente_${num}_comprovante_invalidez`, e.target.files?.[0] || null)}
                                                  className="w-full p-2 border rounded-md text-sm"
                                                />
                                              </div>
                                            )}
                                         </div>
                                       </div>
                                     </div>
                                   
                                    {/* Campo condicional baseado na idade individual */}
                                    {dependentes.idades[num] >= 7 && dependentes.idades[num] <= 14 ? (
                                      <div>
                                        <Label htmlFor={`dependente_${num}_comprovante_escolaridade`}>Comprovante de Escolaridade *</Label>
                                        <input
                                          id={`dependente_${num}_comprovante_escolaridade`}
                                          type="file"
                                          accept=".jpg,.jpeg,.png,.pdf"
                                          onChange={(e) => handleDependenteFileUpload(`dependente_${num}_comprovante_escolaridade`, e.target.files?.[0] || null)}
                                          className="w-full p-2 border rounded-md text-sm"
                                        />
                                      </div>
                                     ) : dependentes.idades[num] <= 6 ? (
                                       <div>
                                         <Label htmlFor={`dependente_${num}_comprovante_vacinacao`}>Comprovante de Vacinação *</Label>
                                         <input
                                           id={`dependente_${num}_comprovante_vacinacao`}
                                           type="file"
                                           accept=".jpg,.jpeg,.png,.pdf"
                                           onChange={(e) => handleDependenteFileUpload(`dependente_${num}_comprovante_vacinacao`, e.target.files?.[0] || null)}
                                           className="w-full p-2 border rounded-md text-sm"
                                         />
                                       </div>
                                     ) : null}

                                     {/* Status dos documentos do dependente */}
                                     <div className="mt-4 space-y-2">
                                        {/* Verificar status dos documentos RG */}
                                        {(!dependentesDocTipo[`dependente_${num}`] || dependentesDocTipo[`dependente_${num}`] === 'rg') && (
                                          <>
                                            {dependentesDocumentosAprovados[`dependente_${num}_rg`] && (
                                              <div className="p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                                                ✅ RG - APROVADO
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => visualizarDocumento(`dependente_${num}_rg`)}
                                                  disabled={loadingDocumento}
                                                  className="ml-2 h-6 px-2 text-xs"
                                                >
                                                  <Eye className="h-3 w-3" />
                                                </Button>
                                              </div>
                                            )}
                                            {dependentesDocumentosReprovados[`dependente_${num}_rg`] && (
                                              <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                                                ❌ RG - REPROVADO - Selecione um novo arquivo
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => visualizarDocumento(`dependente_${num}_rg`)}
                                                  disabled={loadingDocumento}
                                                  className="ml-2 h-6 px-2 text-xs"
                                                >
                                                  <Eye className="h-3 w-3" />
                                                </Button>
                                              </div>
                                            )}
                                          </>
                                        )}

                                        {/* Verificar status da certidão de nascimento */}
                                        {dependentesDocTipo[`dependente_${num}`] === 'certidao' && (
                                          <>
                                            {dependentesDocumentosAprovados[`dependente_${num}_certidao_nascimento`] && (
                                              <div className="p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                                                ✅ Certidão de Nascimento - APROVADO
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => visualizarDocumento(`dependente_${num}_certidao_nascimento`)}
                                                  disabled={loadingDocumento}
                                                  className="ml-2 h-6 px-2 text-xs"
                                                >
                                                  <Eye className="h-3 w-3" />
                                                </Button>
                                              </div>
                                            )}
                                            {dependentesDocumentosReprovados[`dependente_${num}_certidao_nascimento`] && (
                                              <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                                                ❌ Certidão de Nascimento - REPROVADO - Selecione um novo arquivo
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => visualizarDocumento(`dependente_${num}_certidao_nascimento`)}
                                                  disabled={loadingDocumento}
                                                  className="ml-2 h-6 px-2 text-xs"
                                                >
                                                  <Eye className="h-3 w-3" />
                                                </Button>
                                              </div>
                                            )}
                                          </>
                                        )}

                                        {/* Verificar status do comprovante de invalidez */}
                                        {dependentesDocTipo[`dependente_${num}`] === 'comprovante_invalidez' && (
                                          <>
                                            {dependentesDocumentosAprovados[`dependente_${num}_comprovante_invalidez`] && (
                                              <div className="p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                                                ✅ Comprovante de Invalidez - APROVADO
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => visualizarDocumento(`dependente_${num}_comprovante_invalidez`)}
                                                  disabled={loadingDocumento}
                                                  className="ml-2 h-6 px-2 text-xs"
                                                >
                                                  <Eye className="h-3 w-3" />
                                                </Button>
                                              </div>
                                            )}
                                            {dependentesDocumentosReprovados[`dependente_${num}_comprovante_invalidez`] && (
                                              <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                                                ❌ Comprovante de Invalidez - REPROVADO - Selecione um novo arquivo
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => visualizarDocumento(`dependente_${num}_comprovante_invalidez`)}
                                                  disabled={loadingDocumento}
                                                  className="ml-2 h-6 px-2 text-xs"
                                                >
                                                  <Eye className="h-3 w-3" />
                                                </Button>
                                              </div>
                                            )}
                                          </>
                                        )}

                                       {/* Verificar status dos documentos por idade */}
                                       {dependentes.idades[num] >= 7 && dependentes.idades[num] <= 14 && (
                                         <>
                                           {dependentesDocumentosAprovados[`dependente_${num}_comprovante_escolaridade`] && (
                                             <div className="p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                                               ✅ Comprovante de Escolaridade - APROVADO
                                               <Button
                                                 type="button"
                                                 variant="outline"
                                                 size="sm"
                                                 onClick={() => visualizarDocumento(`dependente_${num}_comprovante_escolaridade`)}
                                                 disabled={loadingDocumento}
                                                 className="ml-2 h-6 px-2 text-xs"
                                               >
                                                 <Eye className="h-3 w-3" />
                                               </Button>
                                             </div>
                                           )}
                                           {dependentesDocumentosReprovados[`dependente_${num}_comprovante_escolaridade`] && (
                                             <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                                               ❌ Comprovante de Escolaridade - REPROVADO - Selecione um novo arquivo
                                               <Button
                                                 type="button"
                                                 variant="outline"
                                                 size="sm"
                                                 onClick={() => visualizarDocumento(`dependente_${num}_comprovante_escolaridade`)}
                                                 disabled={loadingDocumento}
                                                 className="ml-2 h-6 px-2 text-xs"
                                               >
                                                 <Eye className="h-3 w-3" />
                                               </Button>
                                             </div>
                                           )}
                                         </>
                                       )}

                                       {dependentes.idades[num] <= 6 && (
                                         <>
                                           {(dependentesDocumentosAprovados[`dependente_${num}_comprovante_vacinacao`] || dependentesDocumentosAprovados[`CV dependente ${num}`]) && (
                                             <div className="p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                                               ✅ Comprovante de Vacinação - APROVADO
                                               <Button
                                                 type="button"
                                                 variant="outline"
                                                 size="sm"
                                                 onClick={() => visualizarDocumento(`dependente_${num}_comprovante_vacinacao`)}
                                                 disabled={loadingDocumento}
                                                 className="ml-2 h-6 px-2 text-xs"
                                               >
                                                 <Eye className="h-3 w-3" />
                                               </Button>
                                             </div>
                                           )}
                                           {(dependentesDocumentosReprovados[`dependente_${num}_comprovante_vacinacao`] || dependentesDocumentosReprovados[`CV dependente ${num}`]) && (
                                             <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                                               ❌ Comprovante de Vacinação - REPROVADO - Selecione um novo arquivo
                                               <Button
                                                 type="button"
                                                 variant="outline"
                                                 size="sm"
                                                 onClick={() => visualizarDocumento(`dependente_${num}_comprovante_vacinacao`)}
                                                 disabled={loadingDocumento}
                                                 className="ml-2 h-6 px-2 text-xs"
                                               >
                                                 <Eye className="h-3 w-3" />
                                               </Button>
                                             </div>
                                           )}
                                         </>
                                       )}
                                     </div>
                                  </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Botão de envio */}
                      <div className="flex justify-center pt-6">
                        <Button 
                          onClick={enviarDocumentos} 
                          disabled={enviandoDocumentos}
                          size="lg" 
                          className="px-8"
                        >
                          {enviandoDocumentos ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Enviando documentos...
                            </>
                          ) : (
                            'Enviar Documentos'
                          )}
                        </Button>
                      </div>
                    </div>
                   </TabsContent>

                  <TabsContent value="transporte" className="space-y-6">
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold">Vale Transporte</h3>
                      
                      <ValeTransporteDisplay 
                        data={valeTransporteDetalhado}
                        transporteConfirmado={transporteConfirmado}
                        onConfirmChange={(checked) => setTransporteConfirmado(checked)}
                      />
                      
                      <div className="flex justify-center">
                        <Button 
                          onClick={async () => {
                            await supabase.from('evolucao_contratacao').update({
                              transporte_confirmado: true,
                              transporte_confirmado_em: new Date().toISOString(),
                              etapa_atual: 'validacao_documentos',
                              etapa_mais_avancada: 'validacao_documentos'
                            }).eq('user_id', user?.id);
                            setCurrentEtapa('validacao_documentos');
                            setEtapaMaisAvancada('validacao_documentos');
                            toast({ title: "Vale Transporte Confirmado", description: "Você confirmou os valores do vale transporte com sucesso." });
                          }}
                          disabled={!transporteConfirmado}
                          size="lg"
                          className="px-8"
                        >
                          Avançar
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                   <TabsContent value="validacao_documentos" className="space-y-6">
                     <div className="space-y-6">
                       <h3 className="text-lg font-semibold">Validação de Documentos</h3>

                       <DocumentosValidacao userId={user?.id} />
                       
                       <div className="flex justify-center">
                         <Button 
                           onClick={() => setCurrentEtapa('envio_documentos')}
                           variant="outline"
                         >
                           <ArrowLeft className="h-4 w-4 mr-2" />
                           Voltar aos Documentos
                         </Button>
                       </div>
                     </div>
                   </TabsContent>

                   <TabsContent value="assinatura_contrato" className="space-y-4">
                      <ContratoStatusDisplay 
                        userEmail={user?.email || ''}
                        userId={user?.id || ''}
                        onComplete={() => avancarEtapa()}
                      />
                   </TabsContent>

                  <TabsContent value="exame_admissional" className="space-y-4">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Exame Admissional</h3>
                      
                      {/* Dados do exame ou mensagem de aguardo */}
                      {exameData && (exameData.endereco_exame || exameData.data_exame || exameData.horario_exame) ? (
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Informações do Exame</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {exameData.endereco_exame && (
                              <div>
                                <Label className="text-sm font-medium">Endereço:</Label>
                                <p className="text-sm text-muted-foreground">{exameData.endereco_exame}</p>
                              </div>
                            )}
                            
                            {exameData.data_exame && (
                              <div>
                                <Label className="text-sm font-medium">Data:</Label>
                                <p className="text-sm text-muted-foreground">
                                  {format(new Date(exameData.data_exame), 'dd/MM/yyyy')}
                                </p>
                              </div>
                            )}
                            
                            {exameData.horario_exame && (
                              <div>
                                <Label className="text-sm font-medium">Horário:</Label>
                                <p className="text-sm text-muted-foreground">{exameData.horario_exame}</p>
                              </div>
                            )}
                            
                              <div>
                                <Label className="text-sm font-medium">Guia do Exame:</Label>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="mt-1"
                                  onClick={visualizarGuiaExame}
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  Exibir Guia
                                </Button>
                              </div>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card>
                          <CardContent className="pt-6">
                            <div className="text-center text-muted-foreground">
                              <Clock className="w-8 h-8 mx-auto mb-2" />
                              <p>Aguarde que o RH liberará essa próxima etapa</p>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                      
                      {/* Upload do Atestado de Saúde */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Atestado de Saúde</CardTitle>
                          <CardDescription>
                            Após realizar o exame, faça o upload do atestado de saúde
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label htmlFor="atestado-file" className="text-sm font-medium">
                              Selecionar Atestado
                            </Label>
                            <Input
                              id="atestado-file"
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setAtestadoFile(file);
                                }
                              }}
                              className="mt-1"
                            />
                            {atestadoFile && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Arquivo selecionado: {atestadoFile.name}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                      
                      {evolucaoData?.exame_status === 'reprovado' && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center gap-2 text-red-700 mb-1">
                            <XCircle className="h-4 w-4" />
                            <span className="font-medium">Atestado reprovado</span>
                          </div>
                          <p className="text-sm text-red-600">
                            {evolucaoData.exame_reprovado_motivo || 'Atestado não aprovado pelo RH. Envie um novo atestado.'}
                          </p>
                        </div>
                      )}

                      {evolucaoData?.exame_status === 'pendente' && evolucaoData?.exame_aprovado === null && (
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center gap-2 text-blue-700 mb-1">
                            <Clock className="h-4 w-4" />
                            <span className="font-medium">Em análise</span>
                          </div>
                          <p className="text-sm text-blue-600">
                            Aguarde, nossa equipe está validando seu atestado de saúde.
                          </p>
                        </div>
                      )}

                      <Button 
                        onClick={async () => {
                          if (atestadoFile) {
                            await uploadAtestadoSaude();
                          }
                        }}
                        disabled={!atestadoFile || uploadingAtestado}
                        className="w-full"
                      >
                        {uploadingAtestado ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Enviando...
                          </>
                        ) : (
                          evolucaoData?.exame_status === 'reprovado' ? 'Reenviar Atestado' : 'Enviar Atestado'
                        )}
                      </Button>
                    </div>
                  </TabsContent>

                  <TabsContent value="integracao" className="space-y-4">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Integração</h3>
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <h4 className="font-medium text-green-800 mb-2">Parabéns!</h4>
                        <p className="text-sm text-green-700">
                          Processo de contratação concluído com sucesso. 
                          Bem-vindo ao Grupo Ares!
                        </p>
                      </div>
                      
                      {/* Informações da Integração */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Informações para o Primeiro Dia</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {contratacaoData?.data_inicio && (
                            <div>
                              <Label className="text-sm font-medium">Data de Início:</Label>
                              <p className="text-sm text-muted-foreground">
                                {(() => {
                                  const [ano, mes, dia] = contratacaoData.data_inicio.split('-').map(Number);
                                  return `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${ano}`;
                                })()}
                              </p>
                            </div>
                          )}
                          
                          {(contratacaoData?.horario_entrada || contratacaoData?.horario_saida) && (
                            <div>
                              <Label className="text-sm font-medium">Horário:</Label>
                              <p className="text-sm text-muted-foreground">
                                {contratacaoData?.horario_entrada || 'N/A'} às {contratacaoData?.horario_saida || 'N/A'}
                              </p>
                            </div>
                          )}
                          
                          {contratacaoData?.quem_recebera_contratado && (
                            <div>
                              <Label className="text-sm font-medium">Quem irá receber você:</Label>
                              <p className="text-sm text-muted-foreground">{contratacaoData.quem_recebera_contratado}</p>
                            </div>
                          )}
                          
                          {contratacaoData?.observacoes && (
                            <div>
                              <Label className="text-sm font-medium">Observações:</Label>
                              <p className="text-sm text-muted-foreground">{contratacaoData.observacoes}</p>
                            </div>
                          )}
                          
                          <div>
                            <Label className="text-sm font-medium">Endereço da Empresa:</Label>
                            <p className="text-sm text-muted-foreground">
                              R. Dona Elisa, 2 - Água Branca, São Paulo - SP, 05003-090
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>
                </div>
              </Tabs>
            </CardContent>
          </Card>

          {/* Dialog de Sucesso */}
  {/* Modais de instruções específicas para cada documento */}
          
          {/* RG */}
          {showInstructions === 'rg' && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Instruções para RG (Frente e Verso)</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInstructions(null)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <ul className="text-sm space-y-2 text-gray-600">
                    <li>• Faça a foto apenas com as letras viradas na horizontal e do lado correto</li>
                    <li>• Enquadre a imagem tão bem quanto possível</li>
                    <li>• Evite imagens tortas e sombras</li>
                    <li>• Tire o documento de dentro de plásticos para evitar reflexos</li>
                    <li>• <strong>Envie uma imagem que mostre tanto a frente quanto o verso do RG</strong></li>
                    <li>• Alternativamente, faça uma montagem com as duas fotos em uma única imagem</li>
                  </ul>
                  
                  <div className="mt-4 space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Exemplo de foto do documento (frente e verso):</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <img 
                          src="/lovable-uploads/65c39aea-6f31-419f-bff0-9bd06bb6fe36.png" 
                          alt="Exemplo de RG Frente" 
                          className="w-full max-w-48 mx-auto border rounded-lg shadow-sm"
                          loading="eager"
                          style={{imageRendering: 'auto'}}
                        />
                        <img 
                          src="/lovable-uploads/12bc3e59-55fa-45fa-a250-65949ddb2433.png" 
                          alt="Exemplo de RG Verso" 
                          className="w-full max-w-48 mx-auto border rounded-lg shadow-sm"
                          loading="eager"
                          style={{imageRendering: 'auto'}}
                        />
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-700">
                      <strong>Não possui?</strong> Dirija-se ao site{' '}
                      <a 
                        href="https://www.poupatempo.sp.gov.br/carta/D97C3844-A2D9-4C9E-B364-CB4FEB26A1FB" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        https://www.poupatempo.sp.gov.br/carta/D97C3844-A2D9-4C9E-B364-CB4FEB26A1FB
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Certificado de Reservista */}
          {showInstructions === 'certificado_reservista' && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Instruções para Certificado de Reservista</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInstructions(null)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <ul className="text-sm space-y-2 text-gray-600">
                    <li>• Faça a foto com o documento completamente visível</li>
                    <li>• Certifique-se de que todos os dados estão legíveis</li>
                    <li>• Mantenha a câmera paralela ao documento</li>
                  </ul>

                  <div className="mt-4">
                    <h5 className="font-medium mb-2">Exemplo do documento:</h5>
                    <img 
                      src="/lovable-uploads/72195de8-e7d3-4562-82fc-437bbc19ac8f.png" 
                      alt="Exemplo de Certificado de Reservista" 
                      className="max-w-48 mx-auto border rounded"
                      loading="eager"
                      style={{imageRendering: 'auto'}}
                    />
                  </div>
                  
                  <p className="text-sm text-gray-700 mt-4">
                    <strong>Não possui?</strong> Faça seu alistamento militar em{' '}
                    <a 
                      href="http://alistamento.eb.mil.br" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      alistamento.eb.mil.br
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Foto 3x4 */}
          {showInstructions === 'foto_3x4' && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Instruções para Foto 3x4</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInstructions(null)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <ul className="text-sm space-y-2 text-gray-600">
                    <li>• Fundo branco ou claro</li>
                    <li>• Rosto de frente, sem sorriso</li>
                    <li>• Sem óculos escuros ou bonés</li>
                  </ul>

                  <div className="mt-4">
                    <h5 className="font-medium mb-2">Exemplo de foto adequada:</h5>
                    <img 
                      src="/lovable-uploads/5869bb22-be67-4bd3-9b85-1c2433e198cd.png" 
                      alt="Exemplo de Foto 3x4" 
                      className="max-w-48 mx-auto border rounded"
                      loading="eager"
                      style={{imageRendering: 'auto'}}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Comprovante Bancário */}
          {showInstructions === 'comprovante_bancario' && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Instruções para Comprovante de Dados Bancários</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInstructions(null)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <ul className="text-sm space-y-2 text-gray-600">
                    <li>- Extrato bancário</li>
                    <li>- Qualquer comprovante de pagamento</li>
                    <li>- Necessário os seguintes dados: nome, banco, agência e conta.</li>
                  </ul>

                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Exemplo de comprovante adequado:</p>
                    <img 
                      src="/lovable-uploads/edeb008c-74c7-4a65-a643-c89fc7e1d56f.png" 
                      alt="Exemplo de Comprovante Bancário" 
                      className="max-w-48 mx-auto border rounded"
                      loading="eager"
                      style={{imageRendering: 'auto'}}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Comprovante de Escolaridade */}
          {showInstructions === 'comprovante_escolaridade' && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Instruções para Comprovante de Escolaridade</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInstructions(null)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <ul className="text-sm space-y-2 text-gray-600">
                    <li>- Enquadre corretamente o documento</li>
                    <li>- Dados legíveis</li>
                    <li>- Pode ser diploma ou comprovante.</li>
                  </ul>

                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Exemplo de certificado adequado:</p>
                    <img 
                      src="/lovable-uploads/45d028b2-5296-439d-9503-4e8d0e2901cf.png" 
                      alt="Exemplo de Certificado de Escolaridade" 
                      className="max-w-48 mx-auto border rounded"
                      loading="eager"
                      style={{imageRendering: 'auto'}}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Título de Eleitor */}
          {showInstructions === 'titulo_eleitor' && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Instruções para Título de Eleitor</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInstructions(null)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <ul className="text-sm space-y-2 text-gray-600">
                    <li>• Fotografe a frente em que estão seus dados</li>
                    <li>• Certifique-se de que os dados estão legíveis</li>
                    <li>• Se preferir utilize o e-Título.</li>
                  </ul>

                  <div className="mt-4">
                    <h5 className="font-medium mb-2">Exemplo de título de eleitor:</h5>
                    <img 
                      src="/lovable-uploads/0d3d09ae-6149-479b-aa62-fec518c30997.png" 
                      alt="Exemplo de Título de Eleitor" 
                      className="max-w-48 mx-auto border rounded"
                      loading="eager"
                      style={{imageRendering: 'auto'}}
                    />
                  </div>
                  
                  <p className="text-sm text-gray-700 mt-4">
                    <strong>Não possui?</strong> Acesse:{' '}
                    <a 
                      href="https://www.justicaeleitoral.jus.br/titulo-eleitoral/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      https://www.justicaeleitoral.jus.br/titulo-eleitoral/
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Comprovante de Residência */}
          {showInstructions === 'comprovante_residencia' && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Instruções para Comprovante de Residência</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInstructions(null)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <ul className="text-sm space-y-2 text-gray-600">
                    <li>- Tire uma foto de uma parte que foque em seu endereço.</li>
                    <li>- Conta de luz, água, gás ou telefone</li>
                    <li>- Contrato de aluguel ou IPTU</li>
                  </ul>

                  <div className="mt-4">
                    <h5 className="font-medium mb-2">Exemplo de comprovante de residência:</h5>
                    <img 
                      src="/lovable-uploads/093a5173-187c-4390-93ae-97df21ad347a.png" 
                      alt="Exemplo de Comprovante de Residência" 
                      className="max-w-48 mx-auto border rounded"
                      loading="eager"
                      style={{imageRendering: 'auto'}}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Carteira de Trabalho */}
          {showInstructions === 'carteira_trabalho' && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Instruções para Carteira de Trabalho</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInstructions(null)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <ul className="text-sm space-y-2 text-gray-600">
                    <li>• Página com dados pessoais e contratos (se houver)</li>
                    <li>• Todas as páginas devem estar legíveis</li>
                  </ul>

                  <div className="mt-4">
                    <h5 className="font-medium mb-2">Exemplo de carteira de trabalho digital:</h5>
                    <img 
                      src="/lovable-uploads/a5000f34-80f4-4fe7-bca3-c7278e13d1ab.png" 
                      alt="Exemplo de Carteira de Trabalho Digital" 
                      className="max-w-48 mx-auto border rounded"
                      loading="eager"
                      style={{imageRendering: 'auto'}}
                    />
                  </div>
                  
                  <p className="text-sm text-gray-700 mt-4">
                    <strong>Não possui?</strong> Acesse:{' '}
                    <a 
                      href="https://www.gov.br/pt-br/servicos/obter-a-carteira-de-trabalho" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      https://www.gov.br/pt-br/servicos/obter-a-carteira-de-trabalho
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Certidão de Nascimento */}
          {showInstructions === 'certidao_nascimento' && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Instruções para Certidão de Nascimento</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInstructions(null)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <ul className="text-sm space-y-2 text-gray-600">
                    <li>• Certidão original ou cópia autenticada</li>
                    <li>• Todos os dados devem estar legíveis</li>
                    <li>• Se casado, pode enviar certidão de casamento</li>
                  </ul>

                  <div className="mt-4">
                    <h5 className="font-medium mb-2">Exemplo de certidão de nascimento:</h5>
                    <img 
                      src="/lovable-uploads/ec7d4f43-5bf6-48ed-b53f-9c1bb6bde92a.png" 
                      alt="Exemplo de Certidão de Nascimento" 
                      className="max-w-48 mx-auto border rounded"
                      loading="eager"
                      style={{imageRendering: 'auto'}}
                    />
                  </div>
                  
                  <p className="text-sm text-gray-700 mt-4">
                    <strong>Não possui?</strong> Acesse:{' '}
                    <a 
                      href="https://www.gov.br/mre/pt-br/consulado-londres/registro-civil/segunda-via/certidao-atualizada" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      https://www.gov.br/mre/pt-br/consulado-londres/registro-civil/segunda-via/certidao-atualizada
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}

          <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-center">✅ Dados Aprovados!</DialogTitle>
                <DialogDescription className="text-center">
                  Seus dados pessoais foram enviados com sucesso e estão sendo processados.
                  Você será redirecionado para a próxima etapa do processo.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-center">
                <Button 
                  onClick={() => {
                    setShowSuccessDialog(false);
                  }}
                  className="px-8"
                >
                  Continuar para Envio de Documentos
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Popup para exibir JSON do webhook */}
          <Dialog open={showWebhookResult} onOpenChange={setShowWebhookResult}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Resultado do Webhook</DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(webhookJsonResult, null, 2)}
                </pre>
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={() => setShowWebhookResult(false)}>
                  Fechar
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Modal de visualização de documentos */}
          <Dialog open={showDocumentViewer} onOpenChange={setShowDocumentViewer}>
            <DialogContent className="max-w-4xl max-h-[90vh] w-[95vw]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Visualização do Documento
                </DialogTitle>
                <DialogDescription>
                  {documentoSelecionado && (
                    <div className="space-y-1">
                      <p><strong>Tipo:</strong> {camposDocumentos.find(c => c.key === documentoSelecionado.tipo)?.label}</p>
                      <p><strong>Arquivo:</strong> {documentoSelecionado.nome}</p>
                    </div>
                  )}
                </DialogDescription>
              </DialogHeader>
              
              <div className="flex-1 overflow-auto">
                {documentoSelecionado && (
                  <div className="w-full h-[60vh] bg-gray-100 rounded-lg overflow-hidden">
                    {documentoSelecionado.nome.toLowerCase().endsWith('.pdf') ? (
                      <iframe
                        src={documentoSelecionado.url}
                        className="w-full h-full border-0"
                        title="Visualização do documento PDF"
                      />
                    ) : (
                      <img
                        src={documentoSelecionado.url}
                        alt="Documento"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          console.error('Erro ao carregar imagem:', e);
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = `
                            <div class="flex items-center justify-center h-full text-gray-500">
                              <div class="text-center">
                                <FileText class="h-16 w-16 mx-auto mb-4" />
                                <p>Não foi possível carregar a visualização</p>
                                <p class="text-sm">Use o botão "Baixar" para acessar o arquivo</p>
                              </div>
                            </div>
                          `;
                        }}
                      />
                    )}
                  </div>
                )}
              </div>

              <DialogFooter className="flex justify-between">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={baixarDocumento}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Baixar
                  </Button>
                  {documentoSelecionado && (
                     <Button
                       variant="outline"
                       onClick={() => {
                         permitirSubstituicao(documentoSelecionado.tipo);
                         setShowDocumentViewer(false);
                       }}
                       className="flex items-center gap-2 flex-shrink-0"
                     >
                       <RotateCcw className="h-4 w-4" />
                       <span className="hidden sm:inline">Substituir</span>
                     </Button>
                  )}
                </div>
                <Button onClick={() => setShowDocumentViewer(false)}>
                  Fechar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Dialog para visualização da guia do exame */}
          <Dialog open={showGuiaViewer} onOpenChange={setShowGuiaViewer}>
            <DialogContent className="max-w-4xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle>Guia de Encaminhamento para Exame</DialogTitle>
                <DialogDescription>
                  Visualize sua guia de encaminhamento para o exame admissional
                </DialogDescription>
              </DialogHeader>
              
              <div className="flex-1 overflow-hidden">
                {guiaUrl && (
                  <iframe
                    src={guiaUrl}
                    className="w-full h-[70vh] border rounded-lg"
                    title="Guia de Encaminhamento"
                  />
                )}
              </div>
              
              <DialogFooter className="flex justify-between">
                <Button variant="outline" onClick={() => setShowGuiaViewer(false)}>
                  <X className="w-4 h-4 mr-2" />
                  Fechar
                </Button>
                <Button onClick={baixarGuiaExame}>
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Guia
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Modal LGPD */}
        <Dialog open={showLgpdModal} onOpenChange={() => {}}>
          <DialogContent aria-describedby="lgpd-description" className="max-w-2xl [&>button]:hidden">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-center">
                Uso de dados pessoais no processo de contratação
              </DialogTitle>
            </DialogHeader>
            
            <DialogDescription id="lgpd-description" className="space-y-4 text-sm text-muted-foreground">
              <p>
                Conforme a Lei Geral de Proteção de Dados (Lei nº 13.709/2018), precisamos do seu consentimento para tratar os documentos e informações enviados nesta plataforma.
              </p>
              
              <div>
                <p className="font-medium text-foreground mb-2">Seus dados serão utilizados apenas para:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Análise do processo de contratação</li>
                  <li>Validação de documentos</li>
                  <li>Cumprimento de obrigações legais</li>
                </ul>
              </div>
              
              <p>
                Eles <strong>não serão compartilhados com terceiros sem sua autorização</strong>, exceto quando exigido por lei.
              </p>
              
              <p>
                Ao clicar em <strong>"Concordo"</strong>, você autoriza o tratamento dos seus dados conforme descrito e de acordo com nossa{' '}
                <a href="https://seudominio.com/politica-de-privacidade" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                  Política de Privacidade
                </a>.
              </p>
            </DialogDescription>
            
            <DialogFooter className="flex justify-center gap-4 mt-6">
              <Button 
                variant="outline" 
                onClick={handleLgpdRejeicao}
                disabled={processingLgpd}
                className="min-w-[120px]"
              >
                Não concordo
              </Button>
              <Button 
                onClick={handleLgpdAceite}
                disabled={processingLgpd}
                className="min-w-[120px]"
              >
                {processingLgpd ? 'Processando...' : 'Concordo'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ProtectedRoute>
    );
  } catch (error) {
    console.error('❌ Erro durante o render da página de contratação:', error);
    return FallbackComponent();
  }
}